import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Cookie, CupSoda, Package, IceCreamCone, Coffee, ShoppingBag, Star } from 'lucide-react';
import './borne.css';
import './borne-themes.css';
import './borne-cookies-premium.css';
import './borne-kiosk-compact.css';
import { initAppareil, getDeviceId } from '../lib/appareil';
import KiffFlow, { KiffVeille } from './BorneKiff';
import { BoiteDuo, flyCookie } from '../components/BoiteDuo';
import BorneClubV2, { ClubBarV2, MonClubV2, GiftSheetV2 } from '../components/BorneClubV2';
import { optimImg } from '../lib/img';

const KC = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get('profil');
    if (q === 'kc') return true;
    if (q === 'std') return false;
    const w = window.innerWidth, h = window.innerHeight;
    return h > w && w >= 1000 && w <= 1150 && h / w >= 1.6;
  } catch { return false; }
})();
if (KC) {
  try {
    document.querySelector('meta[name="viewport"]')?.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
    document.addEventListener('contextmenu', (e) => e.preventDefault());
  } catch { /* kiosk */ }
}
const fmtPrenom = (s) => s.toLowerCase().replace(/(^|[\s-])\p{L}/gu, (c) => c.toUpperCase());
const optim = (u, w = KC ? 800 : 520) => optimImg(u, w);
const CAT_ICO = { cookies: Cookie, hotcookie: Cookie, boissons: CupSoda, milkshakes: CupSoda, cafes: Coffee, glaces: IceCreamCone, gourmandises: IceCreamCone, formules: Package, box: Package, moments: Star, offrir: Star, boutique: ShoppingBag };
const ALLERGENES_LIST = [
  ['gluten', 'Gluten', '🌾'], ['lait', 'Lait', '🥛'], ['oeufs', 'Œufs', '🥚'], ['arachides', 'Arachides', '🥜'],
  ['fruits_coque', 'Fruits à coque', '🌰'], ['soja', 'Soja', '🫘'], ['sesame', 'Sésame', '🥯'], ['crustaces', 'Crustacés', '🦐'],
  ['poissons', 'Poissons', '🐟'], ['mollusques', 'Mollusques', '🦪'], ['celeri', 'Céleri', '🥬'], ['moutarde', 'Moutarde', '🥫'],
  ['lupin', 'Lupin', '🌼'], ['sulfites', 'Sulfites', '🍷'],
];
const ALLERG_LABEL = Object.fromEntries(ALLERGENES_LIST.map(([id, la]) => [id, la]));
const ALLERG_PICTO = Object.fromEntries(ALLERGENES_LIST.map(([id, , pi]) => [id, pi]));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VEILLE_IMGS = [
  'https://static.prod-images.emergentagent.com/jobs/97058278-bf2f-431e-a9cb-46ef4e51b791/images/7831526f1624c63518ba660a579de0899f0d8c684059f86b1e158eb429a7c182.jpeg',
  'https://static.prod-images.emergentagent.com/jobs/97058278-bf2f-431e-a9cb-46ef4e51b791/images/a42f67ab94c56e20156ec2a49b559c8f9eb57dfa76c41ead463f580926b2bdfc.jpeg',
  'https://static.prod-images.emergentagent.com/jobs/97058278-bf2f-431e-a9cb-46ef4e51b791/images/94dcf43dd6b178fc2f3f7166b0a88c0da56d51eedcf33ad5f00a440ad585861e.jpeg',
  'https://static.prod-images.emergentagent.com/jobs/97058278-bf2f-431e-a9cb-46ef4e51b791/images/46cb50a58a98633b65e87465b95f96eea7bbd85f0c4723c2e19381e6d23529d2.jpeg',
];
const eur = (n) => (n == null || isNaN(n)) ? '' : n.toFixed(2).replace('.', ',') + ' €';
const ecoEur = (n) => (n == null || isNaN(n)) ? '' : (Math.round(n * 100) % 100 === 0 ? String(Math.round(n)) : n.toFixed(2).replace('.', ',')) + ' €';
const cle = (l) => l.nom + '|' + (l.opts || []).join('/');
let _actx;
const pop = () => {
  try {
    _actx = _actx || new (window.AudioContext || window.webkitAudioContext)();
    if (_actx.state === 'suspended') _actx.resume();
    const t = _actx.currentTime;
    const o = _actx.createOscillator();
    const g = _actx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(440, t);
    o.frequency.exponentialRampToValueAtTime(920, t + 0.09);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.32, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
    o.connect(g).connect(_actx.destination);
    o.start(t);
    o.stop(t + 0.26);
  } catch { /* audio non dispo */ }
};
const LAUNCH = '2026-09-03';
const POSTE = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get('poste');
    if (q) { localStorage.setItem('borne_poste', q); return q; }
    return localStorage.getItem('borne_poste') || '1';
  } catch { return '1'; }
})();
const APERCU = (() => {
  try { return new URLSearchParams(window.location.search).get('apercu') === '1'; } catch { return false; }
})();
const DEMO = (() => {
  try { return new URLSearchParams(window.location.search).get('demo') === '1'; } catch { return false; }
})();
const BOUTIQUE_RAW = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get('boutique');
    if (q) { localStorage.setItem('borne_boutique', q); return q; }
    return localStorage.getItem('borne_boutique');
  } catch { return null; }
})();
const BOUTIQUE = BOUTIQUE_RAW || 'aix';
const SID = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const MOMENT_OCCASIONS = ['🎂 Joyeux Anniversaire', '❤️ Amour', '🙏 Merci', '🎉 Félicitations', '👶 Naissance', '🌸 Bon Rétablissement', '✨ Juste comme ça'];
const BOX_MAP = { '3 Chocolats': '3 Chocolats', 'Chocolat au lait': 'Chocolat au lait', 'Chocolat noir': 'Chocolat noir', 'Oréo Chocolat Blanc': 'Oréo Chocolat Blanc', 'Speculoos Chocolat au lait': 'Speculoos Chocolat au lait', 'Cookie Pistache': 'Cookie Pistache', 'Cookie Citron': 'Cookie Citron', 'Cookie Matcha': 'Cookie Matcha', 'Cookie Signature': 'Cookie Signature', 'Mini Eggs': 'Mini Eggs' };
const isJourJ = () => { const d = new Date(); const l = new Date(LAUNCH + 'T00:00:00'); return d.toDateString() === l.toDateString(); };
const LaunchBanner = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const iv = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(iv); }, []);
  const d0 = new Date(LAUNCH + 'T00:00:00');
  if (now.toDateString() === d0.toDateString()) return (
    <div className="bo-launch jj fredoka" data-testid="veille-launch-jj">
      C'est le jour J · BAC TO BOUFI
      <span>Jusqu'à 300 € à gagner aujourd'hui</span>
    </div>
  );
  if (now < d0) {
    const s = Math.floor((d0 - now) / 1000);
    const j = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return (
      <div className="bo-launch cd fredoka" data-testid="veille-launch-cd">
        BAC TO BOUFI arrive
        <span>{j > 0 ? `J-${j} · ` : ''}{String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(sec).padStart(2, '0')}</span>
      </div>
    );
  }
  return null;
};
const HappyVeille = ({ info }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const iv = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(iv); }, []);
  if (!info) return null;
  const [dh, dm] = info.debut.split(':').map(Number);
  const d0 = new Date(now); d0.setHours(dh, dm, 0, 0);
  if (info.en_cours || now >= d0) return (
    <div className="bo-launch jj fredoka" data-testid="veille-happy-encours">
      {info.nom} en cours · −{Math.round(info.pct)}%
      <span>Jusqu'à {info.fin} — foncez !</span>
    </div>
  );
  const s = Math.floor((d0 - now) / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return (
    <div className="bo-launch cd fredoka" data-testid="veille-happy-countdown">
      ★ {info.nom} à {info.debut} · −{Math.round(info.pct)}%
      <span>dans {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(sec).padStart(2, '0')}</span>
    </div>
  );
};
const Confettis = () => {
  const parts = React.useMemo(() => Array.from({ length: 28 }, (_, i) => ({
    left: Math.random() * 100, delay: Math.random() * 6, dur: 5 + Math.random() * 5,
    color: ['#F15623', '#FDB714', '#FFF7EC', '#FF8A80'][i % 4],
  })), []);
  return (
    <div className="bo-confetti" aria-hidden="true">
      {parts.map((p, i) => <span key={i} style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`, background: p.color }} />)}
    </div>
  );
};
const Sac = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 7h12l1 13H5L6 7z" /><path d="M9 7a3 3 0 0 1 6 0" />
  </svg>
);

const BannerCarousel = ({ banners, btb, onAction }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 5000);
    return () => clearInterval(t);
  }, [banners.length]);
  if (!banners.length) return null;
  return (
    <div className="bd-bancar" data-testid="borne-banner-carousel">
      <div className="bd-bancar-track" style={{ transform: `translateX(-${(idx % banners.length) * 100}%)` }}>
        {banners.map((b, i) => (
          <button key={b.id || i} className="bd-bancar-slide" onClick={() => onAction(b)} data-testid={`borne-banner-${i}`}>
            <img src={optim(b.img, 1080)} alt="Promo Boufi" loading={i === 0 ? 'eager' : 'lazy'} />
            {b.action === 'btb' && btb && (
              <>
                <span className="bd-bancar-live" data-testid="borne-banner-btb-live">
                  🔥 <b>{btb.total.restantes}</b>/{btb.total.capacite}{btb.daily?.restantes > 0 && <> · {btb.daily.restantes} auj.</>}
                </span>
                <span className="bd-bancar-cta" data-testid="borne-banner-btb-cta">JE COMPOSE MA BOX →</span>
              </>
            )}
          </button>
        ))}
      </div>
      {banners.length > 1 && (
        <div className="bd-bancar-dots">
          {banners.map((_, i) => (
            <button key={i} className={i === idx % banners.length ? 'on' : ''} onClick={() => setIdx(i)} data-testid={`borne-banner-dot-${i}`} aria-label={`Bannière ${i + 1}`} />
          ))}
        </div>
      )}
    </div>
  );
};

class BorneBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: false }; }
  static getDerivedStateFromError() { return { err: true }; }
  componentDidCatch(e) {
    try { console.error('borne-crash', e); } catch {}
    setTimeout(() => window.location.reload(), 5000);
  }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div data-testid="borne-crash-screen" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, background: '#FBF4E8', color: '#2E130B', fontFamily: 'sans-serif' }}>
        <span style={{ fontSize: 54 }}>🍪</span>
        <b style={{ fontSize: 22 }}>Un instant, la borne redémarre…</b>
        <small style={{ opacity: .6 }}>Reprise automatique dans quelques secondes</small>
      </div>
    );
  }
}

export default function BorneClient() {
  const APERCU = useMemo(() => { try { return new URLSearchParams(window.location.search).get('apercu') === '1'; } catch { return false; } }, []);
  // Enregistrement silencieux de l'appareil (visible au back office) — ne bloque JAMAIS la vente
  useEffect(() => { if (!APERCU) initAppareil('borne', 'borne_boutique').catch(() => {}); }, [APERCU]);
  // Signal de vie : permet au Pilotage de détecter une borne éteinte / écran noir
  useEffect(() => {
    if (APERCU) return;
    const hb = () => {
      const did = getDeviceId('borne');
      if (did) axios.post(`${API}/appareils/heartbeat`, { device_id: did, type: 'borne', visible: document.visibilityState === 'visible', ua: navigator.userAgent }).catch(() => {});
    };
    const t0 = setTimeout(hb, 5000);
    const iv = setInterval(hb, 60000);
    document.addEventListener('visibilitychange', hb);
    return () => { clearTimeout(t0); clearInterval(iv); document.removeEventListener('visibilitychange', hb); };
  }, [APERCU]);
  return <BorneBoundary><BorneClientInner /></BorneBoundary>;
}

function BorneClientInner() {
  const [cat, setCat] = useState(null);
  const [data, setData] = useState(null);
  const [panier, setPanier] = useState([]);
  const [bump, setBump] = useState(false);
  const [momentInfo, setMomentInfo] = useState(false);
  const prevNb = useRef(0);
  const snapRef = useRef({ t: null, lock: false, last: 0 });
  useEffect(() => {
    const nb = panier.reduce((s, l) => s + l.q, 0);
    if (nb > prevNb.current) { setBump(true); const t = setTimeout(() => setBump(false), 650); prevNb.current = nb; return () => clearTimeout(t); }
    prevNb.current = nb;
  }, [panier]);
  const [etape, setEtape] = useState('carte');
  const [detail, setDetail] = useState(null);
  const [showPanier, setShowPanier] = useState(false);
  const [prenom, setPrenom] = useState('');
  const [tel, setTel] = useState('');
  const [telOk, setTelOk] = useState(false);
  const [membre, setMembre] = useState(null);
  const [roueGains, setRoueGains] = useState([]);
  const [roueGain, setRoueGain] = useState(null);
  const [rouePick, setRouePick] = useState(null);

  const [msg, setMsg] = useState(['', '']);
  const [champActif, setChampActif] = useState('prenom');
  const [rupture, setRupture] = useState(null);
  const [parrainOpen, setParrainOpen] = useState(false);
  const [giftPick, setGiftPick] = useState(false);
  const giftSkip = useRef(0);
  useEffect(() => {
    const dest = data?.destockage;
    if (!dest?.actif || !dest.cookies?.length) return;
    const nbBox = panier.filter((l) => l.nom === dest.box && !l.reward).reduce((s, l) => s + l.q, 0);
    const nbGift = panier.filter((l) => l.destock).reduce((s, l) => s + l.q, 0);
    if (nbGift > nbBox) {
      setPanier((p) => {
        let exc = nbGift - nbBox;
        return p.map((l) => { if (!l.destock || exc <= 0) return l; const r = Math.min(l.q, exc); exc -= r; return { ...l, q: l.q - r }; }).filter((l) => l.q > 0);
      });
      return;
    }
    if (nbBox === 0) { giftSkip.current = 0; return; }
    if (nbBox > nbGift && nbBox > giftSkip.current && !giftPick) setGiftPick(true);
  }, [panier, data, giftPick]);
  const [finNum, setFinNum] = useState(null);
  const [finTampon, setFinTampon] = useState(null);
  const [compte, setCompte] = useState(0);
  const [vIdx, setVIdx] = useState(0);
  const [upsell, setUpsell] = useState(null);
  const [aiReco, setAiReco] = useState(null);
  const [kiffVisuels, setKiffVisuels] = useState(null);
  const [hints, setHints] = useState(null);
  const [comboHint, setComboHint] = useState(null);
  const comboShown = useRef(false);
  useEffect(() => {
    if (DEMO) return;
    axios.get(`${API}/ai-seller/borne-hints`).then((r) => setHints(r.data)).catch(() => {});
  }, []);
  const badgesLogged = useRef(false);
  const recoVus = useRef(new Set());
  const palierDone = useRef(false);
  const [palierUi, setPalierUi] = useState(null);
  const coachDone = useRef(false);
  const [coachFor, setCoachFor] = useState(null);
  const [fanDone, setFanDone] = useState(false);
  const fanLogged = useRef(false);
  const fanAcc = useRef(false);
  const resetAnims = useCallback(() => {
    comboShown.current = false; badgesLogged.current = false; palierDone.current = false;
    coachDone.current = false; fanLogged.current = false; fanAcc.current = false;
    recoVus.current = new Set(); setPalierUi(null); setCoachFor(null); setFanDone(false);
  }, []);
  const [bxMsg, setBxMsg] = useState(null);
  const [bxShake, setBxShake] = useState(0);
  const [bxHide, setBxHide] = useState({});
  const [bxBusy, setBxBusy] = useState(false);
  const [bxComboOk, setBxComboOk] = useState(false);
  const bxTimer = useRef(null);
  const bxGrid = useRef({});
  const bxSlots = useRef({});
  const bxBox = useRef(null);
  const [aiPay, setAiPay] = useState(null);
  const aiPayDone = useRef(false);
  const vuRef = useRef({ nom: null, t: 0, v: 'A' });
  useEffect(() => {
    if (!detail) { vuRef.current = { nom: null, t: 0, v: 'A' }; return; }
    if (detail.it.nom !== vuRef.current.nom) {
      const v = data?.fiche_version === 'B' && !(detail.it.compose || []).length && !detail.it.choix_groupe && !detail.it.slots && !(detail.it.tailles || []).length && !detail.it.moments_box ? 'B' : 'A';
      vuRef.current = { nom: detail.it.nom, t: Date.now(), v };
      logEv('product_viewed', { nom: detail.it.nom, px: detail.it.px, version: v });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.it?.nom]);
  useEffect(() => { setBxMsg(null); setBxHide({}); setBxBusy(false); setBxComboOk(false); }, [detail?.it?.nom]);
  const aiFeedback = (id, action) => axios.post(`${API}/ai-seller/reco/feedback`, { decision_id: id, action }).catch(() => {});
  const aiDemande = (et) => axios.post(`${API}/ai-seller/reco`, { sid: SID, etape: et, panier: panier.map((l) => ({ nom: l.nom, q: l.q, px: l.px })), membre: !!membre });
  useEffect(() => {
    if (DEMO || !panier.length || etape !== 'carte') { setAiReco(null); return undefined; }
    const t = setTimeout(() => {
      aiDemande('panier').then((r) => {
        if (r.data.show && r.data.reco && !recoVus.current.has(r.data.reco.nom) && canAsk()) { recoVus.current.add(r.data.reco.nom); countAsk(); setAiReco(r.data); aiFeedback(r.data.decision_id, 'affiche'); } else setAiReco(null);
      }).catch(() => setAiReco(null));
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panier, etape]);
  useEffect(() => {
    if (DEMO || etape !== 'ident' || aiPayDone.current || !panier.length) return;
    aiPayDone.current = true;
    aiDemande('paiement').then((r) => {
      if (r.data.show && r.data.reco && !panier.some((l) => l.nom === r.data.reco.nom) && canAsk()) { countAsk(); setAiPay(r.data); aiFeedback(r.data.decision_id, 'affiche'); }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etape]);
  useEffect(() => { if (etape === 'fin' || !panier.length) { aiPayDone.current = false; setAiPay(null); setAiReco(null); askCount.current = 0; } if (etape === 'fin') resetAnims(); }, [etape, panier.length, resetAnims]);
  useEffect(() => {
    if (DEMO || comboShown.current || !hints?.combo_boisson || etape !== 'carte' || !panier.length || !data) return;
    const BSN = ['shake', 'cloud', 'cafes', 'refresher', 'bouteilles'];
    const aBoisson = panier.some((l) => { const p = data.products.find((x) => x.nom === l.nom); return p && BSN.includes(p.cat_id); });
    const aCookie = panier.some((l) => { const p = data.products.find((x) => x.nom === l.nom); return p && p.cat_id === 'cookies'; });
    if (aCookie && !aBoisson && !panier.some((l) => l.nom === hints.combo_boisson.nom) && canAsk()) {
      comboShown.current = true;
      countAsk();
      const t = setTimeout(() => { setComboHint(hints.combo_boisson); track('propose', hints.combo_boisson.nom, hints.combo_boisson.px, 'anim-combo'); setTimeout(() => setComboHint(null), 7000); }, 900);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panier, etape]);
  useEffect(() => {
    if (DEMO || !hints || badgesLogged.current || etape !== 'carte' || !data) return;
    badgesLogged.current = true;
    if (hints.marge_stars?.length) track('propose', hints.marge_stars.join(', '), 0, 'anim-chef');
    const hot = Object.entries(hints.ventes_7j || {}).filter(([, v]) => v > 5).map(([k]) => k);
    if (hot.length) track('propose', hot.slice(0, 5).join(', '), 0, 'anim-hot');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hints, etape, data]);
  useEffect(() => {
    if (DEMO || !data || etape !== 'carte') { setPalierUi(null); return; }
    const box4 = data.products.find((p) => p.choix_groupe === 'box_cookies' && p.choix_n === 4 && !p.epuise && p.actif !== false && !p.moments_box);
    const nC = panier.filter((l) => { const p = data.products.find((x) => x.nom === l.nom); return p && p.cat_id === 'cookies' && !p.choix_n && !p.compose && !l.reward; }).reduce((s, l) => s + l.q, 0);
    if (!palierDone.current && box4 && nC >= 2 && nC < 4) {
      if (!palierUi) track('propose', box4.nom, box4.px, 'anim-palier');
      setPalierUi({ nom: box4.nom, px: box4.px, nC });
    } else if (palierUi) {
      palierDone.current = true;
      if (nC >= 4) track('accepte', palierUi.nom, palierUi.px, 'anim-palier');
      setPalierUi(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panier, etape, data]);
  useEffect(() => {
    if (!detail) { if (coachDone.current && coachFor) setCoachFor(null); return; }
    if (DEMO || coachDone.current || !hints?.top_cookies || !detail.it?.choix_groupe || !detail.it?.choix_n) return;
    const gB = G[detail.it.choix_groupe];
    if (gB && hints.top_cookies.filter((t) => gB.opts.includes(t.nom)).length >= 2) {
      coachDone.current = true;
      setCoachFor(detail.it.nom);
      track('propose', detail.it.nom, detail.it.px || 0, 'anim-coachbox');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, hints]);
  const aiAjouter = (r, close) => {
    aiFeedback(r.decision_id, 'accepte');
    setPanier((prev) => {
      const i = prev.findIndex((x) => x.nom === r.reco.nom && !(x.opts || []).length && !x.reward);
      return i >= 0 ? prev.map((x, k) => (k === i ? { ...x, q: x.q + 1 } : x)) : [...prev, { nom: r.reco.nom, px: r.reco.px, q: 1, opts: [] }];
    });
    close();
  };
  const miniShown = useRef(false);
  const envieShown = useRef(false);
  // Cap anti-intrusion : max 2 sollicitations non-économiques par commande
  // (les offres qui font ÉCONOMISER le client — duo, box, remise, formule — ne comptent pas)
  const askCount = useRef(0);
  const canAsk = () => askCount.current < 2;
  const countAsk = () => { askCount.current += 1; };
  const csidRef = useRef('');
  const csFlags = useRef({});
  const lastAct = useRef(Date.now());
  const abandonRef = useRef(() => {});
  const [keepCompte, setKeepCompte] = useState(20);
  const [banMsg, setBanMsg] = useState(null);
  const duoUpShown = useRef(false);
  const boxUpShown = useRef(false);
  const [clubOpen, setClubOpen] = useState(false);
  const [rewardPick, setRewardPick] = useState(false);
  const [roue, setRoue] = useState(null);
  const [roueDock, setRoueDock] = useState(false);
  useEffect(() => {
    if (DEMO || fanDone || !data || etape !== 'carte' || panier.length || clubOpen || detail) return undefined;
    if (!fanLogged.current) { fanLogged.current = true; track('propose', 'Boufi Club', 0, 'anim-fan'); }
    const t = setTimeout(() => setFanDone(true), 17500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fanDone, data, etape, panier.length, clubOpen, detail]);
  const [univers, setUnivers] = useState('pourvous');
  const [secVisible, setSecVisible] = useState(null);
  const [ultra, setUltra] = useState(null);
  const [keepAsk, setKeepAsk] = useState(false);
  const [suggClub, setSuggClub] = useState(null);

  useEffect(() => {
    if (etape !== 'ident' || membre || tel.length !== 10) { setSuggClub(null); return; }
    let ok = true;
    axios.get(`${API}/borne/club/lookup?phone=${tel}`).then((r) => { if (ok && r.data.found) setSuggClub(r.data); }).catch(() => {});
    return () => { ok = false; };
  }, [etape, tel, membre]);
  const [inside, setInside] = useState(false);
  const [slides, setSlides] = useState(null);
  const [slogan, setSlogan] = useState('More than a cookie · Cookies · Drinks · Good mood');
  const [clubChoix, setClubChoix] = useState(null);
  const [rgpdInfo, setRgpdInfo] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [allergies, setAllergies] = useState([]);
  const [allergOpen, setAllergOpen] = useState(false);
  const [clubIdent, setClubIdent] = useState({ membre: true, rejoindre: true });
  const [btb, setBtb] = useState(null);
  const [btbOpen, setBtbOpen] = useState(false);
  const [banners, setBanners] = useState([]);
  const [pastilles, setPastilles] = useState([]);
  const [theme, setTheme] = useState(null);
  useEffect(() => {
    if (theme?.base === 'editorial' && !kiffVisuels) {
      axios.get(`${API}/borne/kiff-visuels`).then((r) => setKiffVisuels(r.data || {})).catch(() => setKiffVisuels({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);
  const [heroEpure, setHeroEpure] = useState(null);
  const [wc, setWc] = useState({ actif: false, timeout: 45, img: '/promos/welcome_club_haut.webp' });
  const [cv2cfg, setCv2cfg] = useState(null);
  const [cv2, setCv2] = useState(null);
  const [cv2Open, setCv2Open] = useState(null);
  const [monClubOpen, setMonClubOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const cv2Ref = useRef(null);
  useEffect(() => { axios.get(`${API}/borne/club/v2/config?poste=${POSTE}`).then((r) => setCv2cfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (etape === 'veille') axios.get(`${API}/borne/club/v2/config?poste=${POSTE}`).then((r) => setCv2cfg(r.data)).catch(() => {}); }, [etape]);
  const wcNextRef = useRef('carte');
  const wcSeenRef = useRef(false);
  useEffect(() => { axios.get(`${API}/borne/welcome-club`).then((r) => { setWc(r.data || { actif: false }); if (r.data?.img) { const i = new Image(); i.src = r.data.img; } }).catch(() => {}); }, []);
  useEffect(() => { if (etape === 'veille') axios.get(`${API}/borne/welcome-club`).then((r) => setWc(r.data || { actif: false })).catch(() => {}); }, [etape]);
  useEffect(() => {
    if (etape !== 'welcome' || clubOpen) return undefined;
    const t = setTimeout(() => { track('refuse', 'welcome_screen_abandoned', 0, 'welcome'); wcSeenRef.current = false; setMembre(null); setTel(''); setTelOk(false); setPrenom(''); setClubOpen(false); setEtape('veille'); }, (wc.timeout || 45) * 1000);
    return () => clearTimeout(t);
  }, [etape, wc.timeout, clubOpen]);
  useEffect(() => { if (etape === 'welcome' && showPanier) setEtape(wcNextRef.current); }, [etape, showPanier]);
  const [heroIdx, setHeroIdx] = useState(0);
  const herosActifs = (heroEpure?.items || []).filter((h) => h.actif);
  useEffect(() => {
    if (herosActifs.length < 2) return;
    const t = setInterval(() => setHeroIdx((i) => i + 1), 6000);
    return () => clearInterval(t);
  }, [herosActifs.length]);
  const [errCat, setErrCat] = useState(false);
  const [meteo, setMeteo] = useState(null);
  const [happyNext, setHappyNext] = useState(null);
  const tInact = useRef(null);
  const scrollRef = useRef(null);
  const stickyRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || univers === 'pourvous' || theme?.base === 'epure') return;
    const s = snapRef.current;
    s.last = el.scrollTop;
    const heroEndF = () => { const h = el.querySelector('.bd-cathero'); return h ? h.offsetHeight : 0; };
    const go = (top) => {
      s.lock = true;
      el.scrollTo({ top, behavior: 'smooth' });
      setTimeout(() => { s.lock = false; s.last = el.scrollTop; }, 700);
    };
    const onWheel = (e) => {
      const heroEnd = heroEndF();
      if (heroEnd < 120) return;
      const y = el.scrollTop;
      if (e.deltaY < 0 && y > heroEnd + 6) {
        if (y + e.deltaY < heroEnd) { e.preventDefault(); if (!s.lock) go(heroEnd); }
        return;
      }
      if (y < heroEnd - 6 || (e.deltaY < 0 && y <= heroEnd + 6)) {
        e.preventDefault();
        if (!s.lock) go(e.deltaY > 0 ? heroEnd : 0);
      }
    };
    const hscrollT = (n) => {
      for (let x = n; x && x !== el; x = x.parentElement) {
        if (x.scrollWidth - x.clientWidth > 12) { const o = getComputedStyle(x).overflowX; if (o === 'auto' || o === 'scroll') return true; }
      }
      return false;
    };
    const onTS = (e) => { s.ty = e.touches[0].clientY; s.tx = e.touches[0].clientX; s.tSkip = hscrollT(e.target); const he = heroEndF(); s.inHero = el.scrollTop < he - 6; s.atTop = Math.abs(el.scrollTop - he) <= 8; };
    const onTM = (e) => {
      const heroEnd = heroEndF();
      if (heroEnd < 120 || s.tSkip) return;
      const dy = e.touches[0].clientY - s.ty;
      const dx = e.touches[0].clientX - s.tx;
      if (s.inHero) {
        if (!s.lock && Math.abs(dy) > 26 && Math.abs(dy) > Math.abs(dx)) {
          if (e.cancelable) e.preventDefault();
          go(dy < 0 ? heroEnd : 0);
        }
      } else if (s.atTop && dy > 26 && dy > Math.abs(dx)) {
        if (e.cancelable) e.preventDefault();
        if (!s.lock) go(0);
      }
    };
    const onScroll = () => {
      const heroEnd = heroEndF();
      if (heroEnd < 120) return;
      const y = el.scrollTop;
      const prev = s.last;
      const dir = y >= prev ? 1 : -1;
      s.last = y;
      if (s.lock) return;
      if (dir < 0 && prev >= heroEnd && y < heroEnd) { el.scrollTop = heroEnd; s.last = heroEnd; return; }
      clearTimeout(s.t);
      s.t = setTimeout(() => {
        if (y > 14 && y < heroEnd - 20) go(dir > 0 ? heroEnd : 0);
      }, 90);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTS, { passive: true });
    el.addEventListener('touchmove', onTM, { passive: false });
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTS);
      el.removeEventListener('touchmove', onTM);
      el.removeEventListener('scroll', onScroll);
      clearTimeout(s.t);
    };
  }, [univers, etape, theme?.base]);
  const tabs2Ref = useRef(null);
  const secRefs = useRef({});

  useEffect(() => {
    if (!secVisible || !tabs2Ref.current) return;
    const el = tabs2Ref.current.querySelector(`[data-sec="${secVisible}"]`);
    if (el) tabs2Ref.current.scrollTo({ left: el.offsetLeft - tabs2Ref.current.clientWidth / 2 + el.clientWidth / 2, behavior: 'smooth' });
  }, [secVisible]);

  useEffect(() => {
    const root = scrollRef.current;
    if (etape !== 'carte' || !root || !data) return;
    const vis = new Map();
    const io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        const id = en.target.getAttribute('data-secid');
        if (en.isIntersecting) vis.set(id, en.target); else vis.delete(id);
      }
      let best = null; let bestTop = Infinity;
      vis.forEach((el, id) => { const t = el.getBoundingClientRect().top; if (t < bestTop) { bestTop = t; best = id; } });
      if (best) setSecVisible((prev) => (prev === best ? prev : best));
    }, { root, rootMargin: '-135px 0px -45% 0px', threshold: 0 });
    const t = setTimeout(() => {
      Object.values(secRefs.current).forEach((el) => { if (el && el.isConnected) io.observe(el); });
    }, 100);
    return () => { clearTimeout(t); io.disconnect(); };
  }, [etape, univers, data, membre]);


  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }, [cat]);

  const chargerCatalog = useCallback(() => {
    setErrCat(false);
    axios.get(`${API}/borne/catalog?boutique=${BOUTIQUE}`)
      .then((r) => { setData(r.data); setCat(r.data.categories[0]?.id); })
      .catch(() => setErrCat(true));
  }, []);

  const refreshCatalog = useCallback(() => {
    axios.get(`${API}/borne/catalog?boutique=${BOUTIQUE}`).then((r) => {
      setData((d) => (d && JSON.stringify(d) === JSON.stringify(r.data) ? d : r.data));
      setCat((c) => (r.data.categories.some((x) => x.id === c) ? c : r.data.categories[0]?.id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    chargerCatalog();
    axios.get(`${API}/borne/ultra/stats`).then((r) => setUltra((u) => ({ ...(u || {}), ...r.data }))).catch(() => {});
    axios.get(`${API}/borne/nouveautes`).then((r) => setUltra((u) => ({ ...(u || {}), nouveautes: r.data.produits || [] }))).catch(() => {});
    axios.get(`${API}/borne/pourvous`).then((r) => setUltra((u) => ({ ...(u || {}), pv: r.data.sections || {}, pvVoir: r.data.voir_carte !== false }))).catch(() => {});
    axios.get(`${API}/drop/stock/bac-to-boufi`).then((r) => {
      if (r.data?.total?.restantes > 0 && r.data?.borne_visible !== false) setBtb(r.data);
    }).catch(() => {});
    axios.get(`${API}/borne/banners`).then((r) => setBanners(r.data.banners || [])).catch(() => {});
    axios.get(`${API}/borne/pastille`).then((r) => setPastilles(r.data?.items || [])).catch(() => {});
    const previewTheme = (() => { try { return new URLSearchParams(window.location.search).get('theme') || ''; } catch { return ''; } })();
    axios.get(`${API}/borne/theme?poste=${POSTE}&boutique=${BOUTIQUE}${previewTheme ? `&preview=${previewTheme}` : ''}`)
      .then((r) => setTheme(r.data || null)).catch(() => setTheme(null));
    axios.get(`${API}/borne/theme/hero`).then((r) => setHeroEpure(r.data || null)).catch(() => {});
    axios.get(`${API}/borne/club-ident`).then((r) => setClubIdent(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (!data?.products?.length) return;
    const t = setTimeout(() => {
      const urls = new Set();
      data.products.slice(0, 24).forEach((p) => { const u = p.img_thumb || p.img; if (u) urls.add(optim(u)); });
      urls.forEach((u) => { const im = document.createElement('img'); im.decoding = 'async'; im.src = u; });
    }, 700);
    return () => clearTimeout(t);
  }, [data]);

  useEffect(() => {
    if (!data?.products?.length) return;
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 3000));
    idle(() => data.products.slice(0, 80).forEach((p) => { const u = p.img_thumb || p.img; if (u) { const im = new Image(); im.src = u; } }));
  }, [data]);

  useEffect(() => {
    axios.get(`${API}/borne/veille`).then((r) => {
      if (r.data.slogan) setSlogan(r.data.slogan);
      const sl = r.data.slides;
      if (!sl?.length) return;
      setSlides(sl);
      const imgs = sl.filter((s) => s.type === 'img');
      if (imgs[0]) { const im = new Image(); im.src = imgs[0].url; }
      const idle = window.requestIdleCallback || ((f) => setTimeout(f, 4000));
      idle(() => imgs.slice(1).forEach((s) => { const im = new Image(); im.src = s.url; }));
    }).catch(() => {});
    axios.get(`${API}/borne/meteo`).then((r) => setMeteo(r.data)).catch(() => {});
    let prevEnCours = null;
    const majHappy = () => axios.get(`${API}/borne/happy/next`).then((r) => {
      const a = r.data.annonce;
      setHappyNext(a);
      const ec = a ? a.en_cours : false;
      if (prevEnCours !== null && ec !== prevEnCours) chargerCatalog();
      prevEnCours = ec;
    }).catch(() => {});
    majHappy();
    let prevPush = null;
    const majSync = () => axios.get(`${API}/borne/sync`).then((r) => {
      setData((d) => (d && d.mode_test !== !!r.data.mode_test ? { ...d, mode_test: !!r.data.mode_test } : d));
      if (prevPush !== null && r.data.push !== prevPush) refreshCatalog();
      prevPush = r.data.push;
    }).catch(() => {});
    majSync();
    const ivTest = setInterval(majSync, 15000);
    const ivCat = setInterval(refreshCatalog, 60000);
    const ivHappy = setInterval(majHappy, 60000);
    const flush = async () => {
      const q = JSON.parse(localStorage.getItem('borne_queue') || '[]');
      if (!q.length) return;
      const rest = [];
      for (const o of q) {
        try { await axios.post(`${API}/borne/orders`, o, { timeout: 15000 }); }
        catch (err) { if (!(err.response && err.response.status >= 400 && err.response.status < 500)) rest.push(o); }
      }
      localStorage.setItem('borne_queue', JSON.stringify(rest));
    };
    flush();
    const iv = setInterval(flush, 10000);
    return () => { clearInterval(iv); clearInterval(ivHappy); clearInterval(ivTest); clearInterval(ivCat); };
  }, [chargerCatalog, refreshCatalog]);

  useEffect(() => {
    if (APERCU || DEMO) return undefined;
    const ping = () => {
      axios.post(`${API}/tv/appairage/heartbeat`, { store_id: BOUTIQUE, poste: POSTE }).then((r) => {
        if (r.data.desappairer) {
          try { localStorage.removeItem('borne_boutique'); localStorage.removeItem('borne_poste'); } catch { /* privé */ }
          window.location.replace('/borne-connect');
        }
      }).catch(() => {});
    };
    ping();
    const ivP = setInterval(ping, 120000);
    return () => clearInterval(ivP);
  }, []);

  // Interrupteur Borne V2 : lu UNIQUEMENT au retour de veille. Par défaut / erreur réseau / champ absent → rien (V1).
  // Jamais de bascule pendant une commande ni si la file hors-ligne contient des commandes en attente.
  useEffect(() => {
    if (APERCU || DEMO || etape !== 'veille') return undefined;
    let ok = true;
    try {
      if (JSON.parse(localStorage.getItem('borne_queue') || '[]').length) return undefined;
      axios.get(`${API}/borne-v2/switch`, { params: { device_id: getDeviceId('borne') || '', boutique: BOUTIQUE } })
        .then((r) => { if (ok && r.data?.version === 'v2') window.location.replace(`/borne-v2?boutique=${BOUTIQUE}&poste=${POSTE}`); })
        .catch(() => { /* V1 reste maîtresse */ });
    } catch { /* V1 reste maîtresse */ }
    return () => { ok = false; };
  }, [etape]);

  const deconnecter = useCallback(() => { if (cv2Ref.current?.token) { axios.post(`${API}/borne/club/v2/logout`, {}, { headers: { 'X-Club-Token': cv2Ref.current.token } }).catch(() => {}); } cv2Ref.current = null; setCv2(null); setCv2Open(null); setMonClubOpen(false); setGiftOpen(false); setRoueGains([]); setRoueGain(null); setRouePick(null); envieShown.current = false; askCount.current = 0; csidRef.current = ''; csFlags.current = {}; wcSeenRef.current = false; setPanier([]); setTel(''); setTelOk(false); setMembre(null); setPrenom(''); setMsg(['', '']); setDetail(null); setShowPanier(false); setUpsell(null); setClubOpen(false); setClubChoix(null); setAllergies([]); setAllergOpen(false); miniShown.current = false; resetAnims(); setEtape('carte'); }, [resetAnims]);
  useEffect(() => { if (etape === 'ident' && !panier.length && !envoi) setEtape('carte'); }, [etape, panier.length, envoi]);
  useEffect(() => {
    const armer = () => {
      lastAct.current = Date.now();
      clearTimeout(tInact.current);
      if (cv2Open) return;
      if ((etape === 'carte' || etape === 'kiff') && !panier.length) { if (!APERCU) tInact.current = setTimeout(() => { setDetail(null); setShowPanier(false); if (csidRef.current) abandonRef.current('sans_panier'); else setEtape('veille'); }, 30000); }

      else if (etape !== 'fin' && etape !== 'veille') tInact.current = setTimeout(() => setKeepAsk(true), (cv2cfg?.actif ? (cv2cfg.inactivite_s || 60) : (data?.commercial?.session_inactivity_timeout || 120)) * 1000);
    };
    ['click', 'touchstart', 'keydown'].forEach((e) => document.addEventListener(e, armer, { passive: true }));
    armer();
    return () => { clearTimeout(tInact.current); ['click', 'touchstart', 'keydown'].forEach((e) => document.removeEventListener(e, armer)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etape, panier.length, deconnecter, data?.commercial?.session_inactivity_timeout, cv2Open, cv2cfg?.actif]);

  useEffect(() => {
    if (!keepAsk) return undefined;
    const base = cv2cfg?.actif ? (cv2cfg.rebours_s || 15) : 20;
    setKeepCompte(base);
    const iv = setInterval(() => setKeepCompte((s) => {
      if (s <= 1) { clearInterval(iv); abandonRef.current('panier_abandonne'); return base; }
      return s - 1;
    }), 1000);
    return () => clearInterval(iv);
  }, [keepAsk]);

  useEffect(() => {
    if (etape !== 'veille') return;
    const arr = slides || VEILLE_IMGS.map((u) => ({ type: 'img', url: u }));
    const cur = arr[vIdx % arr.length];
    const t = setTimeout(() => setVIdx((i) => (i + 1) % arr.length), cur?.type === 'video' ? 15000 : 5000);
    return () => clearTimeout(t);
  }, [etape, vIdx, slides]);

  useEffect(() => {
    if (etape !== 'fin' || roue) return;
    setCompte(8);
    const iv = setInterval(() => setCompte((s) => { if (s <= 1) { clearInterval(iv); deconnecter(); return 0; } return s - 1; }), 1000);
    return () => clearInterval(iv);
  }, [etape, roue, deconnecter]);

  const swipeRef = useRef({});
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || etape !== 'carte' || !data) return;
    const sw = swipeRef.current;
    const hscroll = (n) => {
      for (let e = n; e && e !== el; e = e.parentElement) {
        if (e.scrollWidth - e.clientWidth > 12) {
          const o = getComputedStyle(e).overflowX;
          if (o === 'auto' || o === 'scroll') return true;
        }
      }
      return false;
    };
    const start = (e) => { const t = e.touches[0]; sw.x = t.clientX; sw.y = t.clientY; sw.t0 = Date.now(); sw.skip = hscroll(e.target); };
    const end = (e) => {
      if (sw.skip || el.closest('.bd-theme-epure')) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - sw.x, dy = t.clientY - sw.y;
      if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.6 && Date.now() - sw.t0 < 650) {
        const ids = universList.map((u) => u.id);
        const next = ids[(ids.indexOf(univers) + (dx < 0 ? 1 : -1) + ids.length) % ids.length];
        goUnivers(next, dx < 0 ? 1 : -1);
      }
    };
    el.addEventListener('touchstart', start, { passive: true });
    el.addEventListener('touchend', end, { passive: true });
    return () => { el.removeEventListener('touchstart', start); el.removeEventListener('touchend', end); };
  });
  const preloadDone = useRef(false);
  useEffect(() => {
    if (!data || preloadDone.current) return;
    preloadDone.current = true;
    const urls = [];
    data.products.forEach((p) => { if (p.img_thumb) urls.push(p.img_thumb); if (p.img) urls.push(p.img); });
    Object.values(data.heroes || {}).forEach((h) => { if (h.url && h.type !== 'video') urls.push(h.url); });
    let i = 0;
    let t;
    const step = () => {
      for (let k = 0; k < 6 && i < urls.length; k += 1, i += 1) { const im = new window.Image(); im.src = urls[i]; }
      if (i < urls.length) t = setTimeout(step, 250);
    };
    t = setTimeout(step, 1200);
    return () => clearTimeout(t);
  }, [data]);
  if (!data) return (
    <div className="bo bd bd-2026" style={{ padding: 40, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
      {errCat ? (<>
        <span>La carte n'a pas pu se charger.</span>
        <button className="bd-cta" style={{ width: 260 }} onClick={chargerCatalog} data-testid="borne-retry-btn">Réessayer</button>
      </>) : 'Chargement de la carte…'}
    </div>
  );
  const G = data.groupes;
  const catObj = data.categories.find((c) => c.id === cat) || data.categories[0];
  const prods = data.products.filter((p) => p.cat_id === catObj?.id || (p.cats_sup || []).includes(catObj?.id));
  const total = panier.reduce((s, l) => s + l.px * l.q, 0);
  const nbArt = panier.reduce((s, l) => s + l.q, 0);

  const BOISSONS_ULTRA = ['shake', 'cloud', 'cafes', 'refresher', 'bouteilles'];
  const ventesJour = ultra?.ventes || {};
  const ucfg = ultra?.config || {};
  const catOfNom = (nom) => data.products.find((p) => p.nom === nom)?.cat_id;
  const remisesDe = (lignes) => {
    const rs = [];
    if (ucfg.promo3_active) {
      const units = lignes.filter((l) => catOfNom(l.nom) === 'cookies' && l.px > 0).flatMap((l) => Array(l.q).fill(l.px)).sort((a, b) => a - b);
      const nb = Math.floor(units.length / 3);
      if (nb) {
        const m = Math.round(units.slice(0, nb).reduce((s, x) => s + x, 0) * (ucfg.promo3_pct || 50)) / 100;
        if (m > 0) rs.push({ nom: `Promo 3ᵉ cookie −${Math.round(ucfg.promo3_pct || 50)}%`, montant: m });
      }
    }
    if (ucfg.duo_active) {
      const aC = lignes.some((l) => catOfNom(l.nom) === 'cookies' && l.px > 0);
      const aB = lignes.some((l) => BOISSONS_ULTRA.includes(catOfNom(l.nom)));
      if (aC && aB) rs.push({ nom: 'Promo duo cookie + boisson', montant: Math.round((ucfg.duo_remise || 0.5) * 100) / 100 });
    }
    return rs;
  };
  const calcTotalPanier = (lignes) => Math.max(0, Math.round((lignes.reduce((s, l) => s + l.px * l.q, 0) - remisesDe(lignes).reduce((s, r) => s + r.montant, 0)) * 100) / 100);
  const remises = remisesDe(panier);
  const totalRemises = remises.reduce((s, r) => s + r.montant, 0);
  const totalAff = calcTotalPanier(panier);
  const suppReel = (p, px = null) => Math.round((calcTotalPanier([...panier, { nom: p.nom, px: px ?? p.px, q: 1 }]) - totalAff) * 100) / 100;
  const minEco = data.commercial?.minimum_saving_badge ?? 1;
  const cookiesSimples = data.products.filter((p) => p.cat_id === 'cookies' && !p.compose && !p.choix_n && p.actif !== false);
  const slotProdsG = (sl) => data.products.filter((p) => sl.cats?.includes(p.cat_id) && p.actif !== false && (!sl.mots || sl.mots.some((m) => p.nom.toLowerCase().includes(m))) && (!sl.seuls || sl.seuls.includes(p.nom)));
  const ecoReelle = (p) => {
    // économie « mêmes produits » vs achat séparé, promos actives incluses
    if (p.choix_n && cookiesSimples.length) {
      const c = [...cookiesSimples].sort((a, b) => a.px - b.px)[0];
      return Math.round((calcTotalPanier([{ nom: c.nom, px: c.px, q: p.choix_n }]) - p.px) * 100) / 100;
    }
    if ((p.slots || []).length) {
      const lignes = p.slots.map((sl) => {
        const cands = sl.groupe === 'cookie' ? cookiesSimples : slotProdsG(sl);
        if (!cands.length) return null;
        const c = [...cands].sort((a, b) => a.px - b.px)[0];
        return { nom: c.nom, px: c.px, q: 1 };
      });
      if (lignes.some((l) => !l)) return 0;
      return Math.round((calcTotalPanier(lignes) - p.px) * 100) / 100;
    }
    return p.px_barre > p.px ? Math.round((p.px_barre - p.px) * 100) / 100 : 0;
  };
  const showEco = (p) => ecoReelle(p) >= minEco;
  // prix barré affiché : px_barre sinon prix séparé réel (px + éco) — jamais null (crash prod Duo cloud/refresher)
  const pxBarreAff = (p) => (p.px_barre != null ? p.px_barre : Math.round((p.px + ecoReelle(p)) * 100) / 100);
  const needTel = !membre && panier.some((l) => {
    const p = data.products.find((x) => x.nom === l.nom);
    return l.moment || p?.moments_box || p?.cat_id === 'offrir';
  });
  const wantTel = needTel || clubChoix === 'rejoindre';

  const UNIV_DEF = [
    { id: 'cookies', nom: 'cookies', cats: ['cookies', 'hotcookie', 'icecookie'] },
    { id: 'boissons', nom: 'boissons', cats: ['shake', 'cloud', 'cafes', 'refresher', 'bouteilles'] },
    { id: 'glace', nom: 'glacé', cats: ['glaces', 'sundae', 'icecookie'] },
    { id: 'box', nom: 'formules', cats: ['formules', 'box'] },
  ];
  const catIds = data.categories.map((c) => c.id);
  const edCfg = data.edition || { nom: '🍂 automne édition', badge: 'Édition Automne' };
  const navUniv = (data.nav?.univers || []).filter((u) => (u.cats || []).some((c) => catIds.includes(c)));
  const baseUniv = navUniv.length ? navUniv : UNIV_DEF.filter((u) => u.cats.some((c) => catIds.includes(c)));
  const editionProdsAll = data.products.filter((p) => p.badge === edCfg.badge && p.actif !== false);
  const universList = [
    { id: 'pourvous', nom: 'pour vous', cats: [] },
    ...(editionProdsAll.length ? [{ id: 'edition', nom: edCfg.nom.toLowerCase(), cats: [] }] : []),
    ...baseUniv,
    ...data.categories.filter((c) => !baseUniv.some((u) => u.cats.includes(c.id))).map((c) => ({ id: 'cat-' + c.id, nom: c.nom.toLowerCase(), cats: [c.id] })),
  ];
  const univObj = universList.find((u) => u.id === univers) || universList[0];
  const menuVersion = data?.nav?.menu_version || 'onglets';
  const ckprem = !!theme?.tokens?.premium || (theme?.base === 'epure' && univObj?.id === 'cookies');
  const goUnivers = (nextId, dirIn) => {
    if (nextId === univers) return;
    const el = scrollRef.current;
    const wrap = el?.parentElement;
    if (wrap && !wrap.querySelector('[data-clone]')) {
      const ids = universList.map((u) => u.id);
      const dir = dirIn ?? (ids.indexOf(nextId) > ids.indexOf(univers) ? 1 : -1);
      const clone = el.cloneNode(true);
      clone.setAttribute('data-clone', '1');
      Object.assign(clone.style, { position: 'absolute', inset: '0', zIndex: 4, pointerEvents: 'none' });
      clone.classList.remove('bd-in-r', 'bd-in-l');
      clone.classList.add(dir > 0 ? 'bd-out-l' : 'bd-out-r');
      wrap.appendChild(clone);
      clone.scrollTop = el.scrollTop;
      el.classList.add(dir > 0 ? 'bd-in-r' : 'bd-in-l');
      setTimeout(() => { clone.remove(); el.classList.remove('bd-in-r', 'bd-in-l'); }, 480);
    }
    setUnivers(nextId); setSecVisible(null);
    logEv('univers', { univers: nextId });
    requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: nextId === 'pourvous' ? (stickyRef.current?.offsetTop || 0) : 0 }));
  };
  const clubProds = data.products.filter((p) => p.club_only);
  const TIER_RANK = { bronze: 0, silver: 1, gold: 2 };
  const rangMembre = membre ? ({ Bronze: 0, Silver: 1, Gold: 2 }[membre.tier] ?? 0) : -1;
  const prodLock = (p) => !!p.club_only && rangMembre < TIER_RANK[p.club_tier || 'bronze'];
  const lockLabel = (t) => (t === 'gold' ? 'Réservé Gold' : t === 'silver' ? 'Réservé Silver' : 'Réservé aux membres');
  const optLockTier = (g, o) => { const t = G[g]?.club?.[o]; return t && rangMembre < TIER_RANK[t] ? t : null; };
  const nextTierId = membre ? (rangMembre === 0 ? 'silver' : rangMembre === 1 ? 'gold' : null) : null;
  const teaserUnlocks = nextTierId ? [...new Set([
    ...data.products.filter((p) => p.club_only && (p.club_tier || 'bronze') === nextTierId).map((p) => p.nom),
    ...Object.values(G).flatMap((m) => Object.entries(m.club || {}).filter(([, t]) => t === nextTierId).map(([o]) => o)),
  ])] : [];
  const teaserRestant = nextTierId ? Math.max(1, (nextTierId === 'silver' ? 10 : 30) - (membre.cumul ?? 0)) : 0;
  const bannerAction = (b) => {
    axios.post(`${API}/borne/banners/event`, { banner_id: b.id || '' }).catch(() => {});
    if (b.action === 'edition') { if (editionProdsAll.length) goUnivers('edition'); return; }
    if (b.action === 'btb') { if (btb) setBtbOpen(true); return; }
    if (b.action === 'bueno' || /bueno/i.test(`${b.title || b.titre || ''} ${b.text || b.texte || ''} ${b.img || ''}`)) {
      const ck = data.products.find((p) => /bueno/i.test(p.nom) && ['cookies', 'hotcookie'].includes(p.cat_id) && p.actif !== false);
      const sh = data.products.find((p) => /bueno/i.test(p.nom) && p.cat_id === 'shake' && p.actif !== false);
      if (!ck || !sh) return;
      if (ck.epuise || sh.epuise) { setBanMsg(`${ck.epuise ? ck.nom : sh.nom} est épuisé — le duo Bueno revient bientôt !`); setTimeout(() => setBanMsg(null), 3200); return; }
      ajouter({ nom: ck.nom, px: ck.px, opts: [] });
      ajouter({ nom: sh.nom, px: sh.px, opts: [] });
      track('accepte', 'Duo Bueno', ck.px + sh.px, 'banner-bueno', ck.px + sh.px);
      ouvrirPanier();
      return;
    }
    if ((b.action || '').startsWith('cat:')) {
      const cid = b.action.slice(4);
      const go = () => { const el = secRefs.current[cid]; if (el) scrollRef.current?.scrollTo({ top: el.offsetTop - 132, behavior: 'smooth' }); };
      if (secRefs.current[cid]) { go(); return; }
      const u = universList.find((x) => (x.cats || []).includes(cid));
      if (u) { setUnivers(u.id); setSecVisible(null); setTimeout(go, 200); }
    }
  };
  const byCat = (id) => data.products.filter((p) => (p.cat_id === id || (p.cats_sup || []).includes(id)) && !prodLock(p));
  const populaires = data.products.filter((p) => p.best_seller && !prodLock(p));
  const nvSel = (ultra?.nouveautes || []).map((n) => data.products.find((p) => p.nom === n)).filter((p) => p && !p.epuise && !prodLock(p));
  const nouveautes = nvSel.length ? nvSel : data.products.filter((p) => p.badge && !prodLock(p));
  let sections;
  const editionProds = editionProdsAll.filter((p) => !p.epuise && !prodLock(p));
  if (univObj.id === 'pourvous') {
    const topProds = (ultra?.top || []).map((n) => data.products.find((p) => p.nom === n)).filter(Boolean);
    const vus = new Set();
    const incontournables = [...topProds, ...populaires, ...data.products.filter((p) => !prodLock(p) && !p.epuise && (p.img || p.img_thumb) && !['formules', 'box'].includes(p.cat_id))]
      .filter((p) => !vus.has(p.nom) && vus.add(p.nom)).slice(0, 3);
    sections = [
      ...(editionProds.length ? [{ id: 'edition', nom: edCfg.nom, prods: editionProds }] : []),
      ...(btb ? [{ id: 'drop', nom: '🔥 édition limitée', prods: [], custom: 'drop' }] : []),
      ...(() => { const fv = (membre?.favoris || []).map((n) => data.products.find((p) => p.nom === n)).filter((p) => p && !p.epuise && !prodLock(p)); return fv.length ? [{ id: 'favoris', nom: `⭐ les favoris de ${membre.prenom}`, prods: fv.slice(0, 3) }] : []; })(),
      ...(() => {
        const h = new Date().getHours() + new Date().getMinutes() / 60;
        const mo = h < 11.5 ? { id: 'matin', nom: '☀️ le combo du matin', cats: ['cafes', 'cookies'] }
          : h < 18 ? null
          : { id: 'soir', nom: '🌙 ce soir', cats: ['hotcookie', 'glaces', 'sundae'] };
        if (!mo) return [];
        const prods = data.products.filter((p) => mo.cats.includes(p.cat_id) && !p.epuise && !prodLock(p) && p.actif !== false && (p.img || p.img_thumb)).slice(0, 3);
        return prods.length ? [{ id: mo.id, nom: mo.nom, prods }] : [];
      })(),
      ...(incontournables.length ? [{ id: 'incontournables', nom: 'les incontournables', prods: incontournables }] : []),
      { id: 'formules', nom: 'Formules & Duos', prods: byCat('formules') },
      ...(() => { const mb = data.products.filter((p) => p.moments_box && !p.epuise && p.actif !== false && !prodLock(p)); return mb.length ? [{ id: 'offrir', nom: '🎁 à offrir', prods: mb.slice(0, 3) }] : []; })(),
      ...(nouveautes.length ? [{ id: 'nouveautes', nom: 'nouveautés', prods: nouveautes.slice(0, 12) }] : []),
      ...(banners.length > 1 ? [{ id: 'moments', nom: 'boufi moments', prods: [], custom: 'moments' }] : []),
      ...(clubProds.length ? [{ id: 'club', nom: membre ? '🔓 exclusif boufi club' : '🔒 exclusif boufi club', prods: clubProds, club: true }] : []),
    ];
    const pvCfg = ultra?.pv;
    if (pvCfg) {
      sections = sections.filter((s) => !pvCfg[s.id]?.masque)
        .map((s) => (pvCfg[s.id]?.nom ? { ...s, nom: pvCfg[s.id].nom } : s));
    }
    const PV_MAX = { edition: 12, favoris: 8, matin: 8, soir: 8, incontournables: 3, formules: 8, offrir: 8, nouveautes: 8 };
    const dejaVus = new Set();
    sections = sections.map((s) => {
      if (s.custom || s.club || !s.prods.length) return s;
      const lim = pvCfg?.[s.id]?.max || PV_MAX[s.id] || 12;
      const prods = s.prods.filter((p) => !dejaVus.has(p.nom)).slice(0, lim);
      prods.forEach((p) => dejaVus.add(p.nom));
      return { ...s, prods };
    });
  } else if (univObj.id === 'edition') {
    sections = [{ id: 'edition', nom: edCfg.nom, prods: editionProds, manuel: true, sous_titre: "édition limitée — profitez-en tant qu'elle est là", colonnes: 2 }];
  } else {
    sections = univObj.cats.filter((c) => catIds.includes(c)).map((cid) => {
      const c = data.categories.find((x) => x.id === cid);
      let prods = byCat(cid);
      let manuel = false;
      if (c.produits?.length) {
        const sel = c.produits.map((n) => prods.find((p) => p.nom === n)).filter(Boolean);
        if (sel.length) { prods = sel; manuel = true; }
      }
      return { id: cid, nom: c.nom.toLowerCase() + (c.emoji ? ' ' + c.emoji : ''), prods, manuel, sous_titre: c.sous_titre || '', colonnes: c.colonnes };
    });
    if (univObj.id === 'gourmandises') {
      const pp = data.products.filter((p) => p.petit_plus && !prodLock(p));
      if (pp.length) sections.push({ id: 'petitsplus', nom: 'les petits + ✨', prods: pp, manuel: true, sous_titre: 'sauces & petits extras gourmands', colonnes: 3 });
    }
  }
  sections = sections.filter((s) => s.prods.length || s.custom);
  const secActive = secVisible || sections[0]?.id;


  const lancerRoue = async () => {
    try {
      const rs = (await axios.get(`${API}/borne/roue/segments?tel=${membre?.tel || tel}`)).data;
      const segs = rs.segments;
      const r = await axios.post(`${API}/borne/roue/spin`, { tel: membre?.tel || tel, boutique_id: BOUTIQUE });
      if (r.data.deja) { setRoue({ segs, gold: rs.gold, deja: true, label: r.data.label }); return; }
      const idx = r.data.gain.index;
      const deg = 5 * 360 + (360 - (idx * 60 + 30));
      setRoue({ segs, gold: r.data.gold, spinning: true, deg: 0, gain: null });
      setTimeout(() => setRoue((x) => x && { ...x, deg }), 80);
      setTimeout(() => setRoue((x) => x && { ...x, spinning: false, gain: r.data.gain }), 4500);
      if (r.data.gain.stamps) setMembre((m) => m && ({ ...m, stamps: (m.stamps ?? 0) + r.data.gain.stamps }));
    } catch { setRoue(null); }
  };

  const ajouter = (l, q = 1) => {
    ensureSession();
    evOnce('first_product_added', { nom: l.nom });
    logEv('ajout_panier', { nom: l.nom, px: l.px, q });
    pop();
    setPanier((p) => {
      const ex = p.find((x) => cle(x) === cle(l));
      return ex ? p.map((x) => (x === ex ? { ...x, q: x.q + q } : x)) : [...p, { ...l, q }];
    });
  };

  const ckCfg = ((data?.categories || []).find((c) => c.id === 'cookies') || {}).upsell_ck || {};
  const filtreCookies = (prods) => {
    const annexes = prods.filter((p) => !p.sous_cat);
    const cks = prods.filter((p) => p.sous_cat);
    return [...cks, ...annexes];
  };

  const cookieUpsell = (it) => {
    if (!it.sous_cat) return false;
    if (panier.some((l) => ['box', 'formules'].includes(catOfNom(l.nom)))) return false;
    const nbCk = panier.filter((l) => !l.reward && data.products.find((p) => p.nom === l.nom && p.cat_id === 'cookies' && p.sous_cat)).reduce((s, l) => s + l.q, 0) + 1;
    if (ckCfg.duo_actif !== false && nbCk === (ckCfg.duo_seuil || 1) && !duoUpShown.current) {
      const duoP = data.products.find((p) => p.nom === 'Duo Cookies' && !p.epuise && p.actif !== false);
      if (!duoP) return false;
      duoUpShown.current = true;
      const dispoCk = (n) => { const p = data.products.find((x) => x.nom === n); return p && !p.epuise && p.actif !== false; };
      const frais = Object.keys(data.fraicheur || {}).filter((n) => n !== it.nom && dispoCk(n))[0];
      const asso = !frais && hints?.assoc_cookies?.[it.nom] && hints.assoc_cookies[it.nom] !== it.nom && dispoCk(hints.assoc_cookies[it.nom]) ? hints.assoc_cookies[it.nom] : null;
      const pop = !frais && !asso && (data.num1 || {}).cookies && (data.num1 || {}).cookies !== it.nom && dispoCk((data.num1 || {}).cookies) ? (data.num1 || {}).cookies : null;
      const sug = frais ? { type: 'fournee', nom: frais } : asso ? { type: 'assoc', nom: asso } : pop ? { type: 'pop', nom: pop } : null;
      const abCfg = data.commercial?.ab_duo || {};
      let abV = null;
      if (abCfg.actif !== false && sug) {
        abV = abCfg.off === 'A' ? 'B' : abCfg.off === 'B' ? 'A'
          : ((csidRef.current || SID).split('').reduce((s2, c2) => s2 + c2.charCodeAt(0), 0) % 2 ? 'B' : 'A');
      }
      setUpsell({ duo: { p: duoP, base: it, sug: abV === 'A' ? null : sug, ab: abV } });
      track('propose', duoP.nom, duoP.px, 'duo-cookie', Math.max(0, Math.round((duoP.px - it.px) * 100) / 100), abV ? `ab-duo-${abV}` : 'v1');
      return true;
    }
    if (ckCfg.box_actif !== false && nbCk === (ckCfg.box_seuil || 3) && !boxUpShown.current) {
      const box4P = data.products.find((p) => p.choix_groupe === 'box_cookies' && p.choix_n === 4 && !p.epuise && p.actif !== false && !p.moments_box);
      if (!box4P) return false;
      const lignesAp = [...panier, { nom: it.nom, px: it.px, q: 1 }];
      const sansCk = lignesAp.filter((l) => catOfNom(l.nom) !== 'cookies');
      const deltaReel = Math.round((calcTotalPanier([...sansCk, { nom: box4P.nom, px: box4P.px, q: 1 }]) - calcTotalPanier(lignesAp)) * 100) / 100;
      const cMin = [...cookiesSimples].sort((a, b) => a.px - b.px)[0];
      const altSep = cMin ? Math.round((calcTotalPanier([...lignesAp, { nom: cMin.nom, px: cMin.px, q: 1 }]) - calcTotalPanier(lignesAp)) * 100) / 100 : null;
      if (altSep != null && deltaReel > altSep) return false;
      boxUpShown.current = true;
      setUpsell({ boxup: { p: box4P, deltaReel } });
      track('propose', box4P.nom, box4P.px, 'box4-cookies', deltaReel);
      return true;
    }
    return false;
  };

  const ajouterDirect = (it) => {
    ajouter({ nom: it.nom, px: it.px, opts: it.precompose || [] });
    if (it.precompose?.length) { track('accepte', it.nom, it.px, 'precompose'); return; }
    if (catOfNom(it.nom) !== 'cookies') return;
    if (cookieUpsell(it)) return;
    if (miniShown.current || !canAsk()) return;
    const catOf = (l) => catOfNom(l.nom);
    if (panier.some((l) => BOISSONS_ULTRA.includes(catOf(l)) || ['formules', 'box'].includes(catOf(l)))) return;
    const chercher = (m) => data.products.find((p) => p.cat_id === 'formules' && p.nom.toLowerCase().includes(m) && !prodLock(p) && !p.epuise);
    const sugg = [chercher('refresh'), chercher('cloud') || chercher('latte')].filter(Boolean).slice(0, 2);
    if (!sugg.length) return;
    miniShown.current = true;
    countAsk();
    setUpsell({ mini: sugg, base: it });
    track('propose', sugg.map((x) => x.nom).join(', '), 0, 'apres-ajout');
  };

  const track = (action, offre = '', montant = 0, type = '', pxAdd = null, version = 'v1') => { axios.post(`${API}/borne/upsell-event`, { action, offre, montant, type, sid: SID, csid: csidRef.current || '', px_add: pxAdd, version }).catch(() => {}); };
  const logEv = (type, dataEv = {}) => { if (DEMO) return; axios.post(`${API}/borne/parcours`, { sid: SID, type, data: dataEv, csid: csidRef.current || '', borne_id: `borne-${POSTE}`, boutique_id: BOUTIQUE }).catch(() => {}); };
  const ensureSession = () => { if (csidRef.current) return; csidRef.current = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; csFlags.current = {}; logEv('session_started'); };
  const evOnce = (type, d = {}) => { if (csFlags.current[type]) return; csFlags.current[type] = true; logEv(type, d); };
  const onActivity = () => { lastAct.current = Date.now(); ensureSession(); evOnce('first_touch'); };
  const ouvrirPanier = () => { setShowPanier(true); ensureSession(); evOnce('basket_opened', { articles: nbArt, total: totalAff }); };
  abandonRef.current = (statut) => {
    logEv('session_abandoned', { statut: statut || (panier.length ? 'panier_abandonne' : 'sans_panier'), articles: nbArt, total: totalAff });
    csidRef.current = ''; csFlags.current = {};
    setKeepAsk(false); deconnecter(); setEtape('veille');
  };
  if (!window.__bfDebut) { window.__bfDebut = true; logEv('debut'); }

  const valider = () => {
    logEv('valider_clic', { total, articles: nbArt });
    evOnce('checkout_started', { total: totalAff, articles: nbArt });
    if (envieShown.current) { setEtape('ident'); return; }
    const catOf = (l) => data.products.find((p) => p.nom === l.nom)?.cat_id;
    const BOISSONS = ['shake', 'cloud', 'cafes', 'refresher', 'bouteilles'];
    const nbCookies = panier.filter((l) => catOf(l) === 'cookies').reduce((s, l) => s + l.q, 0);
    const nbBoissons = panier.filter((l) => BOISSONS.includes(catOf(l))).reduce((s, l) => s + l.q, 0);
    const hasBox = panier.some((l) => catOf(l) === 'box');
    const hasFormule = panier.some((l) => catOf(l) === 'formules');
    const find = (nom) => data.products.find((p) => p.nom === nom && p.actif !== false);
    const chaud = meteo?.temp != null && meteo.temp >= 25;
    const froid = meteo?.pluie || (meteo?.temp != null && meteo.temp <= 12);
    let offre = null;
    const UR = data.upsell_rules || {};
    const hotSansGlace = panier.find((l) => catOf(l) === 'hotcookie' && !/glace/i.test(l.nom) && !l.reward);
    const dejaGlace = panier.some((l) => /glace/i.test(l.nom));
    const slotOk = (sl, prod) => !sl.groupe && sl.cats?.includes(prod.cat_id)
      && (!sl.mots || sl.mots.some((m) => prod.nom.toLowerCase().includes(m)))
      && (!sl.seuls || sl.seuls.includes(prod.nom));
    const slotProds = (sl) => data.products.filter((p) => sl.cats?.includes(p.cat_id) && p.actif !== false
      && (!sl.mots || sl.mots.some((m) => p.nom.toLowerCase().includes(m))) && (!sl.seuls || sl.seuls.includes(p.nom)));
    const minCookie = () => (cookiesSimples.length ? Math.min(...cookiesSimples.map((p) => p.px)) : 0);
    const formules = data.products.filter((p) => p.cat_id === 'formules' && p.actif !== false && !p.epuise && !prodLock(p) && (p.slots || []).length === 2);
    const chercheUpgrade = (ligne, type) => {
      const prod = data.products.find((p) => p.nom === ligne.nom);
      if (!prod) return null;
      let best = null;
      for (const f of formules) {
        let slFixe, slLibre, fixeNom, supp;
        if (type === 'boisson') {
          slFixe = f.slots.find((s) => slotOk(s, prod));
          slLibre = f.slots.find((s) => s.groupe === 'cookie' && !s.seuls);
          fixeNom = prod.nom;
          if (slFixe) {
            const cands = slotProds(slFixe);
            const pmin = cands.length ? Math.min(...cands.map((p) => p.px)) : 0;
            supp = slFixe.inclus ? 0 : Math.round((prod.px - pmin) * 100) / 100;
          }
        } else {
          const opt = BOX_MAP[prod.nom];
          slFixe = opt ? f.slots.find((s) => s.groupe === 'cookie' && G.cookie?.opts?.includes(opt) && (!s.seuls || s.seuls.includes(opt))) : null;
          slLibre = f.slots.find((s) => !s.groupe);
          fixeNom = opt;
          if (slFixe) supp = slFixe.inclus ? 0 : (G.cookie?.supp?.[opt] || 0);
        }
        if (!slFixe || !slLibre || slFixe === slLibre) continue;
        const pxF = Math.round((f.px + supp) * 100) / 100;
        const libreProds = slLibre.groupe === 'cookie' ? null : slotProds(slLibre);
        const cLibre = slLibre.groupe === 'cookie'
          ? [...cookiesSimples].sort((a, b) => a.px - b.px)[0]
          : (libreProds.length ? [...libreProds].sort((a, b) => a.px - b.px)[0] : null);
        const base = calcTotalPanier(panier);
        const sansLigne = [...panier.filter((l) => l !== ligne), ...(ligne.q > 1 ? [{ ...ligne, q: ligne.q - 1 }] : [])];
        const apres = calcTotalPanier([...sansLigne, { nom: f.nom, px: pxF, q: 1 }]);
        const delta = Math.max(0, Math.round((apres - base) * 100) / 100);
        const eco = cLibre ? Math.max(0, Math.round((calcTotalPanier([...panier, { nom: cLibre.nom, px: cLibre.px, q: 1 }]) - apres) * 100) / 100) : 0;
        if (!best || pxF < best.pxF) best = { f, slFixe, slLibre, pxF, fixeNom, delta, eco, prod };
      }
      return best;
    };
    if (UR.glace !== false && hotSansGlace && !dejaGlace) {
      const g = find(`${hotSansGlace.nom} + Glace`) || find('Hot Cookie + Glace');
      if (g && !g.epuise) {
        const delta = Math.max(0, Math.round((g.px - hotSansGlace.px) * 100) / 100);
        offre = { rule: 'glace', p: g, pxAff: g.px, titre: 'Une boule de glace avec ?', texte: `Ton ${hotSansGlace.nom} sort chaud du four — ajoute la glace pour +${eur(delta)}.`, upgrade: { simple: true, remplaceNom: hotSansGlace.nom } };
      }
    }
    if (!offre && ucfg.promo3_active && nbCookies === 2 && !hasBox) {
      const pct = Math.round(ucfg.promo3_pct || 50);
      const cs = data.products.filter((p) => p.cat_id === 'cookies' && !p.compose && !p.choix_n && p.actif !== false && !p.epuise && !prodLock(p) && (p.img || p.img_thumb));
      const top = [...cs].sort((a, b) => (ventesJour[b.nom] || 0) - (ventesJour[a.nom] || 0)).slice(0, 3);
      if (top.length) {
        envieShown.current = true;
        setUpsell({ list: top, titre: `Le 3ᵉ cookie à −${pct}% 🍪`, texte: `Ajoutez un 3ᵉ cookie, la remise de −${pct}% s'applique toute seule sur le moins cher.` });
        track('propose', top.map((s) => s.nom).join(', '), 0, 'cookie3');
        return;
      }
    }
    if (!offre && UR.box !== false && nbCookies >= 3 && !hasBox) {
      const box = find('Box Cookies x4');
      if (box && !box.epuise) {
        const sansCk = panier.filter((l) => catOf(l) !== 'cookies');
        const deltaReel = Math.round((calcTotalPanier([...sansCk, { nom: box.nom, px: box.px, q: 1 }]) - calcTotalPanier(panier)) * 100) / 100;
        const cMin = [...cookiesSimples].sort((a, b) => a.px - b.px)[0];
        const altSep = cMin ? Math.round((calcTotalPanier([...panier, { nom: cMin.nom, px: cMin.px, q: 1 }]) - calcTotalPanier(panier)) * 100) / 100 : null;
        if (altSep == null || deltaReel <= altSep) offre = { rule: 'box', p: box, deltaAff: deltaReel, titre: 'Et si tu passais à la Box Cookies ?', texte: deltaReel > 0 ? `Tes cookies rangés dans une Box de 4 pour +${eur(deltaReel)} — on garde tes parfums, tu complètes.` : 'Tes cookies rangés dans une Box de 4 — on garde tes parfums, tu complètes.' };
      }
    }
    if (!offre && UR.formule_cookie !== false && nbCookies === 1 && nbBoissons === 0 && !hasFormule && !hasBox && !miniShown.current) {
      const lc = panier.find((l) => catOf(l) === 'cookies' && !l.reward);
      const up = lc && chercheUpgrade(lc, 'cookie');
      if (up) offre = {
        rule: 'formule_cookie', p: up.f, pxAff: up.pxF, deltaAff: up.delta,
        imgAff: (chaud && up.f.img_meteo_chaud) || (froid && up.f.img_meteo_froid) || null,
        titre: chaud ? `Il fait ${Math.round(meteo.temp)}° dehors ☀️` : 'Une boisson avec ton cookie ?',
        texte: `Ton ${up.prod.nom} passe en formule ${up.f.nom} : ajoute une boisson au choix pour +${eur(up.delta)}${up.eco >= minEco ? ` — tu économises ${eur(up.eco)}` : ''}.`,
        upgrade: { slotSel: { [up.slFixe.id]: up.fixeNom }, remplaceNom: up.prod.nom, openSlot: up.slLibre.id },
      };
    }
    if (!offre && UR.formule_boisson !== false && nbBoissons === 1 && nbCookies === 0 && !hasFormule && !hasBox) {
      const lb = panier.find((l) => BOISSONS.includes(catOf(l)) && !l.reward);
      const up = lb && chercheUpgrade(lb, 'boisson');
      if (up) {
        const laitPref = (lb.opts || []).find((o) => G.lait?.opts?.includes(o));
        offre = {
          rule: 'formule_boisson', p: up.f, pxAff: up.pxF, deltaAff: up.delta,
          imgAff: (chaud && up.f.img_meteo_chaud) || (froid && up.f.img_meteo_froid) || null,
          titre: 'Un cookie avec ta boisson ?',
          texte: `Ton ${up.prod.nom} passe en formule ${up.f.nom} : ajoute un cookie au choix pour +${eur(up.delta)}${up.eco >= minEco ? ` — tu économises ${eur(up.eco)}` : ''}.`,
          upgrade: { slotSel: { [up.slFixe.id]: up.prod.nom }, remplaceNom: up.prod.nom, openSlot: up.slLibre.id, ...(laitPref ? { laits: { [up.slFixe.id]: laitPref } } : {}) },
        };
      }
    }
    const offreSaves = !!offre && ((offre.eco || 0) >= minEco || /économis/i.test(offre.texte || ''));
    if (offre && (offreSaves || canAsk())) { if (!offreSaves) countAsk(); envieShown.current = true; setUpsell({ smart: offre }); track('propose', offre.p.nom, offre.p.px, offre.rule, offre.deltaAff ?? null); return; }
    if (UR.liste === false) { setEtape('ident'); return; }
    const EC = data.upsell_ecran || {};
    const inPanier = (nom) => panier.some((l) => l.nom === nom);
    const okP = (p) => p && p.actif !== false && !p.epuise && !prodLock(p) && !inPanier(p.nom) && (p.img || p.img_thumb);
    const byNom = (nom) => { const p = nom && data.products.find((x) => x.nom === nom); return okP(p) ? p : null; };
    const cherche = (re, cats) => data.products.find((p) => okP(p) && (!cats || cats.includes(p.cat_id)) && re.test(p.nom));
    const roleProd = {
      sauce: () => byNom(EC.sauce) || cherche(/sauce|dipper/i, null),
      mini: () => byNom(EC.mini) || cherche(/mini/i, ['cookies', 'box', 'hotcookie']),
      boisson: () => byNom(EC.boisson) || [...data.products].filter((p) => okP(p) && BOISSONS.includes(p.cat_id) && !p.compose && !p.choix_n)
        .sort((a, b) => (ventesJour[b.nom] || 0) - (ventesJour[a.nom] || 0))[0],
      gourmandise: () => byNom(EC.gourmandise) || cherche(/./, ['hotcookie', 'icecookie', 'glaces', 'sundae']),
      formule: () => ((!hasFormule && !hasBox && formules.filter((f) => okP(f)).length)
        ? [...formules].filter((f) => okP(f)).sort((a, b) => a.px - b.px)[0] : null),
    };
    const ordreRoles = hasBox ? ['sauce', 'boisson', 'mini']
      : hasFormule ? ['sauce', 'mini', 'gourmandise']
        : (nbCookies > 0 && nbBoissons === 0) ? ['boisson', 'sauce', 'formule']
          : (nbCookies > 0 && nbBoissons > 0) ? ['mini', 'sauce', 'gourmandise']
          : ['sauce', 'mini', 'boisson'];
    const vus = new Set();
    const sugg = [];
    if (nbCookies > 0 && nbBoissons === 0) {
      const petit = [...data.products].filter((p) => okP(p) && p.cat_id === 'cafes' && !p.compose && !p.choix_n).sort((a, b) => a.px - b.px)[0];
      const ice = [...data.products].filter((p) => okP(p) && p.cat_id !== 'cafes' && BOISSONS.includes(p.cat_id) && /ice|iced|latte/i.test(p.nom)).sort((a, b) => (ventesJour[b.nom] || 0) - (ventesJour[a.nom] || 0))[0];
      const shake = [...data.products].filter((p) => okP(p) && p.cat_id === 'shake' && !p.compose).sort((a, b) => (ventesJour[b.nom] || 0) - (ventesJour[a.nom] || 0))[0];
      [petit, ice, shake].forEach((p) => { if (p && !vus.has(p.nom) && sugg.length < 3) { vus.add(p.nom); sugg.push(p); } });
    }
    const backfill = (nbCookies > 0 && nbBoissons > 0) ? ['gourmandise', 'sauce', 'mini'] : ['boisson', 'gourmandise', 'sauce', 'mini'];
    for (const r of [...ordreRoles, ...backfill]) {
      if (sugg.length >= 3) break;
      const p = roleProd[r]?.();
      if (p && !vus.has(p.nom)) { vus.add(p.nom); sugg.push(p); }
    }
    if (sugg.length && canAsk()) {
      countAsk();
      envieShown.current = true;
      setUpsell({ list: sugg.slice(0, 3), ecran: true });
      track('propose', sugg.slice(0, 3).map((s) => s.nom).join(', '), 0, 'ecran');
    } else setEtape('ident');
  };

  const accepterSmart = () => {
    const { p, upgrade } = upsell.smart;
    track('accepte', p.nom, p.px, upsell.smart.rule || 'smart');
    setUpsell(null);
    if (upgrade) {
      if (upgrade.simple) {
        setPanier((prev) => { const i = prev.findIndex((x) => x.nom === upgrade.remplaceNom); const sans = i < 0 ? prev : (prev[i].q > 1 ? prev.map((x, k) => (k === i ? { ...x, q: x.q - 1 } : x)) : prev.filter((_, k) => k !== i)); return [...sans, { nom: p.nom, px: p.px, q: 1, opts: [] }]; });
        setEtape('ident');
        return;
      }
      setDetail({ it: p, choix: {}, multi: {}, q: 1, slotSel: upgrade.slotSel, laits: upgrade.laits || {}, remplaceNom: upgrade.remplaceNom, slotOpen: upgrade.openSlot, slotIdx: 0 });
      return;
    }
    if (p.choix_n) {
      const grp = G[p.choix_groupe];
      const pre = {}; let n = 0; const reste = [];
      for (const l of panier) {
        const cid = data.products.find((x) => x.nom === l.nom)?.cat_id;
        const opt = BOX_MAP[l.nom];
        if (cid === 'cookies' && opt && grp.opts.includes(opt)) {
          let q = l.q;
          while (q > 0 && n < p.choix_n) { pre[opt] = (pre[opt] || 0) + 1; n++; q--; }
          if (q > 0) reste.push({ ...l, q });
        } else reste.push(l);
      }
      setPanier(reste);
      setDetail({ it: p, choix: {}, multi: pre, q: 1 });
    } else {
      ajouter({ nom: p.nom, px: p.px, opts: [] });
      setEtape('ident');
    }
  };

  const envoyer = async () => {
    if (envoi) return;
    setMsg(['', '']);
    setEnvoi(true);
    const t0 = Date.now();
    if (DEMO) {
      await new Promise((res) => setTimeout(res, 1900));
      setEnvoi(false);
      setFinNum('DÉMO');
      setFinTampon(null);
      setPanier([]); setEtape('fin');
      return;
    }
    const local_id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const usedReward = panier.some((l) => l.reward && !l.defi && !l.destock);
    const payload = { prenom: prenom.trim(), tel, membre: !!membre, tier: membre?.tier || '', lignes: panier, local_id, borne_id: `borne-${POSTE}`, boutique_id: BOUTIQUE, menu_version: menuVersion, club_join: clubChoix === 'rejoindre', rgpd_consent: clubChoix === 'rejoindre', allergies: allergies.map((a) => ALLERG_LABEL[a] || a), club_v2: !!cv2Ref.current?.token, club_v2_token: cv2Ref.current?.token || '', roue_gain: roueGain && (roueGain.type !== 'produit' || panier.some((l) => l.roue)) ? { day: roueGain.day, gain: roueGain.gain } : null };
    if (payload.club_join) track('accepte', 'club_signup_completed', 0, wcSeenRef.current ? 'welcome' : 'club');
    try {
      const r = await axios.post(`${API}/borne/orders`, payload);
      setFinNum(r.data.num);
      setFinTampon(r.data.tampon || null);
      if (r.data.tampon) setMembre((m) => (m ? { ...m, stamps: (m.stamps ?? 0) + r.data.tampon } : m));
      logEv('commande_ok', { num: r.data.num, total, articles: nbArt, formule: panier.some((l) => catOfNom(l.nom) === 'formules') });
      logEv('order_validated', { num: r.data.num, local_id, total: totalAff, articles: nbArt });
      axios.get(`${API}/drop/stock/bac-to-boufi`).then((rr) => setBtb(rr.data?.borne_visible !== false && rr.data?.total?.restantes > 0 ? rr.data : null)).catch(() => {});
    } catch (e) {
      if (e.response && e.response.status >= 400 && e.response.status < 500) {
        setEnvoi(false);
        setMsg(['ko', e.response.data?.detail || 'Commande refusée, vérifie ton panier']);
        setEtape('ident');
        return;
      }
      const q = JSON.parse(localStorage.getItem('borne_queue') || '[]');
      q.push(payload); localStorage.setItem('borne_queue', JSON.stringify(q));
      setFinNum('…');
      setFinTampon(null);
    }
    await new Promise((res) => setTimeout(res, Math.max(0, 1900 - (Date.now() - t0))));
    setEnvoi(false);
    if (usedReward && membre) setMembre((m) => m && ({ ...m, stamps: Math.max(0, (m.stamps ?? 0) - 10) }));
    setPanier([]); setEtape('fin');
  };

  const chercheClub = async () => {
    if (tel.length !== 10) { setMsg(['ko', 'Tapez d\'abord vos 10 chiffres']); return; }
    try {
      const r = await axios.get(`${API}/borne/club/lookup?phone=${tel}&source=borne`);
      if (r.data.found) { setMembre({ ...r.data, tel }); setPrenom(r.data.prenom); setMsg(['ok', `Reconnu : ${r.data.prenom}, membre ${r.data.tier}`]); track('accepte', 'club_login_success', 0, wcSeenRef.current ? 'welcome' : 'club'); }
      else setMsg(['ko', 'Ce numéro n\'est pas encore dans le Club']);
    } catch { setMsg(['ko', 'Vérification impossible, réessayez']); }
  };

  const modifierLigne = (i) => {
    const l = panier[i];
    if (l.reward) return;
    const p = data.products.find((x) => x.nom === l.nom);
    if (!p) return;
    const choix = {}; const multi = {};
    const composeGroups = p.compose || [];
    let ci = 0;
    for (const o of l.opts || []) {
      const m = o.match(/^(\d+)× (.+)$/);
      if (m && p.choix_groupe) multi[m[2]] = parseInt(m[1], 10);
      else if (ci < composeGroups.length) { choix[composeGroups[ci]] = o; ci++; }
    }
    const slotSel = {};
    if (p.slots) {
      for (const sl of p.slots) {
        const noms = sl.groupe ? (G[sl.groupe]?.opts || []) : data.products.filter((x) => sl.cats?.includes(x.cat_id)).map((x) => x.nom);
        const found = (l.opts || []).find((o) => noms.includes(o));
        if (found) slotSel[sl.id] = found;
      }
    }
    setShowPanier(false);
    setDetail({ it: p, choix, multi, q: l.q, editIdx: i, slotSel });
  };

  const detailComplet = detail && (detail.it.slots?.length ? true : (detail.it.compose || []).every((g) => detail.choix[g]))
    && (!detail.it.choix_n || Object.values(detail.multi || {}).reduce((s, n) => s + n, 0) === detail.it.choix_n);
  const gBox = detail?.it.choix_groupe ? G[detail.it.choix_groupe] : null;
  const nBox = detail?.it.choix_n || 0;
  const totBox = gBox ? Object.values(detail.multi || {}).reduce((s, n) => s + n, 0) : 0;
  const optInclus = (g) => !!detail && (detail.it.opts_incluses || []).includes(g);
  const simpleB = data?.fiche_version === 'B' && !!detail && !(detail.it.compose || []).length && !detail.it.choix_groupe && !detail.it.slots && !(detail.it.tailles || []).length && !detail.it.moments_box;
  const suppTot = (gBox ? Object.entries(detail.multi || {}).reduce((s, [o, n]) => s + n * (gBox.supp?.[o] || 0), 0) : 0)
    + (detail ? (detail.it.compose || []).reduce((s, g) => s + (optInclus(g) ? 0 : (G[g]?.supp?.[detail.choix[g]] || 0)), 0) : 0);

  const duoMode = detail?.it.choix_groupe === 'duo_cookies';
  const boxAnim = !!gBox && !detail?.it.slots && !detail?.it.moments_box && (duoMode || nBox === 4)
    && (!detail?.it.tailles?.length || !!detail?.tailleChoisie);
  const bxPicks = () => detail?.picks ?? Object.entries(detail?.multi || {}).flatMap(([o, c]) => Array(c).fill(o));
  const bxSync = (picks) => setDetail((d) => {
    const multi = {};
    picks.forEach((o) => { multi[o] = (multi[o] || 0) + 1; });
    return { ...d, picks, multi };
  });
  const bxWarn = (m) => {
    setBxMsg(m); setBxShake((t) => t + 1);
    clearTimeout(bxTimer.current);
    bxTimer.current = setTimeout(() => setBxMsg(null), 2600);
  };
  const bxAdd = (o, cardEl) => {
    const picks = bxPicks();
    if (picks.length >= nBox) { bxWarn('Boîte pleine : touche un cookie pour l\u2019enlever'); return; }
    const idx = picks.length;
    setBxComboOk(false);
    setBxHide((h) => ({ ...h, [idx]: true }));
    bxSync([...picks, o]);
    flyCookie(gBox.imgs?.[o], cardEl?.querySelector('img.ph') || cardEl, bxSlots.current[idx])
      .then(() => setBxHide((h) => ({ ...h, [idx]: false })));
  };
  const bxRemove = (idx) => {
    const slot = bxSlots.current[idx];
    slot?.classList?.add('bx-out');
    setBxComboOk(false); setBxMsg(null);
    setTimeout(() => { slot?.classList?.remove('bx-out'); bxSync(bxPicks().filter((_, k) => k !== idx)); }, 220);
  };
  const bxRefuse = (cardEl) => {
    if (!cardEl) return;
    cardEl.classList.add('bx-no');
    setTimeout(() => cardEl.classList.remove('bx-no'), 520);
  };
  const bxCombo = boxAnim ? ((hints?.top_combos?.[duoMode ? 'duo' : 'box4']) || [])
    .find((c) => c.cookies.length === nBox && c.cookies.every((o) => (gBox.opts || []).includes(o) && !(gBox.epuise || []).includes(o))) : null;
  const bxPrendreCombo = () => {
    if (!bxCombo || bxBusy) return;
    setBxBusy(true); setBxMsg(null); setBxComboOk(false);
    setBxHide(Object.fromEntries(bxCombo.cookies.map((_, i) => [i, true])));
    bxSync([]);
    const acc = [];
    bxCombo.cookies.forEach((o, i) => {
      setTimeout(() => {
        acc.push(o);
        bxSync([...acc]);
        const card = bxGrid.current[o];
        flyCookie(gBox.imgs?.[o], card?.querySelector('img.ph') || card, bxSlots.current[i])
          .then(() => setBxHide((h) => ({ ...h, [i]: false })));
      }, i * 250);
    });
    setTimeout(() => { setBxBusy(false); setBxComboOk(true); }, bxCombo.cookies.length * 250 + 850);
    track('accepte', detail.it.nom, detail.it.px || 0, duoMode ? 'combo-duo' : 'combo-box4');
  };

  const fSlots = detail?.it.slots || null;
  const slotCands = (slot) => {
    if (slot.groupe) {
      const g = G[slot.groupe] || { opts: [] };
      const opts = slot.seuls ? g.opts.filter((o) => slot.seuls.includes(o)) : g.opts;
      return opts.map((o) => ({ nom: o, img: g.imgs?.[o], cont: g.contenance, supp: slot.inclus ? 0 : (g.supp?.[o] || 0), lock: optLockTier(slot.groupe, o), epuise: (g.epuise || []).includes(o) }));
    }
    let prods = data.products.filter((p) => slot.cats?.includes(p.cat_id)
      && (!slot.mots || slot.mots.some((m) => p.nom.toLowerCase().includes(m))));
    if (slot.seuls) prods = prods.filter((p) => slot.seuls.includes(p.nom));
    const pmin = prods.length ? Math.min(...prods.map((p) => p.px)) : 0;
    return prods.map((p) => ({ nom: p.nom, img: p.img_thumb || p.img, cont: p.contenance, supp: slot.inclus ? 0 : Math.round(Math.max(0, p.px - pmin) * 100) / 100,
      lock: prodLock(p) ? (p.club_tier || 'bronze') : null, epuise: !!p.epuise }));
  };
  const slotChoisi = (slot) => {
    const cands = slotCands(slot);
    return cands.find((c) => c.nom === detail.slotSel?.[slot.id] && !c.epuise)
      || cands.find((c) => c.nom === slot.defaut && !c.lock && !c.epuise)
      || cands.find((c) => !c.lock && !c.epuise && !c.supp && c.img) || cands.find((c) => !c.lock && !c.epuise && !c.supp)
      || cands.find((c) => !c.lock && !c.epuise) || cands.find((c) => !c.epuise) || cands[0];
  };
  const suppSlots = fSlots ? fSlots.reduce((s, sl) => s + (slotChoisi(sl)?.supp || 0)
    + (!sl.groupe ? (G.lait?.supp?.[detail.laits?.[sl.id]] || 0) : 0), 0) : 0;
  const slotsEpuise = fSlots ? fSlots.some((sl) => slotChoisi(sl)?.epuise) : false;
  const sommeReelle = fSlots ? Math.round(fSlots.reduce((s, sl) => {
    const c = slotChoisi(sl);
    if (!c) return s;
    const pr = data.products.find((p) => p.nom === c.nom || BOX_MAP[p.nom] === c.nom);
    return s + (pr?.px || c.px || 0);
  }, 0) * 100) / 100 : 0;
  const sepReelle = fSlots ? Math.max(0, Math.round((sommeReelle - (ucfg.duo_active ? (ucfg.duo_remise || 0.5) : 0)) * 100) / 100) : 0;
  const ajouterDetail = (moment) => {
    const optsC = (detail.it.compose || []).map((g) => detail.choix[g]).filter(Boolean);
    const optsB = gBox ? Object.entries(detail.multi || {}).filter(([, n]) => n > 0).map(([o, n]) => `${n}× ${o}`) : [];
    const optsS = fSlots ? fSlots.flatMap((sl) => {
      const base = slotChoisi(sl)?.nom;
      const lait = detail.laits?.[sl.id];
      return [base, ...(lait && lait !== G.lait?.opts?.[0] ? [lait] : [])];
    }).filter(Boolean) : [];
    const optsM = moment ? [`🎁 Pour ${moment.recipient} · ${moment.occasion}`] : [];
    const ligne = { nom: detail.it.nom, px: detail.it.px + suppTot + suppSlots, opts: [...optsS, ...optsC, ...optsB, ...optsM], ...(moment ? { moment } : {}), ...(detail.tailleChoisie ? { taille: detail.tailleChoisie } : {}) };
    if (detail.remplaceNom) setPanier((p) => { const i = p.findIndex((x) => x.nom === detail.remplaceNom); return i < 0 ? p : (p[i].q > 1 ? p.map((x, k) => (k === i ? { ...x, q: x.q - 1 } : x)) : p.filter((_, k) => k !== i)); });
    if (detail.remplaceNoms?.length) setPanier((p) => { let out = [...p]; for (const nom of detail.remplaceNoms) { const i = out.findIndex((x) => x.nom === nom); if (i >= 0) out = out[i].q > 1 ? out.map((x, k) => (k === i ? { ...x, q: x.q - 1 } : x)) : out.filter((_, k) => k !== i); } return out; });
    if (detail.editIdx != null) {
      pop();
      setPanier((p) => p.map((x, k) => (k === detail.editIdx ? { ...ligne, q: detail.q } : x)));
      setShowPanier(true);
    } else ajouter(ligne, detail.q);
    logEv('product_added', { nom: detail.it.nom, q: detail.q, px: ligne.px, version: vuRef.current.nom === detail.it.nom ? vuRef.current.v : 'A', ms: vuRef.current.t && vuRef.current.nom === detail.it.nom ? Date.now() - vuRef.current.t : null });
    setDetail(null); setInside(false);
  };

  return (
    <div className={`bo bd bd-2026${KC ? ' bd-kc' : ''}${menuVersion === 'lateral' ? ' bd-lat' : ''}${ckprem ? ' bd-ckprem' : ''}${theme?.base === 'epure' ? ' bd-theme-epure' : ''}${theme?.base === 'editorial' ? ' bd-theme-editorial' : ''}${theme?.tokens?.carte_fond ? ' th-cf' : ''}${theme?.tokens?.carte_bordure ? ' th-cb' : ''}${theme?.tokens?.carte_ombre ? ' th-co' : ''}${theme?.base === 'epure' && heroEpure?.masquer_bannieres ? ' th-noban' : ''}`}
      style={{ position: 'fixed', inset: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column',
        ...(['epure', 'editorial'].includes(theme?.base) ? (() => { const t = theme.tokens || {}; const v = {};
          if (t.couleur_primaire) v['--th-primaire'] = t.couleur_primaire;
          if (t.couleur_secondaire) v['--th-secondaire'] = t.couleur_secondaire;
          if (t.couleur_accent) v['--th-accent'] = t.couleur_accent;
          if (t.couleur_fond) { v['--th-fond'] = t.couleur_fond; v['--th-nav-couleur'] = t.nav_couleur || t.couleur_fond; }
          if (t.couleur_texte) v['--th-texte'] = t.couleur_texte;
          if (t.taille_titres) v['--th-taille-titres'] = `${t.taille_titres}px`;
          if (t.taille_nom_produit) v['--th-taille-nom'] = `${t.taille_nom_produit}px`;
          if (t.taille_prix) v['--th-taille-prix'] = `${t.taille_prix}px`;
          if (t.nav_largeur) v['--th-nav-largeur'] = `${t.nav_largeur}px`;
          if (t.nav_icones_taille) v['--th-nav-ico'] = `${t.nav_icones_taille}px`;
          if (t.carte_rayon != null) v['--th-carte-rayon'] = `${t.carte_rayon}px`;
          return v; })() : {}) }}
      data-testid="borne-client"
      onPointerDownCapture={onActivity}>
      {data?.mode_test && <div className="bd-testmode" data-testid="borne-mode-test">🧪 MODE TEST — les commandes ne sont pas envoyées à la caisse Biborne</div>}
      <div className="bo-head">
        <img src="/logo-boufi-brun.png" alt="boufi" style={{ height: 32 }} />
        {etape === 'carte' && (
          <button className="bd-allergtop" style={{ marginLeft: 'auto' }} onClick={() => setAllergOpen(true)} data-testid="borne-allerg-top">⚠️ Allergènes{allergies.length ? ` · ${allergies.length}` : ''}</button>
        )}
        {etape === 'carte' && (
          <button className="bd-club-head pulse" onClick={() => { if (fanLogged.current && !fanAcc.current) { fanAcc.current = true; track('accepte', 'Boufi Club', 0, 'anim-fan'); } setClubOpen(true); }} data-testid="borne-club-header-btn">
            ★ {membre ? membre.prenom : 'Boufi Club'}
            {!clubOpen && !detail && !roueDock && !roue && !panier.length && !fanDone && (
              <span className="bd-clubfan" aria-hidden="true" data-testid="borne-club-fan">
                {['🎡', '🍪', '🎟️', '🎁', '⭐'].map((ic, i) => (
                  <span key={i} className="cf-card">{ic}</span>
                ))}
              </span>
            )}
          </button>
        )}
        {etape !== 'carte' && (
          <div style={{ marginLeft: 'auto', fontSize: 13, color: 'rgba(46,19,11,.55)' }}>
            {etape === 'ident' ? 'Dernière étape' : etape === 'fin' ? 'Merci !' : ''}
          </div>
        )}
      </div>

      {etape === 'veille' && theme?.base === 'editorial' && (
        <KiffVeille onStart={() => { wcNextRef.current = 'kiff'; if (cv2cfg?.actif) { setEtape('kiff'); setCv2Open('welcome'); track('propose', 'welcome_screen_viewed', 0, 'welcome'); } else if (wc.actif) { setEtape('welcome'); track('propose', 'welcome_screen_viewed', 0, 'welcome'); } else setEtape('kiff'); }} visuels={kiffVisuels} />
      )}
      {etape === 'kiff' && (
        <KiffFlow
          api={API}
          sid={SID}
          membre={!!membre}
          nbArt={nbArt}
          total={total}
          logEv={logEv}
          visuels={kiffVisuels}
          onVoirCarte={() => setEtape('carte')}
          onAjouter={(nom, px) => ajouter({ nom, px, opts: [] })}
          onOuvrir={(nom) => { const p = data.products.find((x) => x.nom === nom); setEtape('carte'); if (p) setDetail({ it: p, choix: {}, multi: {}, q: 1 }); }}
          onAllergenes={() => setAllergOpen(true)}
          onPanier={() => { setEtape('carte'); ouvrirPanier(); }}
        />
      )}
      {etape === 'veille' && (wc.actif || cv2cfg?.actif) && (
        <div data-testid="welcome-intercept" data-v2={cv2cfg?.actif ? '1' : '0'} style={{ position: 'fixed', inset: 0, zIndex: 55 }}
          onClick={() => {
            refreshCatalog();
            wcNextRef.current = theme?.base === 'editorial' ? 'kiff' : 'carte';
            if (cv2cfg?.actif) { setEtape(wcNextRef.current); setCv2Open('welcome'); track('propose', 'welcome_screen_viewed', 0, 'welcome'); }
            else { wcSeenRef.current = true; setEtape('welcome'); track('propose', 'welcome_screen_viewed', 0, 'welcome'); }
          }} />
      )}
      {cv2cfg?.actif && cv2Open && (
        <BorneClubV2 api={API} poste={POSTE} cfg={cv2cfg} mode={cv2Open} track={track}
          onBack={() => { setCv2Open(null); setEtape('veille'); }}
          onGuest={() => setCv2Open(null)}
          onIdentified={(token, profil, telv) => {
            cv2Ref.current = { token };
            setCv2({ token, profil });
            setMembre({ ...profil, tel: telv });
            setTel(telv); setTelOk(true); setPrenom(profil.prenom || '');
            setCv2Open(null);
            axios.get(`${API}/borne/roue/gains?tel=${telv}`).then((r) => setRoueGains(r.data.gains || [])).catch(() => {});
          }} />
      )}
      {cv2cfg?.actif && etape === 'carte' && !detail && !cv2Open && (
        <ClubBarV2 profil={cv2?.profil} cfg={cv2cfg} giftInCart={panier.some((l) => l.reward)}
          onOpen={() => setMonClubOpen(true)} onGift={() => setGiftOpen(true)} onLogin={() => setCv2Open('ident')} />
      )}
      {cv2?.profil && etape === 'carte' && !detail && !cv2Open && !monClubOpen && !rouePick && (roueGains.length > 0 || roueGain) && (
        roueGain ? (
          <div className="fredoka" data-testid="borne-roue-gain-ok"
            style={{ position: 'fixed', bottom: 130, left: '50%', transform: 'translateX(-50%)', zIndex: 45, background: '#e6f4e6', color: '#1b7a3d', border: '2px solid #1b7a3d', borderRadius: 999, padding: '12px 26px', fontSize: 17, fontWeight: 800, whiteSpace: 'nowrap' }}>
            🎡 {roueGain.label} {roueGain.type === 'remise' ? '— déduit à l\'encaissement ✓' : '— ajouté au panier ✓'}
          </div>
        ) : (
          <button className="fredoka" data-testid="borne-roue-gain-banner"
            onClick={() => { const g = roueGains[0]; if (g.type === 'remise') { setRoueGain(g); } else { setRouePick(g); } }}
            style={{ position: 'fixed', bottom: 130, left: '50%', transform: 'translateX(-50%)', zIndex: 45, background: '#5C1420', color: '#F6EEE3', border: 'none', borderRadius: 999, padding: '16px 30px', fontSize: 19, fontWeight: 800, cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,.35)', whiteSpace: 'nowrap' }}>
            🎡 Ton gain de la roue : {roueGains[0].label} — {roueGains[0].type === 'remise' ? 'Appliquer' : 'Ajouter'} ›
          </button>
        )
      )}
      {rouePick && (
        <GiftSheetV2 titre={`🎡 ${rouePick.label}`} sousTitre="Ton gain de la roue — choisis, il sera à 0 € dans ton panier."
          produits={(data?.products || []).filter((p) => {
            if (p.epuise || p.slots || prodLock(p)) return false;
            if (rouePick.choix === 'cookies') return p.cat_id === 'cookies' && !(p.tailles?.length);
            if (rouePick.choix === 'shake') return p.cat_id === 'shake';
            return p.cat_id === 'cookies' && /\bmini\b/i.test(p.nom) && !/box|eggs/i.test(p.nom);
          }).slice(0, 12)}
          onClose={() => setRouePick(null)}
          onPick={(nom) => {
            setPanier((p) => [...p.filter((l) => !l.roue), { nom, px: 0, opts: ['🎡 Gain de la roue'], q: 1, roue: true }]);
            setRoueGain(rouePick); setRouePick(null); setShowPanier(true);
          }} />
      )}
      {monClubOpen && cv2?.profil && (
        <MonClubV2 profil={cv2.profil} cfg={cv2cfg} giftInCart={panier.some((l) => l.reward)}
          onClose={() => setMonClubOpen(false)}
          onGift={() => { setMonClubOpen(false); setGiftOpen(true); }}
          onLogout={() => {
            if (cv2Ref.current?.token) axios.post(`${API}/borne/club/v2/logout`, {}, { headers: { 'X-Club-Token': cv2Ref.current.token } }).catch(() => {});
            cv2Ref.current = null; setCv2(null); setMonClubOpen(false);
            setMembre(null); setTel(''); setTelOk(false); setPrenom('');
            setRoueGains([]); setRoueGain(null); setRouePick(null);
            setPanier((p) => p.filter((l) => !l.reward && !l.roue));
          }} />
      )}
      {giftOpen && (
        <GiftSheetV2 produits={(data?.products || []).filter((p) => p.cat_id === 'cookies' && !p.epuise && !p.slots && !prodLock(p)).slice(0, 12)}
          onClose={() => setGiftOpen(false)}
          onPick={(nom) => {
            setPanier((p) => [...p.filter((l) => !l.reward), { nom, px: 0, opts: ['Offert fidélité — 10 tampons'], q: 1, reward: true }]);
            setGiftOpen(false); setShowPanier(true);
          }} />
      )}
      {etape === 'welcome' && (
        <div data-testid="borne-welcome" style={{ position: 'fixed', inset: 0, zIndex: 60, background: '#F6EEE3', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, minHeight: 0, background: `url(${wc.img}) center top / contain no-repeat #F6EEE3` }} />
          <div style={{ padding: '14px 22px 26px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {membre && (
              <div data-testid="welcome-bonretour" className="fredoka" style={{ background: 'rgba(92,20,32,.08)', border: '2px solid #5C1420', borderRadius: 22, padding: '12px 20px', textAlign: 'center', color: '#5C1420', fontSize: 22, fontWeight: 800 }}>
                Bon retour {membre.prenom} !
                <span style={{ fontSize: 17, fontWeight: 700, display: 'block', marginTop: 3 }}>🍪 {Math.min(10, membre.stamps ?? 0)}/10 tampons{membre.tier ? ` · statut ${membre.tier}` : ''}</span>
              </div>
            )}
            <button data-testid="welcome-club-btn" className="fredoka"
              onClick={() => { if (membre) { track('accepte', 'catalog_opened', 0, 'welcome'); setEtape(wcNextRef.current); } else { track('propose', 'club_login_clicked', 0, 'welcome'); setClubOpen(true); } }}
              style={{ background: '#5C1420', color: '#F6EEE3', border: 'none', borderRadius: 999, minHeight: 86, fontSize: 25, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, cursor: 'pointer' }}>
              <span style={{ fontSize: 28 }}>▦</span> {membre ? 'Commander avec mon compte' : "J'ai un compte Boufi Club"} <span style={{ fontSize: 26 }}>›</span>
            </button>
            <button data-testid="welcome-guest-btn" className="fredoka"
              onClick={() => { track('propose', 'guest_order_clicked', 0, 'welcome'); track('accepte', 'catalog_opened', 0, 'welcome'); setEtape(wcNextRef.current); }}
              style={{ background: '#FDF8F0', color: '#5C1420', border: '2px solid #E8D9C8', borderRadius: 999, minHeight: 86, fontSize: 25, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, cursor: 'pointer' }}>
              <span style={{ fontSize: 28 }}>🛍</span> Commander maintenant <span style={{ fontSize: 26 }}>›</span>
            </button>
          </div>
        </div>
      )}
      {etape === 'veille' && theme?.base !== 'editorial' && (
        <div className="bo-veille" data-testid="borne-veille" onClick={() => { refreshCatalog(); wcNextRef.current = 'carte'; if (cv2cfg?.actif) { setEtape('carte'); setCv2Open('welcome'); track('propose', 'welcome_screen_viewed', 0, 'welcome'); } else if (wc.actif) { wcSeenRef.current = true; setEtape('welcome'); track('propose', 'welcome_screen_viewed', 0, 'welcome'); } else setEtape('carte'); }}>
          <span className="bd-poste-tag" data-testid="borne-poste-tag">Borne {POSTE}</span>
          {(() => { const vd = data?.products?.find((p) => p.vedette && !p.epuise && !prodLock(p) && (p.video || p.img)); if (!vd) return null; return (
            <div className="bd-vv" data-testid="veille-vedette">
              {vd.video
                ? <video src={vd.video} loop autoPlay playsInline onCanPlay={(e) => { e.target.muted = true; e.target.play().catch(() => {}); }} ref={(el) => { if (el) { el.muted = true; if (el.paused) el.play().catch(() => {}); } }} />
                : <img src={optim(vd.img, 1080)} alt="" />}
              <div className="vv-txt">
                <span className="vv-badge fredoka">★ À LA UNE</span>
                <span className="vv-nom fredoka">{vd.nom}</span>
                <span className="vv-px fredoka">{eur(vd.px)}</span>
              </div>
            </div>
          ); })()}
          {(() => {
            const arr = slides || VEILLE_IMGS.map((u) => ({ type: 'img', url: u }));
            const n = arr.length;
            const cur = vIdx % n;
            const fen = n <= 3 ? arr.map((_, i) => i) : [(cur - 1 + n) % n, cur, (cur + 1) % n];
            return fen.map((i) => {
              const s = arr[i];
              const actif = i === cur;
              return s.type === 'video'
                ? <video key={s.url + i} src={s.url} className={actif ? 'on' : ''} loop muted playsInline preload={actif ? 'auto' : 'metadata'}
                    ref={(el) => { if (el) { el.muted = true; if (actif && el.paused) el.play().catch(() => {}); if (!actif && !el.paused) el.pause(); } }}
                    onError={() => setVIdx((x) => x + 1)} />
                : <img key={s.url + i} src={optim(s.url, 1080)} alt="" className={actif ? 'on' : ''} />;
            });
          })()}
          {isJourJ() && <Confettis />}
          <div className="ov">
            <span className="v-eclat e1">✦</span>
            <span className="v-eclat e2">✦</span>
            <span className="v-eclat e3">✦</span>
            <LaunchBanner />
            <HappyVeille info={happyNext} />
            <div className="v-slogan" data-testid="veille-slogan">{slogan}</div>
            <div className="v-roue">🎡 Membres du Club : la roue tourne après chaque commande</div>
            <button className="cmd" data-testid="veille-commander-btn">Commander →</button>
            <div className="v-touch">Touche l'écran pour commencer</div>
          </div>
        </div>
      )}

      {etape === 'carte' && (<>
        {menuVersion === 'lateral' && (
          <aside className="bd-sidenav" data-testid="borne-sidenav">
            {theme?.base === 'epure' && <img src="/logo-boufi-creme.png" alt="boufi" className="bd-side-logo" />}
            {universList.map((u) => {
              const img = (() => {
                if (u.id === 'pourvous') {
                  if (data.nav?.pourvous_icone) return data.nav.pourvous_icone;
                  const v = data.products.find((p) => p.vedette && (p.img_thumb || p.img)) || data.products.find((p) => p.best_seller && (p.img_thumb || p.img)); return v ? (v.img_thumb || v.img) : '';
                }
                if (u.icone) return u.icone;
                const hc = (data.heroes || {})[u.id];
                if (hc?.url && hc.type !== 'video') return hc.url;
                const p = data.products.find((x) => (u.cats || []).includes(x.cat_id) && (x.img_thumb || x.img));
                return p ? (p.img_thumb || p.img) : '';
              })();
              return (
                <button key={u.id} className={u.id === univObj.id ? 'on' : ''} onClick={() => goUnivers(u.id)} data-testid={`borne-side-${u.id}`}>
                  {theme?.base === 'epure' ? (() => {
                    const custom = u.id === 'pourvous' ? data.nav?.pourvous_picto : u.picto;
                    if (custom) return <img src={custom} alt="" className="bd-side-picto-img" loading="lazy" />;
                    const uid = (u.id || '') + (u.nom || '');
                    const Ico = u.id === 'pourvous' ? CupSoda
                      : /cookie/i.test(uid) ? Cookie
                      : /box|formule/i.test(uid) ? Package
                      : /gourmand/i.test(uid) ? IceCreamCone
                      : /boisson|drink/i.test(uid) ? Coffee
                      : /boutique|shop/i.test(uid) ? ShoppingBag : Star;
                    return <Ico className="bd-side-picto" strokeWidth={1.7} />;
                  })() : img ? <img src={img} alt="" loading="lazy" /> : <span className="ph fredoka">{u.nom[0].toUpperCase()}</span>}
                  <span>{theme?.base === 'epure' ? u.nom.replace(/[^\p{L}\p{N}&' ]/gu, '').trim() : u.nom}</span>
                </button>
              );
            })}
            {false && ckprem && (
              <button className="bd-side-club" onClick={() => setClubOpen(true)} data-testid="borne-side-club">
                <span className="scb-star">★</span>
                <span className="scb-t">BOUFI CLUB</span>
                <span className="scb-s">Des avantages toute l&apos;année&nbsp;!</span>
              </button>
            )}
          </aside>
        )}
        {data.happy && (
          <div className="bd-happy" data-testid="borne-happy-banner">
            🎉 {data.happy.nom} · −{Math.round(data.happy.pct)}% jusqu'à {data.happy.fin} !
          </div>
        )}
        {allergies.length > 0 && (
          <div className="bd-allergbar" onClick={() => setAllergOpen(true)} data-testid="borne-allerg-bar">
            ⚠️ Filtre allergènes : {allergies.map((a) => ALLERG_LABEL[a] || a).join(', ')} — produits concernés bloqués · Traces possibles (atelier commun) · <u>Modifier</u>
          </div>
        )}
        <div className="bd-slidewrap">
        <div className="bd-scroll bd-home" ref={scrollRef} data-testid="borne-home">
          {ckprem && univObj.id === 'cookies' && (() => {
            const catCk = data.categories.find((c) => c.id === 'cookies');
            return (
              <header className="ckp-hero" data-testid="borne-ckp-hero">
                <div className="ckp-hero-txt">
                  <span className="ckp-sur">Nos cookies artisanaux</span>
                  <h1 className="ckp-titre fredoka" data-testid="borne-ckp-titre">{(catCk?.nom || 'Les Cookies').toUpperCase()}</h1>
                  <p className="ckp-sous">Moelleux à l&apos;intérieur, gourmands à l&apos;extrême.</p>
                </div>
                <div className="ckp-hero-img" aria-hidden="true">
                  <img src={(((data.heroes || {}).cookies || {}).url && ((data.heroes || {}).cookies || {}).type !== 'video')
                    ? (data.heroes || {}).cookies.url : '/borne/hero-cookie.jpg'} alt="" fetchPriority="high" decoding="async" />
                  <span className="ckp-manuscrit">Du goût à<br />chaque morceau<b>♥</b></span>
                </div>
              </header>
            );
          })()}
          {univObj.id !== 'pourvous' && !(ckprem && univObj.id === 'cookies') && (() => {
            const pool = data.products.filter((p) => univObj.cats.includes(p.cat_id) && p.img && !p.epuise && !prodLock(p) && p.actif !== false);
            if (!pool.length) return null;
            if (!ckprem && theme?.base === 'epure' && theme?.tokens?.masquer_heros) return null;
            const h = pool.find((p) => p.vedette) || [...pool].sort((a, b) => (ventesJour[b.nom] || 0) - (ventesJour[a.nom] || 0))[0];
            const hc = (data.heroes || {})[univObj.id];
            if (ckprem) {
              const SOUS = { boxformules: 'Plus on partage, plus c\'est bon.', gourmandises: 'Le plaisir, jusqu\'au bout de la gourmandise.', boissons: 'Fraîches ou chaudes, toujours gourmandes.' };
              const himg = (hc?.url && hc.type !== 'video') ? hc.url : h.img;
              return (
                <header className="ckp-hero" data-testid="borne-ckp-hero">
                  <div className="ckp-hero-txt">
                    <span className="ckp-sur">La carte Boufi</span>
                    <h1 className="ckp-titre fredoka" data-testid="borne-ckp-titre">{univObj.nom.replace(/[^\p{L}\p{N}&'’ ]/gu, '').trim().toUpperCase()}</h1>
                    <p className="ckp-sous">{SOUS[univObj.id] || 'Gourmand, à chaque instant.'}</p>
                  </div>
                  <button className="ckp-hero-img photo" data-testid="borne-ckp-hero-photo"
                    onClick={() => (h.moments_box ? setMomentInfo(hc?.url || h.img) : setDetail({ it: h, choix: {}, multi: {}, q: 1 }))}>
                    <img src={optim(himg, KC ? 1080 : 800)} alt={h.nom} fetchPriority="high" decoding="async" />
                    <span className="ckp-hero-tag">{h.nom} · {h.slots ? 'à partir de ' : ''}{eur(h.px)}</span>
                  </button>
                </header>
              );
            }
            return (
              <button key={univObj.id} className="bd-cathero" data-testid="borne-cathero"
                onClick={() => (h.moments_box ? setMomentInfo(hc?.url || h.img) : setDetail({ it: h, choix: {}, multi: {}, q: 1 }))}>
                {hc?.url && hc.type === 'video'
                  ? <video src={hc.url} autoPlay loop playsInline onCanPlay={(e) => { e.target.muted = true; e.target.play().catch(() => {}); }} ref={(el) => { if (el) { el.muted = true; if (el.paused) el.play().catch(() => {}); } }} />
                  : <img src={optim(hc?.url || h.img, KC ? 1080 : 800)} alt={h.nom} fetchPriority="high" decoding="async" />}
                <span className="fade" aria-hidden="true" />
                <span className="txt">
                  <span className="nm fredoka" data-testid="borne-cathero-nom">{h.nom}</span>
                  <span className="px">{h.slots ? 'à partir de ' : ''}{eur(h.px)}</span>
                  <span className="hint">⌄ fais glisser pour découvrir</span>
                </span>
              </button>
            );
          })()}
          {univObj.id === 'pourvous' && (() => { const v = data.products.find((p) => p.vedette && !p.epuise && !prodLock(p) && (p.img || p.video)); if (!v) return null;
            const vc = data.categories.find((c) => c.id === v.cat_id);
            const tag = v.badge || (v.nom.toUpperCase().includes('BAC TO BOUFI') ? "Jeu BAC TO BOUFI · Jusqu'à 300 € à gagner" : (vc?.edition || 'À découvrir'));
            return (
            <button className="bd-hero2" onClick={() => { if (/bueno/i.test(v.nom)) { bannerAction({ action: 'bueno' }); return; } setDetail({ it: v, choix: {}, multi: {}, q: 1 }); }} data-testid="borne-vedette">
              {v.video
                ? <video src={v.video} autoPlay loop playsInline onCanPlay={(e) => { e.target.muted = true; e.target.play().catch(() => {}); }} ref={(el) => { if (el) { el.muted = true; if (el.paused) el.play().catch(() => {}); } }} />
                : <img src={optim(v.img, 1080)} alt={v.nom} />}
              <span className="ov">
                <span className="ti">{v.nom}</span>
                <span className="sub">{tag} · touche pour découvrir</span>
              </span>
            </button>
          ); })()}
          <div className="bd-sticky" ref={stickyRef}>
            <nav className="bd-tabs1" data-testid="borne-univers">
              {universList.map((u) => (
                <button key={u.id} aria-pressed={u.id === univObj.id} data-testid={`borne-univ-${u.id}`}
                  onClick={() => goUnivers(u.id)}>{u.nom}</button>
              ))}
            </nav>
            <div className="bd-swipedots" data-testid="borne-swipe-dots">
              {universList.map((u) => (
                <button key={u.id} className={`dot${u.id === univObj.id ? ' on' : ''}`} aria-label={u.nom} data-testid={`borne-dot-${u.id}`}
                  onClick={() => goUnivers(u.id)} />
              ))}
            </div>
            <nav className="bd-tabs2" ref={tabs2Ref} data-testid="borne-sections" style={theme?.base === 'epure' ? { display: 'none' } : undefined}>
              {sections.map((s) => (
                <button key={s.id} aria-pressed={s.id === secActive} data-sec={s.id} data-testid={`borne-sec-tab-${s.id}`}
                  onClick={() => { const el = secRefs.current[s.id]; if (el) scrollRef.current?.scrollTo({ top: el.offsetTop - 132, behavior: 'smooth' }); }}>{s.nom}</button>
              ))}
            </nav>
          </div>
          {univObj.id === 'pourvous' && theme?.base === 'epure' && herosActifs.length > 0 ? (() => {
            const he = herosActifs[heroIdx % herosActifs.length];
            const hp = data.products.find((x) => x.nom === he.produit);
            const himg = he.img || (hp && (hp.img || hp.img_thumb)) || '';
            const heClick = () => {
              if (he.cible === 'edition') { bannerAction({ action: 'edition' }); return; }
              if (/bueno/i.test(`${he.titre || ''} ${he.sous_titre || ''} ${he.produit || ''}`)) { bannerAction({ action: 'bueno' }); return; }
              if (he.cible) { bannerAction({ action: 'cat:' + he.cible }); return; }
              if (hp) setDetail({ it: hp, choix: {}, multi: {}, q: 1 });
            };
            if (he.plein && himg) return (
              <button key={heroIdx % herosActifs.length} className="bd-hero-epure he-plein" data-testid="borne-hero-epure"
                style={{ backgroundImage: `url(${himg})` }} onClick={heClick}>
                <span className="he-txt">
                  <span className="he-titre fredoka">{he.titre}</span>
                  {he.sous_titre && <span className="he-sous">{he.sous_titre}</span>}
                  <span className="he-cta">{he.cta}</span>
                </span>
              </button>
            );
            return (
              <button key={heroIdx % herosActifs.length} className="bd-hero-epure" data-testid="borne-hero-epure"
                onClick={heClick}>
                <span className="he-txt">
                  <span className="he-titre fredoka">{he.titre}</span>
                  {he.sous_titre && <span className="he-sous">{he.sous_titre}</span>}
                  <span className="he-cta">{he.cta}</span>
                </span>
                {himg && <img src={optim(himg, 800)} alt="" />}
              </button>
            );
          })() : univObj.id === 'pourvous' && banners.length > 0 && (
            <div style={{ padding: '12px 16px 0' }} data-testid="borne-bancar-top">
              <BannerCarousel banners={banners.slice(0, 1)} btb={btb} onAction={bannerAction} />
            </div>
          )}
          {univObj.id === 'pourvous' && banners.length > 0 && theme?.base === 'epure' && herosActifs.length > 0 && (
            <div style={{ padding: '12px 16px 0' }} data-testid="borne-bancar-top2">
              <BannerCarousel banners={banners.slice(0, 1)} btb={btb} onAction={bannerAction} />
            </div>
          )}
          {sections.map((s) => (
            <section key={s.id} ref={(el) => { secRefs.current[s.id] = el; }} data-secid={s.id} className="bd-sec" data-testid={`borne-sec-${s.id}`}>
              <h3 className="bd-sec-title">
                {theme?.base === 'epure' ? s.nom.replace(/[^\p{L}\p{N}&'’ ]/gu, '').trim() : s.nom}
                {s.sous_titre
                  ? <em className="bd-sec-note" data-testid={`borne-sec-note-${s.id}`}>{s.sous_titre}</em>
                  : theme?.base === 'epure' && ['soir', 'gouter', 'matin'].includes(s.id) && s.prods.some((p) => p.cat_id === 'hotcookie' && !p.epuise) && (
                    <em className="bd-sec-note">Cuit à la commande · prêt en 8 min</em>
                  )}
              </h3>
              {s.custom === 'drop' && btb && (
                <button className="bd-btb-photo" onClick={() => setBtbOpen(true)} data-testid="borne-btb-banner">
                  <img src="/promos/bac_to_boufi_banner.webp" alt="Bac to Boufi — édition limitée" />
                  <span className="bd-bancar-live">
                    🔥 <b data-testid="borne-btb-restantes">{btb.total.restantes}</b>/{btb.total.capacite} restantes
                    {btb.daily?.restantes > 0 && <> · plus que {btb.daily.restantes} aujourd'hui</>}
                  </span>
                </button>
              )}
              {s.custom === 'moments' && <BannerCarousel banners={banners.slice(1)} btb={btb} onAction={bannerAction} />}
              {s.id === 'cookies' && univObj.id !== 'pourvous' && s.prods.some((p) => p.sous_cat) && (() => {
                const duoP = data.products.find((p) => p.nom === 'Duo Cookies' && p.actif !== false && !p.epuise);
                const box4P = data.products.find((p) => p.choix_groupe === 'box_cookies' && p.choix_n === 4 && p.actif !== false && !p.epuise && !p.moments_box);
                const pxCl = s.prods.filter((p) => p.sous_cat === 'classique').map((p) => p.px);
                const pxCk = pxCl.length ? Math.min(...pxCl) : null;
                return (<>
                  {(pxCk != null || duoP || box4P) && (
                    <div className="bd-ckoffres" data-testid="borne-cookie-offres">
                      {pxCk != null && (
                        <div className="ck-off" data-testid="borne-ckoffre-un">
                          <span className="ck-ico"><Cookie strokeWidth={1.6} /></span>
                          <span className="ck-in"><small>1 cookie</small><b>{eur(pxCk)}</b><span className="ck-eco">à l&apos;unité</span></span>
                        </div>
                      )}
                      {duoP && (
                        <button className="ck-off clic" onClick={() => setDetail({ it: duoP, choix: {}, multi: {}, q: 1 })} data-testid="borne-ckoffre-duo">
                          <span className="ck-ico duo"><Cookie strokeWidth={1.6} /><Cookie strokeWidth={1.6} /></span>
                          <span className="ck-in">
                            <small>Duo</small>
                            <b>{eur(duoP.px)}{showEco(duoP) && <s>{eur(pxBarreAff(duoP))}</s>}</b>
                            <span className="ck-eco or" data-testid="borne-ckoffre-duo-unite">{showEco(duoP) ? `économise ${ecoEur(ecoReelle(duoP))}` : `soit ${eur(duoP.px / 2)} le cookie`}</span>
                          </span>
                        </button>
                      )}
                      {box4P && (
                        <button className="ck-off clic accent" onClick={() => setDetail({ it: box4P, choix: {}, multi: {}, q: 1 })} data-testid="borne-ckoffre-box4">
                          <span className="ck-ico"><Package strokeWidth={1.6} /></span>
                          <span className="ck-in">
                            <small>Box de 4</small>
                            <b>{eur(box4P.px)}{showEco(box4P) && <s>{eur(pxBarreAff(box4P))}</s>}</b>
                            <span className="ck-eco or" data-testid="borne-ckoffre-box4-unite">{showEco(box4P) ? `économise ${ecoEur(ecoReelle(box4P))}` : `soit ${eur(box4P.px / 4)} le cookie`}</span>
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                  {duoP && (() => {
                    const duosCfg = ((data.categories.find((c) => c.id === 'cookies') || {}).duos || []).filter((d) => d.actif !== false);
                    const suppDuo = (cks2) => cks2.reduce((sum, n) => sum + (((G.duo_cookies || G.box_cookies)?.supp || {})[BOX_MAP[n] || n] || 0), 0);
                    const imgOf = (n) => { const p2 = data.products.find((x) => x.nom === n); return p2 ? (p2.img_thumb || p2.img) : null; };
                    if (!duosCfg.length) return null;
                    return (
                      <div className="bd-ckduos" data-testid="borne-duos-zone">
                        <div className="ckduo-row">
                        {duosCfg.map((d, i) => {
                          const px = Math.round((duoP.px + suppDuo(d.cookies)) * 100) / 100;
                          const gD = G.duo_cookies || G.box_cookies;
                          const duoEp = d.cookies.some((n) => (gD?.epuise || []).includes(BOX_MAP[n] || n));
                          const nomAff = /best[\s-]?seller/i.test(d.nom) ? "Le choix de l'équipe" : d.nom;
                          const badgeAff = d.badge && /best[\s-]?seller|n\s*°\s*1/i.test(d.badge) ? '♥ ÉQUIPE' : d.badge;
                          return (
                            <div className={`ckduo${duoEp ? ' epuise' : ''}`} key={i} data-testid={`borne-duo-${i}`}>
                              {duoEp && <span className="bd-epz card" data-testid={`borne-duo-epuise-${i}`}>ÉPUISÉ</span>}
                              <span className="ckduo-imgs">{d.cookies.map((n, k) => imgOf(n) ? <img key={k} src={optim(imgOf(n), 320)} alt="" loading="lazy" /> : null)}</span>
                              <span className="ckduo-in">
                                <span className="ckduo-nom">{nomAff}{badgeAff ? <em className="ckduo-badge">{badgeAff}</em> : null}</span>
                                <small>{d.cookies.join(' + ')}</small>
                              </span>
                              <span className="ckduo-bas">
                                <span className="ckduo-px fredoka">{eur(px)}</span>
                                <button className="ckduo-add" disabled={duoEp} data-testid={`borne-duo-add-${i}`}
                                  onClick={() => { if (duoEp) return; ajouter({ nom: duoP.nom, px, opts: d.cookies.map((n) => BOX_MAP[n] || n) }); track('accepte', duoP.nom, px, 'duo-precompose'); }}>Ajouter</button>
                              </span>
                            </div>
                          );
                        })}
                        <div className="ckduo compose" data-testid="borne-duo-compose">
                          <span className="ckduo-imgs">{(duoP.img_thumb || duoP.img) ? <img src={optim(duoP.img_thumb || duoP.img, 320)} alt="" loading="lazy" /> : null}</span>
                          <span className="ckduo-in"><span className="ckduo-nom">Compose ton Duo</span><small>2 cookies au choix</small></span>
                          <span className="ckduo-bas">
                            <span className="ckduo-px fredoka">{eur(duoP.px)}</span>
                            <button className="ckduo-add gho" onClick={() => setDetail({ it: duoP, choix: {}, multi: {}, q: 1 })} data-testid="borne-duo-compose-btn">Composer</button>
                          </span>
                        </div>
                        </div>
                      </div>
                    );
                  })()}
                </>);
              })()}
              {s.prods.length > 0 && (() => {
                const tri1 = s.manuel ? [...s.prods]
                  : univObj.id === 'pourvous'
                  ? [...s.prods].sort((a, b) => (ventesJour[b.nom] || 0) - (ventesJour[a.nom] || 0))
                  : [...s.prods].sort((a, b) => (((a.ordre ?? 9999) - (b.ordre ?? 9999)) || ((ventesJour[b.nom] || 0) - (ventesJour[a.nom] || 0))));
                const tri0 = [...tri1].sort((a, b) => (a.epuise ? 1 : 0) - (b.epuise ? 1 : 0));
                const isCk = s.id === 'cookies' && univObj.id !== 'pourvous' && s.prods.some((p) => p.sous_cat);
                const tri = isCk ? filtreCookies(tri0) : tri0;
                const vrOf = (p) => (['featured', 'compact'].includes(p.display_variant) ? p.display_variant : (p.display_variant === 'standard' ? 'standard' : (p.auto_featured ? 'featured' : 'standard')));
                const carte = (it, vr, pleine = false) => {
                  const bloque = allergies.length > 0 && (it.allergenes || []).some((a) => allergies.includes(a));
                  const bloqueLbl = bloque ? (it.allergenes || []).filter((a) => allergies.includes(a)).map((a) => ALLERG_LABEL[a] || a).join(', ') : '';
                  return (
                  <button key={s.id + it.nom} className={`bd-card vr-${vr}${pleine ? ' pleine' : ''}${it.epuise ? ' epuise' : ''}${bloque ? ' allerg' : ''}`} data-testid={`borne-prod-${s.id}-${it.nom.replace(/\s+/g, '-')}`}
                    onClick={() => {
                      if (bloque) { setAllergOpen(true); return; }
                      if (it.epuise) {
                        const alt = data.products.filter((p) => p.cat_id === it.cat_id && !p.epuise && p.nom !== it.nom && !prodLock(p) && p.actif !== false)
                          .sort((a, b) => ((b.best_seller ? 1 : 0) - (a.best_seller ? 1 : 0)) || (Math.abs(a.px - it.px) - Math.abs(b.px - it.px)))[0];
                        setRupture({ prod: it, alt });
                        return;
                      }
                      if (s.club && prodLock(it)) { setClubOpen(true); return; }
                      setDetail({ it, choix: {}, multi: {}, q: 1 });
                    }}>
                    {bloque && <span className="bd-allergz" data-testid={`borne-allerg-bloc-${it.nom.replace(/\s+/g, '-')}`}>⚠ Contient : {bloqueLbl}</span>}
                    {it.epuise && <span className="bd-epz card" data-testid={`borne-epuise-${it.nom.replace(/\s+/g, '-')}`}>ÉPUISÉ</span>}
                    {it.epuise && (() => {
                      const r = (data.retours || {})[it.nom] || (data.retours || {})[it.nom.replace(/^Cookie /, '')];
                      return r ? <span className="bd-retour" data-testid={`borne-retour-${it.nom.replace(/\s+/g, '-')}`}>{r.heure ? `De retour à ${r.heure}` : 'Nouvelle fournée bientôt'}</span> : null;
                    })()}
                    {s.club && prodLock(it) && <span className="bd-lock" data-testid={`borne-lock-${it.nom.replace(/\s+/g, '-')}`}>🔒 {lockLabel(it.club_tier || 'bronze')}</span>}
                    <span className="imwrap">
                      {(it.img_thumb || it.img)
                        ? <img className="im" src={optim(it.img_thumb || it.img)} alt={it.nom} loading="lazy" decoding="async" draggable={false} onError={(e) => { e.currentTarget.outerHTML = `<span class="im ph bfond bfond-${(it.nom.length % 3) + 1}"></span>`; }} />
                        : it.video
                        ? <video className="im" src={it.video} preload="metadata" loop muted playsInline onClick={(e) => e.currentTarget.play().catch(() => {})} />
                        : <span className={`im ph bfond bfond-${(it.nom.length % 3) + 1}`} />}
                      {(() => {
                        const manuelOk = it.badge && !/best[\s-]?seller|n\s*°\s*1|nº\s*1|num[ée]ro\s*1/i.test(it.badge)
                          && !(s.id === 'incontournables' && /édition\s*limitée/i.test(it.badge)) && !(s.id === 'nouveautes' && /nouveau/i.test(it.badge));
                        const fb = it.epuise ? null
                          : ((data.fraicheur || {})[it.nom] || (data.fraicheur || {})[it.nom.replace(/^Cookie /, '')]) ? { cls: ' fournee', el: <>Nouvelle<br />fournée</> }
                          : manuelOk ? { cls: '', el: it.badge }
                          : (data.num1 || {})[it.cat_id] === it.nom ? { cls: ' n1', el: <>★ N°1</> }
                          : it.auto_nouveau ? { cls: '', el: 'Nouveau' } : null;
                        return fb ? <span className={`bd-fl${fb.cls}`} data-testid={`borne-fl-${it.nom.replace(/\s+/g, '-')}`}>{fb.el}</span> : null;
                      })()}
                      {it.px_avant != null && it.px_avant > it.px && (() => {
                        const hh = data.happy && (!(data.happy.cats || []).length || data.happy.cats.includes(it.cat_id));
                        return (
                          <span className={`bd-choc${hh ? ' hh' : ''}`} data-testid={`borne-choc-${it.nom.replace(/\s+/g, '-')}`}>
                            {hh
                              ? <>HAPPY HOUR<b>−{Math.round((1 - it.px / it.px_avant) * 100)}%</b></>
                              : <>−{Math.round((1 - it.px / it.px_avant) * 100)}%</>}
                          </span>
                        );
                      })()}
                      {(it.px_avant == null || it.px_avant <= it.px) && showEco(it) && !it.badge && (
                        <span className="bd-choc eco" data-testid={`borne-eco-${it.nom.replace(/\s+/g, '-')}`}>ÉCO {ecoEur(ecoReelle(it))}</span>
                      )}
                      {it.restant != null && it.restant > 0 && it.restant <= 5
                        && <span className="bd-hotbadge rare" data-testid={`borne-rare-${s.id}-${it.nom.replace(/\s+/g, '-')}`}>⏳ Plus que {it.restant} aujourd'hui</span>}
                      {it.attente > 0 && <span className="bd-wait" data-testid={`borne-wait-${it.nom.replace(/\s+/g, '-')}`}>🔥 CUIT À LA COMMANDE · PRÊT EN {it.attente} MIN</span>}
                      {btb && /bac to boufi/i.test(it.nom) && (
                        <span className={`bd-btbbadge${(btb.total.restantes <= 0 || (btb.daily && btb.daily.restantes <= 0)) ? ' out' : ''}`} data-testid="borne-btb-card-badge">
                          {btb.total.restantes <= 0 ? '⛔ Série épuisée' : (btb.daily && btb.daily.restantes <= 0) ? '⛔ Quota du jour épuisé' : `🔥 Plus que ${btb.daily ? btb.daily.restantes : btb.total.restantes} aujourd'hui`}
                        </span>
                      )}
                      {!it.epuise && pastilles.filter((pa) => pa.produits.includes(it.nom)).slice(0, 1).map((pa) => (
                        <img key={pa.pid} className={`bd-pastille pp-${pa.pos}`} src={optim(pa.img, 320)} alt="Offre"
                          style={{ width: pa.taille }} data-testid={`borne-pastille-${it.nom.replace(/\s+/g, '-')}`} />
                      ))}
                    </span>
                    <span className="in">
                      {(it.compose || it.choix_n) && <span className="bd-badge">{it.choix_n ? 'À composer' : it.compose.length > 1 ? 'À composer' : ({ lait: 'Lait au choix', cookie: 'À composer', sauce: 'Sauce au choix', topping: 'Topping au choix', sirop: 'Sirop au choix' }[it.compose[0]] || 'Au choix')}</span>}
                      {it.slots && !it.compose && !it.choix_n && <span className="bd-badge">À composer</span>}
                      {!it.epuise && !it.badge && (data.num1 || {})[it.cat_id] !== it.nom && !(data.fraicheur || {})[it.nom] && hints?.marge_stars?.includes(it.nom) && <span className="bd-badge bd-chef" data-testid={`borne-chef-${it.nom.replace(/\s+/g, '-')}`}>💥 Bon plan</span>}
                      <span className="nm">{it.nom}</span>
                      <span className="row"><span className="pr">{it.px_avant != null ? <s className="bd-avant">{eur(it.px_avant)}</s> : showEco(it) ? <s className="bd-avant">{eur(pxBarreAff(it))}</s> : null}{(it.slots || it.tailles?.length) ? <small className="apd">à partir de </small> : null}{eur(it.px)}</span>
                        {(it.compose || it.choix_n || it.slots || (s.club && prodLock(it)))
                          ? <span className="go act">Composer</span>
                          : <span className="go act" role="button" data-testid={`borne-plus-${it.nom.replace(/\s+/g, '-')}`}
                              onClick={(e) => { e.stopPropagation(); if (bloque) { setAllergOpen(true); return; } if (!it.epuise) ajouterDirect(it); }}>Ajouter</span>}
                      </span>
                    </span>
                  </button>
                  );
                };
                if (univObj.id === 'pourvous') {
                  if (s.id === 'incontournables') return <div className="bd-grid2" data-testid="borne-grid-incontournables">{tri.map((it, i) => carte(it, 'standard', tri.length % 2 === 1 && i === tri.length - 1))}</div>;
                  return <div className="bd-carousel" data-testid={`borne-compact-${s.id}`}>{tri.map((it) => carte(it, 'compact'))}</div>;
                }
                const epure = theme?.base === 'epure';
                const forceFeat = s.id === 'formules' && !epure;
                const feats = epure ? [] : (forceFeat ? tri : tri.filter((p) => vrOf(p) === 'featured'));
                const stds = epure ? tri : (forceFeat ? [] : tri.filter((p) => vrOf(p) === 'standard'));
                const comps = epure ? [] : (forceFeat ? [] : tri.filter((p) => vrOf(p) === 'compact'));
                return (<>
                  {feats.map((it) => carte(it, 'featured'))}
                  {stds.length > 0 && <div className="bd-grid2" style={s.colonnes ? { gridTemplateColumns: `repeat(${s.colonnes},1fr)` } : undefined} data-testid={`borne-grid-${s.id}`}>{stds.map((it, i) => carte(it, 'standard', !s.colonnes && stds.length % 2 === 1 && i === stds.length - 1))}</div>}
                  {comps.length > 0 && <div className="bd-carousel" data-testid={`borne-compact-${s.id}`}>{comps.map((it) => carte(it, 'compact'))}</div>}
                </>);
              })()}
            </section>
          ))}
          {univObj.id === 'pourvous' && ultra?.pvVoir !== false && universList.length > 1 && (
            <button className="bd-cta" style={{ position: 'static', display: 'block', margin: '14px auto 4px', width: 'min(430px, calc(100% - 44px))' }}
              onClick={() => goUnivers(universList[1].id, 1)} data-testid="borne-voir-carte-btn">
              Voir toute la carte
            </button>
          )}
          <div style={{ height: 120 }} />
        </div>
        </div>
        {momentInfo && (
          <div className="bd-fpick" onClick={() => setMomentInfo(false)} data-testid="borne-moments-info-modal">
            <div className="fpick-card bd-momentinfo" style={{ maxHeight: '90vh', overflowY: 'auto', padding: 14 }} onClick={(e) => e.stopPropagation()}>
              <button className="bd-back" style={{ position: 'absolute', top: 22, right: 22, zIndex: 2 }} onClick={() => setMomentInfo(false)} data-testid="borne-moments-info-fermer">✕</button>
              {typeof momentInfo === 'string' && momentInfo && (
                <img src={momentInfo} alt="" style={{ width: '100%', height: 300, objectFit: 'cover', borderRadius: 20, display: 'block' }} />
              )}
              <div style={{ padding: '18px 12px 10px' }}>
                <h3 className="fredoka" style={{ fontSize: 28, margin: '0 0 10px' }}>🎁 La Box Boufi Moments</h3>
                <p style={{ fontSize: 15.5, opacity: .75, margin: '0 0 8px', lineHeight: 1.5 }}>
                  Une box de cookies à offrir, avec un <b>message surprise caché</b> à l'intérieur.
                </p>
                <p style={{ fontSize: 15.5, opacity: .75, margin: '0 0 18px', lineHeight: 1.5 }}>
                  La personne qui la reçoit scanne le QR de la box et découvre ton mot, ta photo ou ton vocal. Elle peut même te répondre. 🤍
                </p>
                {[['1', '🍪 Choisis ta box', 'Ton événement, ta taille, tes cookies préférés — directement sur la borne.'],
                  ['2', '💌 Enregistre ta surprise', 'Après le paiement, tu reçois un SMS pour ajouter ton message : texte, photo, vidéo ou vocal.'],
                  ['3', '✨ La magie opère', 'Ton destinataire scanne le QR de la box et découvre ta surprise.']].map(([n, t, d]) => (
                  <div key={n} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 13 }}>
                    <span className="num-step" style={{ flex: 'none', width: 34, height: 34, borderRadius: 999, background: '#F15623', color: '#FFF7EC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Fredoka', fontWeight: 600 }}>{n}</span>
                    <div>
                      <div className="fredoka" style={{ fontSize: 16.5, fontWeight: 600 }}>{t}</div>
                      <div style={{ fontSize: 13.5, opacity: .65, lineHeight: 1.4 }}>{d}</div>
                    </div>
                  </div>
                ))}
                <p className="fredoka" style={{ fontSize: 17, fontWeight: 600, margin: '14px 0 12px' }}>Ça te tente ?</p>
                <button className="bd-cta" style={{ width: '100%' }} onClick={() => setMomentInfo(false)} data-testid="borne-moments-info-ok">Je choisis ma box 🎁</button>
              </div>
            </div>
          </div>
        )}
        {membre && (
          <div className="bd-fidbar" data-testid="borne-fidbar">
            {membre.anniv ? `🎂 Joyeux anniversaire ! ${membre.anniv} aujourd'hui · ` : ''}
            {(membre.stamps ?? 0) >= 10
              ? '🎁 Cookie offert disponible — ouvre ton panier !'
              : `⭐ ${membre.stamps ?? 0}/10 tampons · encore ${10 - (membre.stamps ?? 0)} → cookie offert`}
            {teaserUnlocks.length > 0 && ` · encore ${teaserRestant} → 🔓 ${teaserUnlocks[0]}`}
          </div>
        )}
        {aiReco && !detail && !showPanier && !clubOpen && !upsell && (
          <div className="bd-aireco" data-testid="borne-ai-reco">
            {aiReco.reco.img ? <img src={optim(aiReco.reco.img, 320)} alt="" /> : <span className="ic">💡</span>}
            <div className="txt"><b>{aiReco.reco.nom}</b><small>{aiReco.reco.raison}</small></div>
            <button className="add" onClick={() => aiAjouter(aiReco, () => setAiReco(null))} data-testid="borne-ai-reco-ajouter">+ {eur(Math.max(0, suppReel(aiReco.reco)))}</button>
            <button className="close" onClick={() => { aiFeedback(aiReco.decision_id, 'refuse'); setAiReco(null); }} data-testid="borne-ai-reco-fermer" aria-label="fermer">✕</button>
          </div>
        )}
        {palierUi && !detail && !showPanier && !clubOpen && !aiReco && !comboHint && !upsell && (
          <div className="bd-palier" data-testid="borne-palier">
            🎁 Plus que {4 - palierUi.nC} cookie{4 - palierUi.nC > 1 ? 's' : ''} pour la {palierUi.nom} <b>{eur(palierUi.px)}</b>
            <span className="pal-track"><i style={{ width: `${(palierUi.nC / 4) * 100}%` }} /></span>
          </div>
        )}
        {comboHint && !detail && !showPanier && !aiReco && !upsell && !clubOpen && (
          <div className="bd-comboglide" data-testid="borne-combo-glide">
            {comboHint.img ? <img src={optim(comboHint.img, 320)} alt="" /> : <span className="ic">🥤</span>}
            <div className="txt"><b>+ {comboHint.nom} ?</b><small>Le duo préféré des clients</small></div>
            <button type="button" className="add" onClick={() => { ajouter({ nom: comboHint.nom, px: comboHint.px, opts: [] }); track('accepte', comboHint.nom, comboHint.px, 'anim-combo'); setComboHint(null); }} data-testid="borne-combo-add">+ {eur(Math.max(0, suppReel(comboHint)))}</button>
            <button type="button" className="x" onClick={() => { track('refuse', comboHint.nom, 0, 'anim-combo'); setComboHint(null); }} data-testid="borne-combo-fermer">✕</button>
          </div>
        )}
        <div className="bd-bar">
          <button className={`bd-pill${bump ? ' bump' : ''}`} onClick={() => panier.length && ouvrirPanier()} data-testid="borne-panier-pill">
            <Sac /><span>{nbArt}{nbArt > 0 ? ` · ${eur(totalAff)}` : ''}</span>
          </button>
          {panier.length > 0 && (
            <button className="bd-cta" onClick={valider} data-testid="borne-valider-btn">
              Valider · {eur(totalAff)}
            </button>
          )}
        </div>
      </>)}

      {detail && simpleB && (
        <div className="bd-detail bd-fb" data-testid="borne-detail">
          <div className="fb-top">
            <div className="fb-nav">
              <button className="bd-back" onClick={() => { if (detail.editIdx != null) setShowPanier(true); setDetail(null); setInside(false); }} data-testid="borne-detail-fermer">‹</button>
              <span className="fb-retour">Retour</span>
            </div>
            <div className="fb-logo"><img src="/logo-boufi-brun.png" alt="boufi" /><small>COOKIES · GOOD MOOD</small></div>
            <div className="fb-tagline">Des cookies qui font du bien</div>
          </div>
          <div className="fb-titres">
            <h2 className="fb-nom" data-testid="borne-fb-nom">{detail.it.nom}</h2>
            {detail.it.de && <p className="fb-promesse" data-testid="borne-fb-promesse">{detail.it.de}</p>}
          </div>
          <div className="fb-photo" data-testid="borne-fb-photo">
            {detail.it.cat_id === 'cookies' && <span className="fb-note" aria-hidden="true">Le cookie qui fait fondre tout le monde ♡</span>}
            {((data.fraicheur || {})[detail.it.nom] || (data.num1 || {})[detail.it.cat_id] === detail.it.nom) && (
              <span className="fb-sticker" data-testid="borne-fb-sticker">{(data.fraicheur || {})[detail.it.nom] ? <><small>NOUVELLE</small><b>FOUR<br />NÉE</b><i>♡</i></> : <><small>★ N°1</small><b>CETTE<br />SEMAINE</b><i>♡</i></>}</span>
            )}
            {detail.it.video
              ? <video src={detail.it.video} autoPlay loop playsInline muted />
              : detail.it.img
              ? <img src={optim(detail.it.img, 800)} alt={detail.it.nom} />
              : <div className="fb-noimg fredoka">{detail.it.nom[0]}</div>}
          </div>
          {(detail.it.attente > 0 || (detail.it.infos || []).length > 0) && (
            <div className="fb-args" data-testid="borne-fb-args">
              {(detail.it.attente > 0 ? [`Cuit à la commande · ${detail.it.attente} min`] : []).concat((detail.it.infos || [])).slice(0, 3).map((inf, i) => (
                <span key={inf} className="fb-arg"><i>{['❖', '♡', '✦'][i % 3]}</i>{inf}</span>
              ))}
            </div>
          )}
          <div className="fb-bar">
            <div className="bd-pill bd-qty fb-qty">
              <button onClick={() => setDetail((d) => ({ ...d, q: Math.max(1, d.q - 1) }))} data-testid="borne-detail-moins">−</button>
              <b>{detail.q}</b>
              <button onClick={() => setDetail((d) => ({ ...d, q: d.q + 1 }))} data-testid="borne-detail-plus">+</button>
            </div>
            <button className="bd-cta fb-cta" data-testid="borne-detail-ajouter" onClick={() => ajouterDetail(null)}>
              {detail.editIdx != null ? 'OK' : 'AJOUTER'} • {eur(detail.it.px * detail.q)}
              {showEco(detail.it) && <s className="fbarre" data-testid="borne-detail-eco-barre">{eur(pxBarreAff(detail.it) * detail.q)}</s>}
              <span className="fb-fleche">→</span>
            </button>
          </div>
          <button className="fb-ingr" onClick={() => { setInside(true); logEv('product_detail_ingredients_opened', { nom: detail.it.nom, version: 'B' }); }} data-testid="borne-fb-ingredients">Voir les ingrédients ˅</button>
        </div>
      )}
      {detail && !simpleB && (
        <div className="bd-detail" data-testid="borne-detail">
          {detail.it.video
            ? <video className="bg" src={detail.it.video} autoPlay loop playsInline onCanPlay={(e) => { e.target.muted = true; e.target.play().catch(() => {}); }} ref={(el) => { if (el) { el.muted = true; if (el.paused) el.play().catch(() => {}); } }} />
            : detail.it.img
            ? <img className="bg" src={optim(detail.it.img, 520)} alt="" />
            : <div className="bg bfond bfond-1" />}
          <div className="shade" />
          {!fSlots && (detail.it.compose || []).length > 0 && (() => {
            const grps = detail.it.compose;
            const openG = grps.length === 1 ? grps[0] : (detail.grpOpen !== undefined ? detail.grpOpen : null);
            const items = grps.filter((g) => detail.choix[g] && G[g].imgs?.[detail.choix[g]]);
            if (!items.length) return null;
            return (
              <div className="bd-viz" aria-hidden="true">
                {items.map((g, i) => {
                  const o = detail.choix[g];
                  return (
                    <div key={g + o} className={`viz v${i % 3}`} data-testid={`borne-viz-${g}`}>
                      <img src={optim(G[g].imgs[o], 320)} alt="" />
                      <span>{o}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          <div className="dhead">
            <button className="bd-back" onClick={() => { if (detail.editIdx != null) setShowPanier(true); setDetail(null); setInside(false); }} data-testid="borne-detail-fermer">‹</button>
            <div className="dt">
              <h2>{detail.it.nom}</h2>
              {fSlots && <span className="fbonplan" data-testid="borne-bonplan">★ LE BON PLAN</span>}
              {detail.it.badge && !fSlots && <span className="bd-fl" style={{ position: 'static', animation: 'none' }}>{detail.it.badge}</span>}
              <button className="bd-inside" onClick={() => { setInside(true); logEv('product_detail_ingredients_opened', { nom: detail.it.nom, version: 'A' }); }} data-testid="borne-inside-btn">ce qu'il y a dedans ⌄</button>
            </div>
            <span style={{ width: 52, flex: 'none' }} />
          </div>
          <div className="body">
            {(detail.it.attente > 0 || (detail.it.infos || []).length > 0) && (
              <div className="bd-infochips" data-testid="borne-detail-infos">
                {detail.it.attente > 0 && <span className="wait">🔥 CUIT À LA COMMANDE · PRÊT EN {detail.it.attente} MIN</span>}
                {(detail.it.infos || []).map((inf) => <span key={inf}>{inf}</span>)}
              </div>
            )}
            {!fSlots && (() => { const c = data.categories.find((x) => x.id === detail.it.cat_id); return c?.edition ? <span className="bd-ed">{c.edition}</span> : null; })()}
            {fSlots && (
              <div className="bd-fcfg2" data-testid="borne-formule-cfg">
                <div className="fhero2">
                  <div className="fhalo" />
                </div>
                <div className="fcartes">
                  {fSlots.map((sl) => { const c = slotChoisi(sl); if (!c) return null; const nCands = slotCands(sl).length;
                    const ouvrir = nCands > 1 ? () => setDetail((d) => ({ ...d, slotOpen: sl.id, slotIdx: Math.max(0, slotCands(sl).findIndex((x) => x.nom === c.nom)) })) : null;
                    return (
                    <div key={sl.id} className={`fcarte${c.epuise ? ' epuise' : ''}`} data-testid={`borne-fslot-${sl.id}`}
                      role={ouvrir ? 'button' : undefined} onClick={ouvrir || undefined} style={ouvrir ? { cursor: 'pointer' } : undefined}>
                      {c.epuise && <span className="bd-epz" data-testid={`borne-fslot-epuise-${sl.id}`}>Épuisé</span>}
                      {c.img ? <img key={c.nom} src={optim(c.img, 320)} alt="" /> : <div className="fnoimg petit fredoka">{c.nom[0]}</div>}
                      <small>{c.cont || ''}{c.supp > 0 ? ` · +${eur(c.supp)}` : ''}</small>
                      <b data-testid={`borne-fslot-nom-${sl.id}`}>{c.nom}</b>
                      {detail.laits?.[sl.id] && detail.laits[sl.id] !== G.lait?.opts?.[0] && <em className="flait">{detail.laits[sl.id]}</em>}
                      {nCands > 1 && (
                        <button className="fchg" data-testid={`borne-fslot-changer-${sl.id}`} onClick={ouvrir}>
                          Changer
                        </button>
                      )}
                    </div>
                  ); })}
                </div>
              </div>
            )}
            {!fSlots && gBox && detail.it.tailles?.length > 0 && (
              <div className="bd-dk" data-testid="borne-tailles">
                <div className="dk-aide">Combien de cookies à offrir ? <small>{detail.tailleChoisie ? `Box de ${detail.tailleChoisie}` : 'choisis ta taille'}</small></div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {detail.it.tailles.map((tt) => {
                    const on = detail.tailleChoisie === tt.n;
                    return (
                      <button key={tt.n} data-testid={`borne-taille-${tt.n}`}
                        onClick={() => setDetail((d) => ({ ...d, tailleChoisie: tt.n, it: { ...d.it, choix_n: tt.n, px: tt.px }, multi: {}, picks: [] }))}
                        style={{ flex: 1, padding: '14px 8px', borderRadius: 18, cursor: 'pointer', textAlign: 'center',
                          border: `2px solid ${on ? '#F15623' : 'rgba(46,19,11,.25)'}`,
                          background: on ? '#F15623' : 'rgba(255,255,255,.65)', color: on ? '#FFF7EC' : '#2E130B' }}>
                        <div className="fredoka" style={{ fontSize: 22, fontWeight: 600 }}>×{tt.n}</div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, opacity: .85 }}>{eur(tt.px)}</div>
                        <div style={{ fontSize: 11, opacity: .7 }} data-testid={`borne-taille-unite-${tt.n}`}>soit {eur(tt.px / tt.n)} /cookie</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {!fSlots && gBox && (!detail.it.tailles?.length || detail.tailleChoisie) && (
              <div className="bd-dk" data-testid="borne-box">
                {boxAnim ? (
                  <BoiteDuo n={nBox} duo={duoMode} picks={bxPicks()} imgs={gBox.imgs} hide={bxHide} msg={bxMsg}
                    shakeTick={bxShake} boxRef={bxBox} slotRefs={bxSlots} onRemove={bxRemove}
                    combo={bxCombo} comboDone={bxComboOk} comboBusy={bxBusy} onCombo={bxPrendreCombo} />
                ) : (
                <>
                {coachFor === detail.it.nom && nBox > 0 && (hints?.top_cookies || []).filter((t) => gBox.opts.includes(t.nom)).length >= 2
                  && !/boss/i.test(detail.it.nom || '') && !panier.some((l) => /boss/i.test(l.nom)) && (
                  <div className="bd-coachbox" data-testid="borne-coach-box">
                    <div className="cb-head">⭐ Les préférés des clients <span className="cb-fleche">↓</span></div>
                    <div className="cb-row">
                      {hints.top_cookies.filter((t) => gBox.opts.includes(t.nom)).slice(0, 4).map((t, i) => (
                        <span key={t.nom} className="cb-card" style={{ animationDelay: `${i * 0.13}s` }}>
                          {t.img ? <img src={optim(t.img, 320)} alt="" /> : <i>🍪</i>}<small>{t.nom}</small>
                        </span>
                      ))}
                    </div>
                    <button type="button" className="cb-fill" data-testid="borne-coach-box-fill" onClick={() => {
                      const dispo = hints.top_cookies.filter((t) => gBox.opts.includes(t.nom)).map((t) => t.nom).slice(0, 4);
                      if (!dispo.length) return;
                      const m = {};
                      dispo.slice(0, Math.min(4, nBox)).forEach((nom) => { m[nom] = 1; });
                      setDetail((d) => ({ ...d, multi: m }));
                      track('accepte', detail.it.nom, detail.it.px || 0, 'anim-coachbox');
                    }}>Je prends les mêmes ✓</button>
                    {nBox > 4 && <small style={{ display: 'block', textAlign: 'center', opacity: .75, marginTop: 4 }}>1 de chaque — complète les {nBox - 4} restants à ton goût</small>}
                  </div>
                )}
                <div className="dk-aide">{gBox.titre} <small>{totBox}/{nBox} choisis · {gBox.aide}</small></div>
                </>
                )}
                <div className="dk-row" data-testid="borne-dk-row-box">
                  {gBox.opts.map((o) => {
                    const n = (detail.multi || {})[o] || 0;
                    const supp = gBox.supp?.[o] || 0;
                    const lt = optLockTier(detail.it.choix_groupe, o);
                    const ep = (gBox.epuise || []).includes(o);
                    const n1 = boxAnim && hints?.num1_semaine?.nom && (BOX_MAP[hints.num1_semaine.nom] || hints.num1_semaine.nom) === o;
                    return (
                      <button key={o} ref={(el) => { bxGrid.current[o] = el; }} className={`dk-card${n > 0 ? ' on' : ''}${ep ? ' epuise' : ''}`} disabled={ep && !boxAnim}
                        onClick={(e) => {
                          if (ep) { if (boxAnim) bxRefuse(e.currentTarget); return; }
                          if (lt) { setClubOpen(true); return; }
                          if (boxAnim) { bxAdd(o, e.currentTarget); return; }
                          totBox < nBox && setDetail((d) => ({ ...d, multi: { ...d.multi, [o]: (d.multi?.[o] || 0) + 1 } }));
                        }}
                        data-testid={`borne-box-${o.replace(/\s+/g, '-')}`}>
                        {n1 && <span className="bx-n1" data-testid={`borne-n1-${o.replace(/\s+/g, '-')}`}>★ N°1</span>}
                        {ep && <span className="bd-epz mini" data-testid={`borne-epuise-opt-${o.replace(/\s+/g, '-')}`}>Épuisé</span>}
                        {lt && <span className="bd-optlock" data-testid={`borne-optlock-${o.replace(/\s+/g, '-')}`}>🔒 {lockLabel(lt)}</span>}
                        {gBox.imgs?.[o] && <img className="ph" src={optim(gBox.imgs[o], 320)} alt="" loading="lazy" />}
                        <span className="nm">{o}{supp > 0 && <small className="supp">+{eur(supp)}</small>}</span>
                        <div className="dk-qty">
                          {n > 0 && <span className="moins" onClick={(e) => { e.stopPropagation(); if (boxAnim) { const i = bxPicks().lastIndexOf(o); if (i >= 0) bxRemove(i); return; } setDetail((d) => ({ ...d, multi: { ...d.multi, [o]: d.multi[o] - 1 } })); }} data-testid={`borne-box-moins-${o.replace(/\s+/g, '-')}`}>−</span>}
                          <span className="tick">{n > 0 ? `×${n}` : '+'}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {!fSlots && (detail.it.compose || []).length > 0 && (() => {
              const grps = detail.it.compose;
              const solo = grps.length === 1;
              const open = solo ? grps[0] : (detail.grpOpen !== undefined ? detail.grpOpen : null);
              const optsG = (g) => { const sel = detail.it.opts_produit?.[g]; return sel?.length ? G[g].opts.filter((o) => sel.includes(o)) : G[g].opts; };
              return (
                <div className="bd-dk" data-testid="borne-compose">
                  {open && G[open] && (
                    <React.Fragment key={open}>
                      <div className="dk-aide">{G[open].titre} <small>{optInclus(open) ? 'inclus — sans supplément' : G[open].aide}</small></div>
                      <div className="dk-row" data-testid={`borne-dk-row-${open}`}>
                        {optsG(open).map((o) => { const lt = optLockTier(open, o); const ep = (G[open].epuise || []).includes(o); const on = detail.choix[open] === o; return (
                          <button key={o} className={`dk-card${on ? ' on' : ''}${ep ? ' epuise' : ''}`} disabled={ep}
                            onClick={() => { if (ep) return; if (lt) { setClubOpen(true); return; } setDetail((d) => { const choix = { ...d.choix, [open]: o }; return { ...d, choix, grpOpen: grps.find((x) => !choix[x]) ?? open }; }); }}
                            data-testid={`borne-opt-${o.replace(/\s+/g, '-')}`}>
                            {ep && <span className="bd-epz mini" data-testid={`borne-epuise-opt-${o.replace(/\s+/g, '-')}`}>Épuisé</span>}
                            {lt && <span className="bd-optlock" data-testid={`borne-optlock-${o.replace(/\s+/g, '-')}`}>🔒 {lockLabel(lt)}</span>}
                            {G[open].imgs?.[o] && <img className="ph" src={optim(G[open].imgs[o], 320)} alt="" loading="lazy" />}
                            <span className="nm">{o}{!optInclus(open) && (G[open].supp?.[o] || 0) > 0 && <small className="supp">+{eur(G[open].supp[o])}</small>}</span>
                            <span className="tick">{on ? '✓' : '+'}</span>
                          </button>
                        ); })}
                      </div>
                    </React.Fragment>
                  )}
                  {!solo && (
                    <div className="dk-rail">
                      {grps.map((g) => {
                        const sel = detail.choix[g];
                        const img = G[g].imgs?.[sel] || G[g].imgs?.[optsG(g)[0]] || (g === 'espresso_shot' ? G[g].imgs?.['Espresso shot'] : null);
                        return (
                          <button key={g} className={`dk-tab${open === g ? ' on' : ''}`}
                            onClick={() => setDetail((d) => ({ ...d, grpOpen: (d.grpOpen !== undefined ? d.grpOpen : null) === g ? null : g }))}
                            data-testid={`borne-grp-${g}`}>
                            {img ? <img src={optim(img, 320)} alt="" loading="lazy" /> : <span className="dk-tabic fredoka">{(sel || G[g].titre)[0]}</span>}
                            <span>{sel || G[g].titre}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="bd-bar">
            <div className="bd-pill bd-qty">
              <button onClick={() => setDetail((d) => ({ ...d, q: Math.max(1, d.q - 1) }))} data-testid="borne-detail-moins">−</button>
              <b>{detail.q}</b>
              <button onClick={() => setDetail((d) => ({ ...d, q: d.q + 1 }))} data-testid="borne-detail-plus">+</button>
            </div>
            <button className={`bd-cta${boxAnim ? (detailComplet ? ' bx-ready' : ' bx-ghost') : ''}`} disabled={boxAnim ? false : (!detailComplet || slotsEpuise)} data-testid="borne-detail-ajouter"
              onClick={() => {
                if (boxAnim && !detailComplet) { const r = nBox - totBox; bxWarn(`Encore ${r} cookie${r > 1 ? 's' : ''} à choisir`); return; }
                if (detail.it.moments_box) { setDetail((d) => ({ ...d, momentOpen: true })); return; }
                ajouterDetail(null);
              }}>
              {boxAnim && detail.editIdx == null ? (detailComplet ? `Ajouter ${duoMode ? 'mon duo' : 'ma box'} · ` : 'Ajouter · ') : detail.editIdx != null ? 'OK · ' : '+ '}{eur((detail.it.px + suppTot + suppSlots) * detail.q)}
              {fSlots && sepReelle - (detail.it.px + suppTot + suppSlots) >= minEco && <s className="fbarre" data-testid="borne-prix-barre">{eur(sepReelle * detail.q)}</s>}
              {!fSlots && showEco(detail.it) && <s className="fbarre" data-testid="borne-detail-eco-barre">{eur((pxBarreAff(detail.it) + suppTot + suppSlots) * detail.q)}</s>}
            </button>
          </div>
          {detail.momentOpen && (
            <div className="bd-fpick" data-testid="borne-moment-cfg">
              <div className="fpick-card" style={{ maxHeight: '88vh', overflowY: 'auto' }}>
                <button className="bd-back" onClick={() => setDetail((d) => ({ ...d, momentOpen: false }))} data-testid="borne-moment-fermer">✕</button>
                <h3 className="fredoka" style={{ fontSize: 24, margin: '6px 0 4px' }}>🎁 Pour qui est cette box ?</h3>
                <p style={{ fontSize: 13.5, opacity: .65, margin: '0 0 14px', lineHeight: 1.4 }}>
                  Après le paiement, tu recevras un <b>SMS</b> pour enregistrer ton message surprise (texte, photo ou vocal). Le destinataire le découvrira en scannant le QR de la box.
                </p>
                <input value={detail.mDest || ''} maxLength={30} placeholder="Prénom du destinataire"
                  onChange={(e) => setDetail((d) => ({ ...d, mDest: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: 17, fontFamily: 'Fredoka', padding: '13px 16px', borderRadius: 14, border: '1.5px solid rgba(46,19,11,.25)', background: '#fff', color: '#2E130B', marginBottom: 14 }}
                  data-testid="borne-moment-dest" />
                {detail.it.moment_occasion ? (
                  <div style={{ marginBottom: 18 }}>
                    <span style={{ display: 'inline-block', fontFamily: 'Fredoka', fontSize: 15, fontWeight: 500, padding: '9px 16px', borderRadius: 999, background: '#F15623', color: '#FFF7EC' }} data-testid="borne-moment-occ-fixe">{detail.it.moment_occasion}</span>
                  </div>
                ) : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                  {MOMENT_OCCASIONS.map((o) => (
                    <button key={o} onClick={() => setDetail((d) => ({ ...d, mOcc: o }))}
                      style={{ fontFamily: 'Fredoka', fontSize: 14.5, fontWeight: 500, padding: '9px 14px', borderRadius: 999, cursor: 'pointer',
                        border: `1.5px solid ${detail.mOcc === o ? '#F15623' : 'rgba(46,19,11,.2)'}`,
                        background: detail.mOcc === o ? '#F15623' : 'transparent', color: detail.mOcc === o ? '#FFF7EC' : '#2E130B' }}
                      data-testid={`borne-moment-occ-${o.replace(/\s+/g, '-')}`}>{o}</button>
                  ))}
                </div>
                )}
                <button className="bd-cta" style={{ width: '100%' }} disabled={!(detail.mDest || '').trim() || !(detail.it.moment_occasion || detail.mOcc)}
                  onClick={() => ajouterDetail({ recipient: detail.mDest.trim(), occasion: detail.it.moment_occasion || detail.mOcc })}
                  data-testid="borne-moment-ok">
                  C'est noté · {eur((detail.it.px + suppTot) * detail.q)}
                </button>
              </div>
            </div>
          )}
          {fSlots && detail.slotOpen && (() => {
            const sl = fSlots.find((s) => s.id === detail.slotOpen);
            const cands = slotCands(sl);
            const idx = Math.min(detail.slotIdx ?? 0, cands.length - 1);
            const cur = cands[idx];
            return (
              <div className="bd-fpick" data-testid="borne-fpick">
                <div className="fpick-card">
                  <button className="bd-back" onClick={() => setDetail((d) => ({ ...d, slotOpen: null }))} data-testid="borne-fpick-fermer">✕</button>
                  <div className="fpick-titre" data-testid="borne-fpick-titre">
                    Choisis {(sl.titre || 'ton produit').toLowerCase().startsWith('le ') || (sl.titre || '').toLowerCase().startsWith('la ') || (sl.titre || '').toLowerCase().startsWith('l\'') ? (sl.titre || '').toLowerCase().replace(/^le /, 'ton ').replace(/^la /, 'ta ').replace(/^l'/, 'ton ') : (sl.titre || 'ton produit').toLowerCase()}
                    <small>{(() => { const dispo = cands.filter((c) => !c.epuise); const supps = dispo.map((c) => c.supp || 0); const mn = supps.length ? Math.min(...supps) : 0; const mx = supps.length ? Math.max(...supps) : 0;
                      return mn > 0 ? `à partir de +${eur(mn)}` : mx > 0 ? `inclus · suppléments dès +${eur(Math.min(...supps.filter((s) => s > 0)))}` : 'inclus dans la formule'; })()}</small>
                  </div>
                  {cands.length <= 12 && (
                    <div className="fpick-hero">
                      {cur.img ? <img key={cur.nom} src={optim(cur.img, 800)} alt={cur.nom} /> : <div key={cur.nom} className="fnoimg grand fredoka">{cur.nom[0]}</div>}
                    </div>
                  )}
                  <h3 className="fredoka" data-testid="borne-fpick-nom">{cur.nom}</h3>
                  {cur.cont && <small className="fpick-cont">{cur.cont}</small>}
                  <div className="fpick-supp" data-testid="borne-fpick-supp">{cur.epuise ? '⛔ ÉPUISÉ' : cur.lock ? `🔒 ${lockLabel(cur.lock)}` : cur.supp > 0 ? `+${eur(cur.supp)}` : 'Inclus'}</div>
                  {!sl.groupe && G.lait && ['cloud', 'shake', 'cafes'].includes(catOfNom(cur.nom)) && (
                    <>
                      <button className="fchg" onClick={() => setDetail((d) => ({ ...d, persoOpen: !d.persoOpen }))} data-testid="borne-fpick-perso">Personnaliser</button>
                      {detail.persoOpen && (
                        <div className="laitrow" data-testid="borne-fpick-laits">
                          {G.lait.opts.map((o) => { const ep = (G.lait.epuise || []).includes(o); return (
                            <button key={o} className={`bd-chip${ep ? ' epuise' : ''}`} aria-pressed={(detail.laits?.[sl.id] || G.lait.opts[0]) === o} disabled={ep}
                              onClick={() => { if (ep) return; setDetail((d) => ({ ...d, laits: { ...d.laits, [sl.id]: o } })); }} data-testid={`borne-lait-${o.replace(/\s+/g, '-')}`}>
                              {ep && <span className="bd-epz mini" data-testid={`borne-epuise-lait-${o.replace(/\s+/g, '-')}`}>Épuisé</span>}
                              <span>{o}{(G.lait.supp?.[o] || 0) > 0 && <small className="supp"> +{eur(G.lait.supp[o])}</small>}</span>
                            </button>
                          ); })}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className={`fpick-grid${cands.length > 12 ? ' dense' : ''}`} data-testid="borne-fpick-grid">
                  {cands.map((c, i) => {
                    const r = c.epuise ? ((data.retours || {})[c.nom] || (data.retours || {})[c.nom.replace(/^Cookie /, '')]) : null;
                    return (
                    <button key={c.nom} className={`fpick-cell${i === idx ? ' on' : ''}${c.lock ? ' off' : ''}${c.epuise ? ' epuise' : ''}`} disabled={c.epuise}
                      onClick={() => { if (c.epuise) return; setDetail((d) => ({ ...d, slotIdx: i })); }} data-testid={`borne-fpick-item-${c.nom.replace(/\s+/g, '-')}`}>
                      {c.img ? <img src={c.img} alt="" loading="lazy" /> : <span className="fnoimg petit fredoka">{c.nom[0]}</span>}
                      <span className="fp-nom">{c.nom}</span>
                      <span className={`fp-supp${c.epuise ? ' ep' : ''}`} data-testid={c.epuise ? `borne-fpick-epuise-${c.nom.replace(/\s+/g, '-')}` : undefined}>
                        {c.epuise ? (r?.heure ? `Épuisé · retour ${r.heure}` : 'Épuisé') : c.lock ? `🔒 ${lockLabel(c.lock)}` : c.supp > 0 ? `+${eur(c.supp)}` : 'Inclus'}
                      </span>
                    </button>
                    );
                  })}
                </div>
                <button className="bd-cta fpick-ok" disabled={!!cur.lock || !!cur.epuise} data-testid="borne-fpick-valider"
                  onClick={() => {
                    if (cur.epuise) return;
                    if (cur.lock) { setClubOpen(true); return; }
                    setDetail((d) => {
                      const laits = { ...d.laits };
                      if (!['cloud', 'shake', 'cafes'].includes(catOfNom(cur.nom))) delete laits[sl.id];
                      return { ...d, laits, slotSel: { ...d.slotSel, [sl.id]: cur.nom }, slotOpen: null };
                    });
                  }}>
                  C'est lui
                </button>
              </div>
            );
          })()}
        </div>
      )}

      {inside && detail && (
        <div className="bd-veil" onClick={(e) => e.target === e.currentTarget && setInside(false)}>
          <div className="bd-sheet" data-testid="borne-inside-sheet">
            <button className="bd-back" style={{ float: 'right', width: 44, height: 44, fontSize: 18 }} onClick={() => setInside(false)} data-testid="borne-inside-fermer">✕</button>
            <h3 style={{ fontSize: 26, marginBottom: 10 }}>{detail.it.nom}</h3>
            <h4 style={{ fontSize: 17, margin: '14px 0 6px', fontFamily: 'Fredoka' }}>Ce qu'il y a dedans</h4>
            {detail.it.slots ? (
              <p style={{ fontSize: 16, color: 'rgba(46,19,11,.78)', lineHeight: 1.7, margin: 0, fontFamily: 'Fredoka' }} data-testid="borne-inside-formule">
                {detail.it.slots.map((s) => `1 ${s.titre.replace(/^L[ea] /i, '').toLowerCase()} au choix`).join(' + ')}
              </p>
            ) : (
              <p style={{ fontSize: 15, color: 'rgba(46,19,11,.72)', lineHeight: 1.5, margin: 0 }}>{detail.it.de || 'Recette maison toute simple — demande le détail à l\'équipe en caisse.'}</p>
            )}
            <h4 style={{ fontSize: 17, margin: '18px 0 8px', fontFamily: 'Fredoka' }}>Allergènes</h4>
            {(detail.it.allergenes || []).length > 0 && (
              <div className="bd-allergpictos" data-testid="borne-detail-allergenes">
                {detail.it.allergenes.filter((a) => ALLERG_LABEL[a]).map((a) => (
                  <span key={a} className="apx"><span className="ap">{ALLERG_PICTO[a]}</span>{ALLERG_LABEL[a]}</span>
                ))}
              </div>
            )}
            <p style={{ fontSize: 14, color: 'rgba(46,19,11,.62)', lineHeight: 1.5, margin: '0 0 6px' }}>Nos produits sont préparés dans un atelier qui utilise : gluten, lait, œufs, soja, arachides, fruits à coque et sésame.</p>
            <p style={{ fontSize: 13, color: 'rgba(46,19,11,.45)', margin: 0 }}>Une question ? L'équipe en caisse connaît chaque recette.</p>
          </div>
        </div>
      )}

      {showPanier && (
        <div className="bd-veil" onClick={(e) => e.target === e.currentTarget && setShowPanier(false)}>
          <div className="bd-sheet" data-testid="borne-panier">
            <h3 style={{ fontSize: 22, marginBottom: 6 }}>Ma commande</h3>
            <p style={{ fontSize: 13, color: 'rgba(46,19,11,.55)', margin: '0 0 14px' }}>Le paiement se fait en caisse. Rien n'est débité ici.</p>
            {panier.map((l, i) => (
              <div key={i} className="bd-ligne">
                <div style={{ flex: 1 }}>
                  <div className="fredoka" style={{ fontSize: 18 }}>{l.nom}{l.reward ? ' 🎁' : ''}{l.roue ? ' 🎡' : ''}</div>
                  {l.opts.length > 0 && <div style={{ fontSize: 16, color: 'rgba(46,19,11,.6)' }}>{l.opts.join(' · ')}</div>}
                  {!l.reward && !l.roue && <button className="bd-lmod" onClick={() => modifierLigne(i)} data-testid={`borne-ligne-modifier-${i}`}>Modifier</button>}
                </div>
                {l.reward || l.roue ? (
                  <span className="fredoka" style={{ fontSize: 13.5, fontWeight: 700, color: '#2FA96B' }}>OFFERT</span>
                ) : (
                  <div className="bd-qty" style={{ gap: 10 }}>
                    <button onClick={() => setPanier((p) => p.map((x, k) => k === i ? { ...x, q: x.q - 1 } : x).filter((x) => x.q > 0))} data-testid={`borne-ligne-moins-${i}`}>−</button>
                    <b>{l.q}</b>
                    <button onClick={() => setPanier((p) => p.map((x, k) => k === i ? { ...x, q: x.q + 1 } : x))} data-testid={`borne-ligne-plus-${i}`}>+</button>
                  </div>
                )}
                <div className="fredoka" style={{ fontSize: 18, minWidth: 62, textAlign: 'right' }}>{eur(l.px * l.q)}</div>
                <button className="bd-lsuppr" onClick={() => { logEv('product_removed', { nom: l.nom }); if (l.roue) setRoueGain(null); setPanier((p) => p.filter((_, k) => k !== i)); }} data-testid={`borne-ligne-suppr-${i}`} aria-label="supprimer">✕</button>
              </div>
            ))}
            {remises.map((r) => (
              <div key={r.nom} className="bd-ligne" style={{ color: '#2FA96B' }} data-testid={`borne-remise-${r.nom.replace(/\s+/g, '-')}`}>
                <div style={{ flex: 1, fontWeight: 700, fontSize: 18 }}>🏷️ {r.nom}</div>
                <div className="fredoka" style={{ fontSize: 18, fontWeight: 700 }}>−{eur(r.montant)}</div>
              </div>
            ))}
            {membre && (membre.stamps ?? 0) >= 10 && !panier.some((l) => l.reward && !l.defi && !l.destock) && (
              <button className="bd-reward-btn" onClick={() => { setShowPanier(false); setRewardPick(true); }} data-testid="borne-reward-btn">
                🎁 Cookie offert — utiliser 10 tampons ({membre.stamps} dispo)
              </button>
            )}
            {(() => {
              const inPanier = new Set(panier.map((l) => l.nom));
              const simple = (p) => !p.compose && !p.choix_n && p.actif !== false && !p.epuise && !prodLock(p);
              const aCookie = panier.some((l) => catOfNom(l.nom) === 'cookies');
              const aBoisson = panier.some((l) => BOISSONS_ULTRA.includes(catOfNom(l.nom)));
              let sugg = [];
              if (aCookie && !aBoisson) sugg = data.products.filter((p) => BOISSONS_ULTRA.includes(p.cat_id) && simple(p) && !inPanier.has(p.nom));
              else if (aBoisson && !aCookie) sugg = data.products.filter((p) => p.cat_id === 'cookies' && simple(p) && !inPanier.has(p.nom));
              sugg = [...sugg, ...data.products.filter((p) => simple(p) && p.px <= 3.5 && !inPanier.has(p.nom) && !sugg.includes(p))].slice(0, 4);
              if (!sugg.length || !panier.length) return null;
              const titre = aCookie && !aBoisson ? "L'accord parfait 🥤" : aBoisson && !aCookie ? 'Un cookie avec ça ? 🍪' : 'Une dernière gourmandise ?';
              return (
                <div style={{ margin: '14px 0 2px' }} data-testid="borne-suggestions">
                  <div style={{ fontFamily: 'Space Mono, monospace', textTransform: 'uppercase', fontSize: 16, letterSpacing: '.8px', color: 'rgba(46,19,11,.7)', marginBottom: 8 }}>{titre}</div>
                  <div className="bd-suggrow">
                    {sugg.map((p) => (
                      <button key={p.nom} className="bd-sugg" data-testid={`borne-sugg-${p.nom.replace(/\s+/g, '-')}`}
                        onClick={() => { pop(); ajouter({ nom: p.nom, px: p.px, opts: [] }, 1); }}>
                        {(p.img_thumb || p.img) ? <img src={p.img_thumb || p.img} alt="" loading="lazy" /> : <span className="ph bfond bfond-1" />}
                        <span className="n">{p.nom}</span>
                        <span className="p">+ {eur(Math.max(0, suppReel(p)))}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
            <button className="bd-ghost" style={{ margin: '10px 0 0' }} onClick={() => setShowPanier(false)} data-testid="borne-ajouter-produits">＋ Ajouter d'autres produits</button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0 14px' }}>
              <span className="fredoka" style={{ fontSize: 18 }}>Total à payer en caisse</span>
              <span className="fredoka" style={{ fontSize: 26, fontWeight: 700 }} data-testid="borne-total">
                {totalRemises > 0 && <s style={{ fontSize: 16, opacity: .45, marginRight: 8 }}>{eur(total)}</s>}
                {eur(totalAff)}
              </span>
            </div>
            <button className="bd-cta" style={{ width: '100%' }} disabled={!panier.length} onClick={() => { setShowPanier(false); valider(); }} data-testid="borne-sheet-valider">Valider ma commande</button>
            <button className="bd-ghost" onClick={() => { setPanier([]); setShowPanier(false); }} data-testid="borne-tout-effacer">Tout effacer</button>
          </div>
        </div>
      )}

      {aiPay && etape === 'ident' && (
        <div className="bd-veil" onClick={(e) => { if (e.target === e.currentTarget) { aiFeedback(aiPay.decision_id, 'refuse'); setAiPay(null); } }}>
          <div className="bd-sheet" data-testid="borne-ai-pay-sheet">
            <h3 style={{ fontSize: 23 }} data-testid="borne-ai-pay-titre">Une dernière gourmandise ? ✨</h3>
            <div className="bd-smartup" data-testid="borne-ai-pay-card">
              {aiPay.reco.img && <img src={aiPay.reco.img} alt="" />}
              <div className="su-txt">
                <b>{aiPay.reco.nom}</b>
                <small>{aiPay.reco.raison}</small>
              </div>
              <span className="su-px">+ {eur(Math.max(0, suppReel(aiPay.reco)))}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button className="bd-ghost" style={{ width: 'auto', padding: '0 22px', margin: 0 }} onClick={() => { aiFeedback(aiPay.decision_id, 'refuse'); setAiPay(null); }} data-testid="borne-ai-pay-non">Non merci</button>
              <button className="bd-cta" style={{ flex: 1 }} onClick={() => aiAjouter(aiPay, () => setAiPay(null))} data-testid="borne-ai-pay-oui">Oui, j'ajoute · +{eur(Math.max(0, suppReel(aiPay.reco)))}</button>
            </div>
          </div>
        </div>
      )}

      {etape === 'ident' && (
        <div className="bo-ident-grid bd-ident" style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 330px', gap: 26, padding: '24px 30px 34px', alignItems: 'start', overflow: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center' }}>
            <button className="bd-ghost" style={{ alignSelf: 'flex-start', margin: 0 }} onClick={() => { setEtape('carte'); ouvrirPanier(); }} data-testid="borne-retour-panier">‹ Modifier mon panier</button>
            {suggClub && (
              <div style={{ background: '#F4C464', borderRadius: 16, padding: '14px 18px', width: '100%', maxWidth: 460 }} data-testid="borne-sugg-club">
                <div className="fredoka" style={{ fontSize: 16, fontWeight: 700, color: '#2E130B' }}>👋 {suggClub.prenom}, tu as un compte Boufi Club !</div>
                <div style={{ fontSize: 13, color: 'rgba(46,19,11,.7),', margin: '4px 0 10px' }}>Connecte-toi pour tes tampons, la roue et tes avantages.</div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button className="bd-cta" style={{ padding: '10px 22px', fontSize: 13.5 }} onClick={() => { chercheClub(); setSuggClub(null); }} data-testid="borne-sugg-club-oui">Oui, me connecter</button>
                  <button className="bd-ghost" style={{ margin: 0 }} onClick={() => setSuggClub(null)} data-testid="borne-sugg-club-non">Non merci</button>
                </div>
              </div>
            )}
            <h2 style={{ fontSize: 30, color: '#2E130B' }}>Presque terminé</h2>
            <p style={{ fontSize: 15.5, color: 'rgba(46,19,11,.62)', maxWidth: 460, margin: 0 }} data-testid="borne-ident-texte">
              {needTel
                ? 'Indique ton prénom — il apparaîtra sur les étiquettes. Une Box Moments est dans ton panier : un numéro de téléphone confirmé est nécessaire. Tu régleras directement au comptoir.'
                : 'Indique ton prénom — il apparaîtra sur les étiquettes. Tu régleras directement au comptoir.'}
            </p>
            <div className="bd-recap" data-testid="borne-recap">
              {panier.map((l, i) => {
                const Ic = CAT_ICO[catOfNom(l.nom)] || Cookie;
                return (
                  <div key={i} className="bd-recap-l" data-testid={`borne-recap-ligne-${i}`}>
                    <span className="ric"><Ic size={17} strokeWidth={2.2} /></span>
                    <span className="rnom">{l.nom}{l.opts.length > 0 && <small>{l.opts.join(' · ')}</small>}</span>
                    {l.reward ? <span className="roff">OFFERT</span> : (
                      <span className="rqty">
                        <button onClick={() => setPanier((p) => p.map((x, k) => k === i ? { ...x, q: x.q - 1 } : x).filter((x) => x.q > 0))} data-testid={`borne-recap-moins-${i}`}>−</button>
                        <b>{l.q}</b>
                        <button onClick={() => setPanier((p) => p.map((x, k) => k === i ? { ...x, q: x.q + 1 } : x))} data-testid={`borne-recap-plus-${i}`}>+</button>
                      </span>
                    )}
                    <span className="rprix">{eur(l.px * l.q)}</span>
                    <button className="rdel" onClick={() => { logEv('product_removed', { nom: l.nom }); setPanier((p) => p.filter((_, k) => k !== i)); }} data-testid={`borne-recap-suppr-${i}`} aria-label="supprimer">✕</button>
                  </div>
                );
              })}
              <div className="rtotal"><b>Total</b><b data-testid="borne-recap-total">{eur(totalAff)}</b></div>
            </div>
            <div className="bo-identbloc" data-testid="borne-ident-bloc">
              <input className={`bo-champ${champActif === 'prenom' ? ' actif' : ''}`} placeholder="Prénom" value={prenom} maxLength={16} enterKeyHint="done" inputMode="none"
                onFocus={() => setChampActif('prenom')}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                onChange={(e) => setPrenom(e.target.value)} data-testid="borne-prenom-input" />
              {wantTel && (
                <div className={`bo-champ${champActif === 'tel' ? ' actif' : ''}`} onClick={() => setChampActif('tel')} data-testid="borne-tel-aff">
                  {tel ? tel.replace(/(\d{2})(?=\d)/g, '$1 ') : <span style={{ color: 'rgba(46,19,11,.4)' }}>Numéro de téléphone</span>}
                </div>
              )}
            </div>
            {champActif === 'prenom' && (
              <div className="bo-azerty" data-testid="borne-azerty">
                {[['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'], ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'], ['W', 'X', 'C', 'V', 'B', 'N', 'É', 'È', 'À', 'Ç']].map((row, ri) => (
                  <div className="row" key={ri}>
                    {row.map((k) => (
                      <button key={k} data-testid={`borne-azerty-${k}`} onClick={() => setPrenom((p) => (p.length < 16 ? fmtPrenom(p + k) : p))}>{k}</button>
                    ))}
                  </div>
                ))}
                <div className="row">
                  <button style={{ width: 'auto', padding: '0 24px' }} onClick={() => setPrenom((p) => p.slice(0, -1))} data-testid="borne-azerty-back">⌫</button>
                  <button onClick={() => setPrenom((p) => (p.trim() && p.length < 15 ? p + '-' : p))} data-testid="borne-azerty-tiret">-</button>
                  <button style={{ width: 'auto', padding: '0 48px' }} onClick={() => setPrenom((p) => (p.trim() && p.length < 15 ? p + ' ' : p))} data-testid="borne-azerty-espace">espace</button>
                  <button className="ok" style={{ margin: 0, width: 'auto', height: 'auto' }} disabled={!prenom.trim()} onClick={() => setChampActif(wantTel ? 'tel' : '')} data-testid="borne-azerty-ok">✓ OK</button>
                </div>
              </div>
            )}
            {wantTel && champActif === 'tel' && (
              <div className="bo-pave" data-testid="borne-pave">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '←'].map((k) => (
                  <button key={k} data-testid={`borne-pave-${k}`} onClick={() => {
                    setTel((t) => k === 'C' ? '' : k === '←' ? t.slice(0, -1) : t.length < 10 ? t + k : t);
                    setTelOk(false); setMembre(null); setMsg(['', '']);
                  }}>{k}</button>
                ))}
              </div>
            )}
            {wantTel && (
              <div style={{ fontSize: 15, minHeight: 22, color: msg[0] === 'ok' ? '#2FA96B' : 'rgba(46,19,11,.55)', fontWeight: msg[0] === 'ok' ? 600 : 400 }} data-testid="borne-msg-tel">
                {msg[1] || (tel.length === 10 ? '' : tel.length ? 'Il manque des chiffres' : '')}
              </div>
            )}
            {wantTel && tel.length === 10 && (
              <div style={{ background: telOk ? 'rgba(95,224,160,.16)' : '#FFF3D6', border: `1px solid ${telOk ? 'rgba(46,160,100,.4)' : 'rgba(46,19,11,.18)'}`, borderRadius: 14, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 14, maxWidth: 460 }} data-testid="borne-tel-confirm-bloc">
                <span style={{ fontSize: 15, fontWeight: 700, color: '#2E130B' }}>{tel.replace(/(\d{2})(?=\d)/g, '$1 ')}</span>
                {telOk
                  ? <span style={{ color: '#1E7A4A', fontWeight: 700, fontSize: 14 }}>✓ Numéro confirmé</span>
                  : <button className="bd-cta" style={{ padding: '9px 18px', fontSize: 13.5 }} onClick={() => setTelOk(true)} data-testid="borne-tel-confirmer">Je confirme ce numéro</button>}
              </div>
            )}
            {msg[0] === 'ko' && !wantTel && msg[1] && (
              <div className="bd-ident-err" data-testid="borne-ident-erreur">⚠ {msg[1]}</div>
            )}
            <p style={{ fontSize: 11.5, color: 'rgba(46,19,11,.55)', maxWidth: 400, lineHeight: 1.45, margin: '0 0 10px' }} data-testid="borne-rgpd-mention">
              🔒 Vos données (prénom{wantTel ? ', téléphone' : ''}) sont utilisées par HA Tacos uniquement pour votre commande{clubChoix === 'rejoindre' ? ' et votre fidélité Boufi Club (avec votre accord)' : ''}, conservées 36 mois max.{' '}
              <span onClick={() => setRgpdInfo(true)} style={{ textDecoration: 'underline', fontWeight: 700, cursor: 'pointer' }} data-testid="borne-rgpd-lien">Vos droits</span>
            </p>
            <button className="bd-cta" style={{ width: 'min(400px,100%)' }} disabled={!(prenom.trim() && (!wantTel || (tel.length === 10 && telOk)))} onClick={envoyer} data-testid="borne-envoyer-btn">Envoyer en caisse</button>
            <button className="bd-ghost" onClick={() => setEtape('carte')}>Revenir à la carte</button>
          </div>
          <div className={`bd-club${!membre && !clubChoix ? ' club-glow' : ''}`} data-testid="borne-club-box">
            {membre ? (<>
              <h3 style={{ fontSize: 19 }}>Bonjour {membre.prenom}<span className={`bo-tier ${membre.tier}`}>{membre.tier}</span></h3>
              <p style={{ fontSize: 13.5, color: 'rgba(46,19,11,.62)' }}>On t'a reconnu. Ta commande sera créditée sur ton compte Club après le passage en caisse.</p>
              <button className="bd-ghost" onClick={() => { setMembre(null); setTel(''); setPrenom(''); setMsg(['', '']); }}>Ce n'est pas moi</button>
            </>) : clubChoix === 'sans' ? (<>
              <h3 style={{ fontSize: 19 }}>Sans le Club</h3>
              <p style={{ fontSize: 13.5, color: 'rgba(46,19,11,.62)' }}>C'est noté — il ne reste que ton prénom à indiquer.</p>
              <button className="bd-ghost" onClick={() => setClubChoix(null)} data-testid="borne-club-changer">Changer d'avis</button>
            </>) : clubChoix === 'rejoindre' ? (<>
              <h3 style={{ fontSize: 19 }}>✨ Bienvenue au Boufi Club</h3>
              <p style={{ fontSize: 13.5, color: 'rgba(46,19,11,.62)' }}>Tape ton numéro de téléphone : ta carte démarre aujourd'hui et tes tampons seront crédités en caisse.</p>
              <p style={{ fontSize: 11, color: 'rgba(46,19,11,.5)', lineHeight: 1.4 }} data-testid="borne-club-consent">En rejoignant le Club, vous acceptez que HA Tacos utilise votre numéro pour gérer votre fidélité et vous informer des offres BOUFI. Retrait possible à tout moment.</p>
              <button className="bd-ghost" onClick={() => { setClubChoix(null); setChampActif('prenom'); if (!needTel) { setTel(''); setTelOk(false); setMsg(['', '']); } }} data-testid="borne-club-rejoindre-annuler">Annuler</button>
            </>) : (<>
              <h3 style={{ fontSize: 19 }}>Boufi Club</h3>
              <p style={{ fontSize: 13.5, color: 'rgba(46,19,11,.62)' }}>Tampons, roue cadeau et exclusivités à chaque commande.</p>
              {clubIdent.membre !== false && (
                <button className="bd-cta" style={{ width: '100%', height: 54, fontSize: 16 }} onClick={() => { if (tel.length === 10) chercheClub(); else setClubOpen(true); }} data-testid="borne-club-btn">⭐ Je suis membre</button>
              )}
              {clubIdent.rejoindre !== false && (
                <button className="bd-cta" style={{ width: '100%', height: 54, fontSize: 15.5, marginTop: 10, background: '#F4C464', color: '#2E130B' }}
                  onClick={() => { track('propose', 'club_signup_clicked', 0, wcSeenRef.current ? 'welcome' : 'club'); setClubChoix('rejoindre'); setChampActif(prenom.trim() ? 'tel' : 'prenom'); }} data-testid="borne-club-rejoindre-btn">✨ Rejoindre le Boufi Club</button>
              )}
              <button className="bd-ghost" style={{ width: '100%', marginTop: 10 }} onClick={() => setClubChoix('sans')} data-testid="borne-club-sans-btn">Continuer sans le Club</button>
            </>)}
          </div>
        </div>
      )}

      {data && etape === 'fin' && !roue && !detail && !clubOpen && (
        <button className="bd-rouedock" onClick={() => { if (membre) lancerRoue(); else setRoueDock(true); }} data-testid="borne-roue-dock">
          <span className="rd-wheel">
            {[0, 60, 120, 180, 240, 300].map((a) => <span key={a} className="rd-ray" style={{ transform: `rotate(${a}deg)` }} />)}
            <span className="rd-hub">🎁</span>
          </span>
        </button>
      )}

      {banMsg && <div className="bd-banmsg" data-testid="borne-banmsg">{banMsg}</div>}

      {roueDock && (
        <div className="bd-veil" style={{ zIndex: 80 }} onClick={(e) => { if (e.target === e.currentTarget) setRoueDock(false); }} data-testid="borne-rouedock-overlay">
          <div className="rd-panel">
            <span className="rd-wheel big">
              {[0, 60, 120, 180, 240, 300].map((a) => <span key={a} className="rd-ray" style={{ transform: `rotate(${a}deg)` }} />)}
              <span className="rd-hub">🎁</span>
            </span>
            <h3 className="fredoka" data-testid="rouedock-titre">La Roue Boufi</h3>
            {membre ? (
              <p>Tu es membre, {membre.prenom} ⭐ Termine ta commande et la roue tournera pour toi : cookies offerts, tampons bonus et surprises !</p>
            ) : (
              <p>Réservée aux membres du <b>Boufi Club</b> : après chaque commande, la roue tourne et tu peux gagner des cookies, des tampons et des cadeaux. C'est gratuit.</p>
            )}
            {!membre && (
              <button className="bd-cta" style={{ width: '100%' }} onClick={() => { setRoueDock(false); setClubOpen(true); }} data-testid="rouedock-membre-btn">⭐ Je suis déjà membre</button>
            )}
            {!membre && (
              <p className="rd-note">Pas encore membre ? Choisis « ✨ Rejoindre le Boufi Club » à la fin de ta commande.</p>
            )}
            <button className="bd-ghost" style={{ width: '100%' }} onClick={() => setRoueDock(false)} data-testid="rouedock-fermer">Continuer ma commande</button>
          </div>
        </div>
      )}

      {rgpdInfo && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(20,8,4,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setRgpdInfo(false)} data-testid="borne-rgpd-modal">
          <div style={{ background: '#FFF7EC', borderRadius: 22, maxWidth: 560, padding: '26px 28px', color: '#2E130B' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: 20, margin: '0 0 10px' }}>🔒 Vos données personnelles</h3>
            <p style={{ fontSize: 14, lineHeight: 1.55, margin: '0 0 10px' }}>HA Tacos (BOUFI) utilise votre prénom et, si vous le donnez, votre téléphone pour : préparer et vous remettre votre commande, et gérer votre carte Boufi Club si vous la rejoignez (avec votre accord, retirable à tout moment).</p>
            <p style={{ fontSize: 14, lineHeight: 1.55, margin: '0 0 14px' }}>Conservation : <b>36 mois maximum</b>, puis anonymisation automatique. Vos droits (accès, rectification, effacement, portabilité, opposition) : demandez en boutique ou consultez notre politique de confidentialité. Réclamation possible auprès de la CNIL (cnil.fr).</p>
            <button className="bd-cta" style={{ width: '100%' }} onClick={() => setRgpdInfo(false)} data-testid="borne-rgpd-fermer">J'ai compris</button>
          </div>
        </div>
      )}

      {etape === 'fin' && (
        <div className="bd-fin-bg" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 40 }} data-testid="borne-confirmation">
          <div className="bo-numero beclat" style={{ color: '#E6391F', display: 'inline-block' }} data-testid="borne-fin-num">N° {finNum}</div>
          {finTampon && (
            <div style={{ margin: '10px auto 0', display: 'inline-block', padding: '8px 18px', borderRadius: 999, background: 'rgba(95,224,160,.16)', border: '1px solid rgba(46,160,100,.35)', color: '#1E7A4A', fontWeight: 800, fontSize: 15 }} data-testid="borne-fin-tampon">
              ✓ +{finTampon} tampon{finTampon > 1 ? 's' : ''} Boufi Club crédité{finTampon > 1 ? 's' : ''}{membre ? ` · total : ${membre.stamps}` : ''} 🍪
            </div>
          )}
          <h2 style={{ fontSize: 28, margin: '14px 0 10px', color: '#2E130B' }}>Merci {prenom}</h2>
          {membre && (
            <button className="bd-cta" style={{ width: 320, marginBottom: 14, background: '#E6391F' }} onClick={lancerRoue} data-testid="borne-roue-btn">
              🎡 Tourner la roue Boufi
            </button>
          )}
          {membre && (
            <div className="bd-club" style={{ maxWidth: 440, margin: '0 0 18px', textAlign: 'center' }} data-testid="borne-fin-club">
              <h3 className="fredoka" style={{ fontSize: 19, margin: '0 0 6px' }}>⭐ {membre.stamps ?? 0} tampon{(membre.stamps ?? 0) > 1 ? 's' : ''} sur la carte</h3>
              <p style={{ fontSize: 14, color: 'rgba(46,19,11,.68)', margin: 0 }}>
                {(membre.stamps ?? 0) >= 10 ? 'Cookie offert disponible à ta prochaine commande 🎁'
                  : `Plus que ${10 - (membre.stamps ?? 0)} avant un cookie offert 🎁`}
                {' '}Cette commande sera créditée après l'encaissement.
              </p>
            </div>
          )}
          <p style={{ fontSize: 17, color: 'rgba(46,19,11,.68)', maxWidth: 460, margin: '0 0 20px' }}>Présente-toi en caisse pour régler. La préparation démarre après le paiement, suis ton numéro sur l'écran.</p>
          <button className="bd-cta" style={{ width: 320 }} onClick={deconnecter} data-testid="borne-nouvelle-btn">Nouvelle commande</button>
          <p style={{ fontSize: 14, color: 'rgba(46,19,11,.55)', marginTop: 12 }}>Retour à l'accueil dans {compte} s</p>
        </div>
      )}

      {DEMO && <div className="bd-demo-pill" data-testid="borne-demo-pill">MODE DÉMO — rien n'est envoyé en caisse</div>}

      {etape === 'carte' && !detail && !showPanier && false && (
        <button className={`bd-allergbtn${allergies.length ? ' on' : ''}`} onClick={() => setAllergOpen(true)} data-testid="borne-allerg-btn">
          ⚠️ Allergènes{allergies.length ? ` · ${allergies.length}` : ''}
        </button>
      )}

      {allergOpen && (
        <div className="bd-veil" style={{ zIndex: 82 }} data-testid="borne-allerg-modal" onClick={(e) => e.target === e.currentTarget && setAllergOpen(false)}>
          <div style={{ position: 'relative', width: 'min(660px, calc(100% - 40px))', background: '#FFF7EC', color: '#2E130B', borderRadius: 26, padding: '26px 28px', maxHeight: '88vh', overflowY: 'auto' }}>
            <button className="bd-back" style={{ position: 'absolute', top: 16, right: 16, width: 46, height: 46, fontSize: 18 }} onClick={() => setAllergOpen(false)} data-testid="borne-allerg-fermer">✕</button>
            <h3 className="fredoka" style={{ fontSize: 24, margin: '0 0 6px' }}>⚠️ Tes allergies</h3>
            <p style={{ fontSize: 16, color: 'rgba(46,19,11,.62)', margin: '0 0 14px' }}>
              Sélectionne tes allergènes : les produits qui en contiennent resteront visibles mais seront bloqués à la commande.
            </p>
            <div className="bd-allerggrid">
              {ALLERGENES_LIST.map(([id, la, pi]) => (
                <button key={id} className={`bd-allergchip${allergies.includes(id) ? ' on' : ''}`}
                  onClick={() => setAllergies((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])}
                  data-testid={`borne-allerg-chip-${id}`}>
                  <span className="ap">{pi}</span>{la}
                </button>
              ))}
            </div>
            <p style={{ fontSize: 12, color: 'rgba(46,19,11,.5)', margin: '16px 0 14px', lineHeight: 1.5 }}>
              ⚠️ Nos produits sont préparés dans un atelier utilisant gluten, lait, œufs, soja, arachides et fruits à coque :
              des traces sont possibles dans tous nos produits, même filtrés. En cas d'allergie sévère, parles-en à notre équipe.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="bd-ghost" style={{ flex: 1 }} onClick={() => { setAllergies([]); setAllergOpen(false); }} data-testid="borne-allerg-aucune">Aucune allergie</button>
              <button className="bd-cta" style={{ flex: 1, position: 'static' }} onClick={() => setAllergOpen(false)} data-testid="borne-allerg-valider">Valider</button>
            </div>
          </div>
        </div>
      )}

      {envoi && (
        <div className="bd-envoi" data-testid="borne-envoi-anim">
          <div className="bd-envoi-scene">
            <div className="bd-envoi-badge"><Cookie size={52} strokeWidth={2} /></div>
            <div className="bd-envoi-dots"><span /><span /><span /></div>
            <div className="bd-envoi-caisse"><ShoppingBag size={38} strokeWidth={2} /></div>
          </div>
          <h3 className="fredoka">Envoi en caisse…</h3>
          <p>Ton numéro de commande arrive</p>
          <div className="bd-envoi-bar"><span /></div>
        </div>
      )}

      {btbOpen && btb && (
        <div className="bd-veil" onClick={(e) => e.target === e.currentTarget && setBtbOpen(false)}>
          <div className="bd-sheet" data-testid="borne-btb-sheet">
            <span className="btb-tag" style={{ alignSelf: 'flex-start' }}>Édition limitée · 400 exemplaires</span>
            <h3 className="fredoka" style={{ fontSize: 26, margin: '10px 0 4px' }}>BAC TO BOUFI — 10 €</h3>
            <p style={{ fontSize: 14.5, color: 'rgba(46,19,11,.7)', lineHeight: 1.55, margin: '0 0 12px' }}>
              1 Cookie XL + 4 minis + 1 dip + 1 Cookie'shake Trois Choco. Dans ta box : un QR code unique — passe le BAC version cookie, décroche ta mention (100 lots à gagner) et tente le <b>Golden BAC 300 €</b> au tirage final du 30/09.
            </p>
            <div className="club-teaser" data-testid="borne-btb-stock" style={{ width: '100%' }}>
              <b>{btb.total.restantes}/{btb.total.capacite} box encore en circulation</b>
              {btb.daily?.restantes > 0 ? `Plus que ${btb.daily.restantes} disponibles aujourd'hui — premier arrivé, premier servi.` : 'Quota du jour épuisé — reviens demain dès l\'ouverture !'}
            </div>
            <button className="bd-cta" style={{ width: '100%', justifyContent: 'center' }}
              disabled={btb.total.restantes <= 0 || (btb.daily ? btb.daily.restantes <= 0 : false)}
              onClick={() => {
                const p = data.products.find((x) => /bac to boufi/i.test(x.nom));
                if (p) ajouter({ nom: p.nom, px: p.px, opts: [] });
                setBtbOpen(false);
              }} data-testid="borne-btb-ajouter">
              {btb.total.restantes <= 0 ? 'Série épuisée' : (btb.daily && btb.daily.restantes <= 0) ? 'Quota du jour épuisé — reviens demain' : 'Ajouter au panier — 10 €'}
            </button>
            <button className="bd-ghost" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} onClick={() => setBtbOpen(false)} data-testid="borne-btb-fermer">
              Plus tard
            </button>
          </div>
        </div>
      )}
      {clubOpen && (
        <div className="bd-veil" onClick={(e) => e.target === e.currentTarget && setClubOpen(false)}>
          <div className="bd-sheet" data-testid="borne-club-sheet">
            <button className="bd-back" style={{ position: 'absolute', top: 14, right: 14, width: 44, height: 44, fontSize: 18, zIndex: 3 }} onClick={() => setClubOpen(false)} data-testid="borne-club-sheet-fermer">✕</button>
            {membre ? (<>
              <h3 style={{ fontSize: 24 }}>Bonjour {membre.prenom} <span className={`bo-tier ${membre.tier}`}>{membre.tier}</span></h3>
              <div className="club-stamps" data-testid="borne-club-stamps">
                {Array.from({ length: 10 }).map((_, i) => (
                  <i key={i} className={i < Math.min(10, membre.stamps ?? 0) ? 'on' : ''} style={{ animationDelay: `${i * 0.05}s` }}>🍪</i>
                ))}
                <b>{Math.min(10, membre.stamps ?? 0) >= 10 ? '🎁 débloqué !' : `${10 - Math.min(10, membre.stamps ?? 0)} → 🎁`}</b>
              </div>
              <p style={{ fontSize: 12.5, color: 'rgba(46,19,11,.5)', margin: '4px 0 14px', textAlign: 'center' }}>Tout est appliqué automatiquement en caisse ✓</p>
              {membre.anniv && (
                <div className="club-teaser" style={{ background: 'rgba(230,57,31,.10)', borderColor: 'rgba(230,57,31,.5)' }} data-testid="borne-club-anniv">
                  <b>🎂 Joyeux anniversaire {membre.prenom} !</b>
                  Ta surprise du jour : {membre.anniv} — montre cet écran en caisse, c'est valable aujourd'hui seulement !
                </div>
              )}
              {teaserUnlocks.length > 0 && (
                <div className="club-teaser" data-testid="borne-club-teaser">
                  <b>Encore {teaserRestant} tampon{teaserRestant > 1 ? 's' : ''} → statut {nextTierId === 'silver' ? 'Silver ⭐' : 'Gold 👑'}</b>
                  Tu débloqueras : {teaserUnlocks.slice(0, 4).join(' · ')}{teaserUnlocks.length > 4 ? ` +${teaserUnlocks.length - 4} autres` : ''}
                </div>
              )}
              {membre.last_order?.lignes?.length > 0 && (() => {
                const suppOf = (name, prod) => { for (const [gk, g] of Object.entries(G)) { if (g.supp?.[name]) return (prod?.opts_incluses || []).includes(gk) ? 0 : g.supp[name]; } return 0; };
                const valides = membre.last_order.lignes.map((l) => {
                  const p = data.products.find((x) => x.nom === l.nom);
                  if (!p || p.epuise || p.slots || p.moments_box || prodLock(p)) return null;
                  const supp = (l.opts || []).reduce((s, o) => { const mm = /^(\d+)× (.+)$/.exec(o); return s + (mm ? +mm[1] * suppOf(mm[2], p) : suppOf(o, p)); }, 0);
                  return { nom: l.nom, px: p.px + supp, opts: l.opts || [], q: l.q || 1 };
                }).filter(Boolean);
                if (!valides.length) return null;
                const tot = valides.reduce((s, l) => s + l.px * l.q, 0);
                return (
                  <div className="club-teaser" style={{ background: 'rgba(244,196,100,.22)', borderColor: 'rgba(46,19,11,.28)' }} data-testid="borne-club-lastorder">
                    <b>⭐ Ma dernière commande</b>
                    {valides.map((l) => `${l.q}× ${l.nom}`).join(' · ')}
                    <button className="bd-cta" style={{ width: '100%', height: 50, fontSize: 15, marginTop: 10 }} data-testid="borne-club-reorder-btn"
                      onClick={() => { pop(); valides.forEach((l) => ajouter({ nom: l.nom, px: l.px, opts: l.opts }, l.q)); setClubOpen(false); setShowPanier(true); }}>
                      Tout remettre au panier · {eur(tot)}
                    </button>
                  </div>
                );
              })()}
              {membre.defi && (
                <div className="club-teaser" data-testid="borne-club-defi">
                  <b>🏆 Défi du mois — goûte 3 parfums différents</b>
                  {membre.defi.complete
                    ? (membre.defi.utilise ? 'Défi réussi — récompense déjà utilisée ce mois-ci 💪' : 'Bravo, défi réussi ! Ton cookie offert t\'attend 🎉')
                    : `${membre.defi.n}/3 parfums goûtés ce mois-ci${membre.defi.n ? ` : ${membre.defi.parfums.join(', ')}` : ''}`}
                  {membre.defi.complete && !membre.defi.utilise && !panier.some((l) => l.defi) && (
                    <button className="bd-cta" style={{ width: '100%', height: 48, fontSize: 15, marginTop: 10 }} data-testid="borne-club-defi-btn"
                      onClick={() => { pop(); setPanier((pn) => [...pn, { nom: 'Cookie offert · Défi du mois', px: 0, opts: ['Parfum au choix en caisse'], q: 1, reward: true, defi: true }]); setClubOpen(false); setShowPanier(true); }}>
                      🎁 Ajouter mon cookie offert
                    </button>
                  )}
                </div>
              )}
              <button className="bd-ghost" style={{ width: '100%', height: 48, fontSize: 14.5, marginBottom: 4 }} onClick={() => setParrainOpen(true)} data-testid="borne-club-parrainage-btn">
                🤝 Parrainer un ami — tu gagnes tous les deux
              </button>
              {(membre.stamps ?? 0) >= 10 && !panier.some((l) => l.reward && !l.defi && !l.destock) && (
                <button className="bd-reward-btn" style={{ marginBottom: 10 }} onClick={() => { setClubOpen(false); setRewardPick(true); }} data-testid="borne-club-reward-btn">
                  🎁 Utiliser 10 tampons — cookie offert
                </button>
              )}
              <button className="bd-cta" style={{ width: '100%' }} onClick={() => setClubOpen(false)} data-testid="borne-club-go">C'est parti, je commande</button>
              <button className="bd-ghost" onClick={() => { setMembre(null); setTel(''); setPrenom(''); setMsg(['', '']); }} data-testid="borne-club-logout">Ce n'est pas moi</button>
            </>) : (<>
              <div className="club-hero" data-testid="borne-club-hero">
                <img src="/logo-boufi-brun.png" alt="boufi" />
                <span className="club-hero-badge fredoka">★ CLUB</span>
              </div>
              <h3 className="fredoka" style={{ fontSize: 24, textAlign: 'center', margin: '14px 0 10px' }}>Bienvenue au Boufi Club</h3>
              <div className="club-tuiles" data-testid="borne-club-avantages">
                {[['🎡', 'Roue cadeau'], ['🍪', 'Cookie offert'], ['🎟️', '1 achat = 1 tampon'], ['🎁', 'Surprises']].map(([ic, lb], i) => (
                  <span key={lb} className="ct" style={{ animationDelay: `${0.09 * i}s` }}><i>{ic}</i><small>{lb}</small></span>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <p className="fredoka" style={{ fontSize: 17, fontWeight: 600, color: '#2E130B', margin: '2px 0 0', textAlign: 'center' }}>Ton numéro. C'est tout. 👇</p>
                <div className="bo-champ" data-testid="borne-club-tel-aff">{tel ? tel.replace(/(\d{2})(?=\d)/g, '$1 ') : <span style={{ color: 'rgba(46,19,11,.4)' }}>Numéro de téléphone</span>}</div>
                <div className="bo-pave club-pave" data-testid="borne-club-pave">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '←'].map((k) => (
                    <button key={k} data-testid={`borne-club-pave-${k}`} onClick={() => {
                      setTel((t) => k === 'C' ? '' : k === '←' ? t.slice(0, -1) : t.length < 10 ? t + k : t);
                      setMembre(null); setMsg(['', '']);
                    }}>{k}</button>
                  ))}
                </div>
                <div style={{ fontSize: 14.5, minHeight: 20, color: msg[0] === 'ok' ? '#2FA96B' : '#E6391F', fontWeight: 600 }} data-testid="borne-club-msg">{msg[1]}</div>
                <button className="bd-cta" style={{ width: 'min(400px,100%)' }} disabled={tel.length !== 10} onClick={chercheClub} data-testid="borne-club-connect">Me connecter</button>
                {clubChoix === 'rejoindre' ? (
                  <div className="club-join-hint" style={{ background: 'rgba(47,169,107,.16)', border: '1px solid rgba(46,160,100,.4)', color: '#1E7A4A', fontWeight: 700 }} data-testid="borne-club-signup-ok">✓ Inscription enregistrée — ta carte démarre avec cette commande</div>
                ) : (<>
                  {clubIdent.rejoindre !== false && (
                    <button className="bd-cta" style={{ width: 'min(400px,100%)', marginTop: 2, opacity: tel.length === 10 ? 1 : 0.55 }} disabled={tel.length !== 10}
                      onClick={() => { track('propose', 'club_signup_clicked', 0, wcSeenRef.current ? 'welcome' : 'club'); setClubChoix('rejoindre'); setTelOk(true); setMsg(['ok', '✨ Inscription enregistrée !']); setClubOpen(false); }}
                      data-testid="borne-club-signup-now">✨ M'inscrire avec ce numéro</button>
                  )}
                  <div className="club-join-hint" data-testid="borne-club-join-hint">✨ Pas encore membre ? Tape ton numéro et inscris-toi <b>tout de suite</b> — c'est gratuit. Ton numéro sert uniquement à ta fidélité.</div>
                </>)}
              </div>
            </>)}
          </div>
        </div>
      )}

      {giftPick && data?.destockage?.actif && (
        <div className="bd-veil" style={{ zIndex: 77 }}>
          <div className="bd-sheet" style={{ textAlign: 'center' }} data-testid="borne-gift-modal">
            <h3 style={{ fontSize: 22 }}>{data.destockage.titre}</h3>
            <p style={{ fontSize: 14.5, color: 'rgba(46,19,11,.65)', margin: '6px 0 16px' }}>Pour l'achat de ta {data.destockage.box}, choisis ton cookie offert :</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
              {data.destockage.cookies.map((c) => (
                <button key={c} className="bd-chip" style={{ flexDirection: 'column', height: 'auto', padding: '14px 20px', gap: 6 }} data-testid={`borne-gift-${c.replace(/\s+/g, '-')}`}
                  onClick={() => {
                    pop();
                    setPanier((p) => {
                      const k = cle({ nom: c, opts: ['🎁 Offert avec la Box x4'] });
                      const ex = p.find((l) => l.destock && cle(l) === k);
                      return ex ? p.map((l) => (l === ex ? { ...l, q: l.q + 1 } : l))
                        : [...p, { nom: c, px: 0, opts: ['🎁 Offert avec la Box x4'], q: 1, reward: true, destock: true }];
                    });
                    setGiftPick(false);
                  }}>
                  {G.cookie?.imgs?.[c] && <img src={G.cookie.imgs[c]} alt="" style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 14 }} />}
                  <span style={{ fontWeight: 700 }}>{c}</span>
                  <span style={{ color: '#E6391F', fontWeight: 800 }}>0,00 €</span>
                </button>
              ))}
            </div>
            <button className="bd-ghost" onClick={() => {
              giftSkip.current = panier.filter((l) => l.nom === data.destockage.box && !l.reward).reduce((s, l) => s + l.q, 0);
              setGiftPick(false);
            }} data-testid="borne-gift-non">Non merci</button>
          </div>
        </div>
      )}

      {rupture && (
        <div className="bd-veil" style={{ zIndex: 76 }} onClick={(e) => e.target === e.currentTarget && setRupture(null)}>
          <div className="bd-sheet" style={{ textAlign: 'center' }} data-testid="borne-rupture-modal">
            <h3 style={{ fontSize: 22 }}>Plus de {rupture.prod.nom} 😢</h3>
            {rupture.alt ? (<>
              <p style={{ fontSize: 14.5, color: 'rgba(46,19,11,.65)', margin: '8px 0 16px' }}>Mais on te conseille une valeur sûre, toute proche :</p>
              {(rupture.alt.img_thumb || rupture.alt.img) && <img src={rupture.alt.img_thumb || rupture.alt.img} alt={rupture.alt.nom} style={{ width: 150, height: 150, objectFit: 'cover', borderRadius: 20, margin: '0 auto 12px', display: 'block' }} />}
              <button className="bd-cta" style={{ width: '100%', marginBottom: 10 }} data-testid="borne-rupture-decouvrir"
                onClick={() => { const alt = rupture.alt; setRupture(null); setDetail({ it: alt, choix: {}, multi: {}, q: 1 }); }}>
                Découvrir {rupture.alt.nom} · {eur(rupture.alt.px)}
              </button>
            </>) : <p style={{ fontSize: 14.5, color: 'rgba(46,19,11,.65)', margin: '8px 0 16px' }}>Il revient très vite !</p>}
            <button className="bd-ghost" onClick={() => setRupture(null)} data-testid="borne-rupture-fermer">Tant pis</button>
          </div>
        </div>
      )}

      {parrainOpen && membre && (
        <div className="bd-veil" style={{ zIndex: 80 }} onClick={(e) => e.target === e.currentTarget && setParrainOpen(false)}>
          <div className="bd-sheet" style={{ textAlign: 'center' }} data-testid="borne-parrainage-modal">
            <button className="bd-back" style={{ float: 'right', width: 44, height: 44, fontSize: 18 }} onClick={() => setParrainOpen(false)} data-testid="borne-parrainage-fermer">✕</button>
            <h3 style={{ fontSize: 22 }}>🤝 Parraine un ami</h3>
            <p style={{ fontSize: 14, color: 'rgba(46,19,11,.65)', margin: '6px 0 14px' }}>Ton ami scanne ce QR avec son téléphone et s'inscrit avec ton code :<br /><b>+2 tampons pour toi, +1 pour lui</b> 🍪</p>
            <img src={`${API}/loyalty/public/referral/qr?phone=${encodeURIComponent(tel)}`} alt="QR parrainage" style={{ width: 220, height: 220, borderRadius: 18, border: '1px solid rgba(46,19,11,.15)', background: '#fff' }} data-testid="borne-parrainage-qr" />
            {membre.referral_code && <p className="fredoka" style={{ fontSize: 21, letterSpacing: '.06em', margin: '12px 0 0' }} data-testid="borne-parrainage-code">{membre.referral_code}</p>}
          </div>
        </div>
      )}

      {keepAsk && (
        <div className="bd-veil" style={{ zIndex: 78 }}>
          <div className="bd-sheet" style={{ textAlign: 'center' }} data-testid="borne-keepask">
            <h3 style={{ fontSize: 24 }}>Tu es toujours là ? 👋</h3>
            <p style={{ fontSize: 15, color: 'rgba(46,19,11,.65)', margin: '8px 0 18px' }}>On garde ton panier ({nbArt} article{nbArt > 1 ? 's' : ''}) — sans réponse, la borne repart à l'accueil dans <b data-testid="borne-keepask-compte">{keepCompte}s</b>.</p>
            <button className="bd-cta" style={{ width: '100%', marginBottom: 10 }} onClick={() => { lastAct.current = Date.now(); setKeepAsk(false); }} data-testid="borne-keepask-garder">Je continue ma commande</button>
            <button className="bd-ghost" onClick={() => { logEv('session_abandoned', { statut: 'panier_abandonne', articles: nbArt, total: totalAff }); csidRef.current = ''; csFlags.current = {}; setKeepAsk(false); deconnecter(); }} data-testid="borne-keepask-vider">Vider et recommencer</button>
          </div>
        </div>
      )}

      {rewardPick && (
        <div className="bd-veil" onClick={(e) => e.target === e.currentTarget && setRewardPick(false)}>
          <div className="bd-sheet" data-testid="borne-reward-sheet">
            <button className="bd-back" style={{ float: 'right', width: 44, height: 44, fontSize: 18 }} onClick={() => setRewardPick(false)} data-testid="borne-reward-fermer">✕</button>
            <h3 style={{ fontSize: 22 }}>Choisis ton cookie offert</h3>
            <p style={{ fontSize: 13.5, color: 'rgba(46,19,11,.6)', margin: '4px 0 14px' }}>10 tampons seront déduits de ton compte à l'envoi de la commande en caisse.</p>
            <div className="bd-chiprow">
              {data.products.filter((p) => p.cat_id === 'cookies' && !p.compose && !p.choix_n && p.actif !== false).map((c) => (
                <button key={c.nom} className="bd-chip" data-testid={`borne-reward-${c.nom.replace(/\s+/g, '-')}`}
                  onClick={() => { pop(); setPanier((p) => [...p, { nom: c.nom, px: 0, opts: ['Offert fidélité — 10 tampons'], q: 1, reward: true }]); setRewardPick(false); setShowPanier(true); }}>
                  {(c.img_thumb || c.img) && <img className="ic" src={optim(c.img_thumb || c.img, 320)} alt="" loading="lazy" />}
                  <span>{c.nom}</span>
                  <span className="plus">+</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {roue && (
        <div className="bd-veil" style={{ zIndex: 75 }} onClick={(e) => { if (e.target === e.currentTarget && !roue.spinning) setRoue(null); }}>
          <div className="bd-sheet bd-roue-fond" style={{ textAlign: 'center' }} data-testid="borne-roue-sheet">
            {roue.deja ? (<>
              <h3 style={{ fontSize: 24 }}>Déjà tourné aujourd'hui !</h3>
              <p style={{ fontSize: 15, color: 'rgba(46,19,11,.65)', margin: '8px 0 18px' }}>Ton gain du jour : <b>{roue.label}</b>. Reviens demain pour retenter 🍪</p>
              <button className="bd-cta" style={{ width: '100%' }} onClick={() => setRoue(null)} data-testid="borne-roue-ok">OK</button>
            </>) : (<>
              <h3 style={{ fontSize: 24 }}>{roue.gold ? '👑 La roue Gold' : 'La roue Boufi'}</h3>
              {roue.gold && <p style={{ fontSize: 13, color: '#C58A0A', fontWeight: 700, margin: '2px 0 0' }} data-testid="borne-roue-gold-tag">Réservée aux membres Gold — gains boostés !</p>}
              <div className="bd-roue-wrap">
                <span className="bd-roue-fleche">▼</span>
                <div className="bd-roue" style={{ transform: `rotate(${roue.deg}deg)` }}>
                  {roue.segs.map((s, i) => (
                    <span key={s.id} className="bd-roue-lbl" style={{ transform: `translate(-50%,-50%) rotate(${i * 60 + 30}deg) translateY(-82px)`, color: [1, 5].includes(i) ? '#2E130B' : '#FFF7EC' }}>{s.label}</span>
                  ))}
                </div>
              </div>
              {roue.gain ? (<>
                <div className="bd-roue-gain beclat" style={{ display: 'inline-block' }} data-testid="borne-roue-gain">🎉 {roue.gain.label}</div>
                <p style={{ fontSize: 13.5, color: 'rgba(46,19,11,.6)', margin: '6px 0 14px' }}>
                  {roue.gain.stamps ? 'Tampons déjà crédités sur ton compte !' : 'Montre cet écran en caisse pour en profiter.'}
                </p>
                <button className="bd-cta" style={{ width: '100%' }} onClick={() => setRoue(null)} data-testid="borne-roue-ok">Super !</button>
              </>) : (
                <p style={{ fontSize: 15, color: 'rgba(46,19,11,.6)' }}>Bonne chance {prenom} !</p>
              )}
            </>)}
          </div>
        </div>
      )}

      {upsell && (
        <div className="bd-veil" onClick={(e) => { if (e.target !== e.currentTarget) return; const rester = !!(upsell.mini || upsell.duo || upsell.boxup); setUpsell(null); if (!rester) setEtape('ident'); }}>
          <div className="bd-sheet" data-testid="borne-upsell-sheet">
            {upsell.duo ? (() => {
              const u = upsell.duo;
              const delta = Math.max(0, Math.round((u.p.px - u.base.px) * 100) / 100);
              return (<>
                <h3 style={{ fontSize: 23 }} data-testid="upsell-duo-titre">{u.sug?.type === 'fournee' ? `Nouvelle fournée de ${u.sug.nom} !` : (ckCfg.duo_titre || 'Un deuxième ? 🍪')}</h3>
                {u.sug?.type === 'assoc' && <p style={{ fontSize: 13.5, color: 'rgba(46,19,11,.72)', margin: '2px 0 0' }} data-testid="upsell-duo-assoc">Avec le {u.base.nom}, les clients choisissent souvent le <b>{u.sug.nom}</b>.</p>}
                <p style={{ fontSize: 14.5, color: 'rgba(46,19,11,.62)', margin: '4px 0 14px' }}>
                  Passe au Duo pour seulement <b>+{eur(delta)}</b>{showEco(u.p) ? <> — {eur(u.p.px)} au lieu de <s>{eur(pxBarreAff(u.p))}</s>.</> : '.'}
                </p>
                <div className="bd-smartup" data-testid="upsell-duo-card">
                  {(u.p.img_thumb || u.p.img) ? <img src={u.p.img_thumb || u.p.img} alt="" /> : <div className="bd-smartup-ph">🍪</div>}
                  <div>
                    <span className="fredoka" style={{ fontSize: 19 }}>{u.p.nom}</span>
                    <span className="fredoka" style={{ fontSize: 23, color: '#E6391F' }}>{eur(u.p.px)}{showEco(u.p) && <s style={{ fontSize: 15, opacity: .5, marginLeft: 8 }}>{eur(pxBarreAff(u.p))}</s>}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="bd-ghost" style={{ width: 'auto', padding: '0 22px' }} onClick={() => { track('refuse', u.p.nom, 0, 'duo-cookie', null, u.ab ? `ab-duo-${u.ab}` : 'v1'); setUpsell(null); }} data-testid="upsell-duo-non">Non merci</button>
                  <button className="bd-cta" style={{ flex: 1, fontSize: 15.5, whiteSpace: 'normal', lineHeight: 1.15, padding: '10px 14px', height: 'auto', minHeight: 54 }} data-testid="upsell-duo-oui"
                    onClick={() => {
                      track('accepte', u.p.nom, u.p.px, 'duo-cookie', Math.max(0, Math.round((u.p.px - u.base.px) * 100) / 100), u.ab ? `ab-duo-${u.ab}` : 'v1'); setUpsell(null);
                      const opt = BOX_MAP[u.base.nom] || u.base.nom;
                      const ok = (G.box_cookies?.opts || []).includes(opt);
                      setDetail({ it: u.p, choix: {}, multi: ok ? { [opt]: 1 } : {}, q: 1, ...(ok ? { remplaceNom: u.base.nom } : {}) });
                    }}>{ckCfg.duo_cta || 'Choisir mon 2ᵉ cookie'}</button>
                </div>
              </>);
            })() : upsell.boxup ? (() => {
              const u = upsell.boxup;
              const lignesCk = [];
              panier.forEach((l) => { const p = data.products.find((x) => x.nom === l.nom); if (p?.cat_id === 'cookies' && p.sous_cat && !l.reward) { for (let k = 0; k < l.q && lignesCk.length < 3; k++) lignesCk.push({ nom: l.nom, px: p.px }); } });
              return (<>
                <h3 style={{ fontSize: 23 }} data-testid="upsell-box-titre">{ckCfg.box_titre || 'Plus qu\'un 🍪'}</h3>
                <p style={{ fontSize: 14.5, color: 'rgba(46,19,11,.62)', margin: '4px 0 14px' }}>
                  Passe à la Box de 4 pour <b>+{eur(Math.max(0, u.deltaReel ?? 0))}</b> : tes {lignesCk.length} cookies + 1 de plus, rangés dans leur box{showEco(u.p) ? <> — économise {ecoEur(ecoReelle(u.p))}</> : ''}.
                </p>
                <div className="bd-smartup" data-testid="upsell-box-card">
                  {(u.p.img_thumb || u.p.img) ? <img src={u.p.img_thumb || u.p.img} alt="" /> : <div className="bd-smartup-ph">📦</div>}
                  <div>
                    <span className="fredoka" style={{ fontSize: 19 }}>{u.p.nom}</span>
                    <span className="fredoka" style={{ fontSize: 23, color: '#E6391F' }}>{eur(u.p.px)}{showEco(u.p) && <s style={{ fontSize: 15, opacity: .5, marginLeft: 8 }}>{eur(pxBarreAff(u.p))}</s>}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="bd-ghost" style={{ width: 'auto', padding: '0 22px' }} onClick={() => { track('refuse', u.p.nom, 0, 'box4-cookies'); setUpsell(null); }} data-testid="upsell-box-non">Non merci</button>
                  <button className="bd-cta" style={{ flex: 1 }} data-testid="upsell-box-oui"
                    onClick={() => {
                      track('accepte', u.p.nom, u.p.px, 'box4-cookies'); setUpsell(null);
                      const multi = {}; const rempl = [];
                      lignesCk.forEach(({ nom }) => { const o = BOX_MAP[nom] || nom; if ((G.box_cookies?.opts || []).includes(o)) { multi[o] = (multi[o] || 0) + 1; rempl.push(nom); } });
                      setDetail({ it: u.p, choix: {}, multi, q: 1, ...(rempl.length ? { remplaceNoms: rempl } : {}) });
                    }}>{ckCfg.box_cta || 'Choisir mon 4ᵉ cookie'}</button>
                </div>
              </>);
            })() : upsell.mini ? (<>
              <h3 style={{ fontSize: 23 }}>Ton cookie mérite son +1 🥤</h3>
              <p style={{ fontSize: 14.5, color: 'rgba(46,19,11,.62)', margin: '4px 0 14px' }}>Passe en formule avec une boisson, c'est plus malin qu'à l'unité.</p>
              {upsell.mini.map((f) => (
                <button key={f.nom} className="bd-miniup" data-testid={`upsell-mini-${f.nom.replace(/\s+/g, '-')}`}
                  onClick={() => {
                    track('accepte', f.nom, f.px, 'apres-ajout'); const base = upsell.base; setUpsell(null);
                    const opt = BOX_MAP[base.nom];
                    const slC = (f.slots || []).find((s) => s.groupe === 'cookie' && opt && (!s.seuls || s.seuls.includes(opt)));
                    const slB = (f.slots || []).find((s) => !s.groupe);
                    setDetail({ it: f, choix: {}, multi: {}, q: 1, remplaceNom: base.nom, ...(slC && opt ? { slotSel: { [slC.id]: opt } } : {}), ...(slC && opt && slB ? { slotOpen: slB.id, slotIdx: 0 } : {}) });
                  }}>
                  {(f.img_thumb || f.img) ? <img src={optim(f.img_thumb || f.img, 320)} alt="" loading="lazy" /> : <span className="ph">🍪</span>}
                  <span className="t"><b>{f.nom}</b><small>pour +{eur(Math.max(0, Math.round((f.px - upsell.base.px) * 100) / 100))} seulement</small></span>
                  <span className="go">›</span>
                </button>
              ))}
              <button className="bd-ghost" style={{ margin: '8px 0 0' }} onClick={() => { track('refuse', '', 0, 'apres-ajout'); setUpsell(null); }} data-testid="upsell-mini-non">Non merci</button>
            </>) : upsell.smart ? (<>
              <h3 style={{ fontSize: 24 }}>{upsell.smart.titre}</h3>
              <p style={{ fontSize: 14.5, color: 'rgba(46,19,11,.62)', margin: '4px 0 16px' }}>{upsell.smart.texte}</p>
              <div className="bd-smartup" data-testid="upsell-smart-card">
                {(upsell.smart.imgAff || upsell.smart.p.img) ? <img src={upsell.smart.imgAff || upsell.smart.p.img} alt="" data-testid="upsell-smart-img" /> : <div className="bd-smartup-ph">🍪</div>}
                <div>
                  <span className="fredoka" style={{ fontSize: 19 }}>{upsell.smart.p.nom}</span>
                  <span className="fredoka" style={{ fontSize: 23, color: '#E6391F' }}>{eur(upsell.smart.pxAff ?? upsell.smart.p.px)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="bd-ghost" style={{ width: 'auto', padding: '0 22px' }} onClick={() => { track('refuse', upsell.smart.p.nom, 0, upsell.smart.rule || 'smart'); setUpsell(null); setEtape('ident'); }} data-testid="upsell-smart-non">Non merci</button>
                <button className="bd-cta" style={{ flex: 1 }} onClick={accepterSmart} data-testid="upsell-smart-oui">Oui, je prends !</button>
              </div>
            </>) : (<>
            <h3 style={{ fontSize: 23 }} data-testid="envie-titre">{upsell.titre || 'Une petite envie en plus ?'}</h3>
            <p style={{ fontSize: 14, color: 'rgba(46,19,11,.6)', margin: '4px 0 18px' }}>{upsell.texte || 'Les petits plus qui font toute la différence.'}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 12, marginBottom: 18 }}>
              {upsell.list.slice(0, 3).map((p) => (
                <div key={p.nom} className="bd-upcard">
                  {(p.img || p.img_thumb) && <img src={p.img || p.img_thumb} alt="" />}
                  <span className="fredoka" style={{ fontSize: 16 }}>{p.nom}</span>
                  {p.de && <span style={{ fontSize: 12, color: 'rgba(46,19,11,.55)', lineHeight: 1.25, minHeight: 30 }}>{p.de}</span>}
                  <span className="fredoka" style={{ fontSize: 16, color: '#E6391F' }}>{p.slots?.length || p.compose?.length ? 'à partir de ' : ''}+{eur(p.px)}</span>
                  <button className="bd-cta" style={{ height: 52, fontSize: 15.5, width: '100%' }} data-testid={`upsell-add-${p.nom.replace(/\s+/g, '-')}`}
                    onClick={() => { ajouter({ nom: p.nom, px: p.px, opts: [] }); track('accepte', p.nom, p.px, upsell.ecran ? 'ecran' : 'liste'); setUpsell((u) => u.list.filter((x) => x.nom !== p.nom).length ? { ...u, list: u.list.filter((x) => x.nom !== p.nom) } : (setEtape('ident'), null)); }}>+ Ajouter</button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
              <button className="bd-ghost" style={{ flex: '1 1 150px', minHeight: 60, padding: '0 18px', position: 'static' }} onClick={() => { track('refuse', '', 0, upsell.ecran ? 'ecran' : 'liste'); setUpsell(null); setEtape('ident'); }} data-testid="upsell-non-merci">Non merci</button>
              <button className="bd-cta" style={{ flex: '2 1 230px', minHeight: 60, position: 'static' }} onClick={() => { setUpsell(null); setEtape('ident'); }} data-testid="upsell-continuer">Continuer ma commande · {eur(totalAff)} →</button>
            </div>
            </>)}
          </div>
        </div>
      )}
    </div>
  );
}
